import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Hexagon,
  Plus,
  ChevronLeft,
  Trash2,
  Search,
  X,
  Filter,
  TrendingDown,
  Building2,
  User,
  ArrowDown,
  Check,
  AlertCircle,
  FileText,
  CircleDot,
  Calendar,
  Clock,
  Printer,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  LogOut,
  Library,
  Star,
  BarChart3,
  Mail,
  Download,
  BookOpen,
  Paperclip,
  Upload,
  Loader2,
  Layers,
  AlertOctagon,
  AlertTriangle,
  Eye,
  Info,
  Bell,
  PenLine,
  CheckSquare,
  ChevronUp,
} from "lucide-react";
import {
  hasStrategyDoc,
  downloadStrategyDoc,
  buildStrategyMailto,
} from "../lib/strategyDoc.js";
import FeedbackPanel from "./FeedbackPanel.jsx";
import { supabase } from "../lib/supabase.js";
import storage, {
  setActivePlan as setStorageActivePlan,
  loadCustomStrategies,
  saveCustomStrategy,
  deleteCustomStrategy,
  uploadAttachment,
  getAttachmentDownloadUrl,
  deleteAttachment,
  loadAllPlansForAnalytics,
  logStrategyEvent,
  loadStrategyEvents,
} from "../lib/storage.js";
import {
  STRATEGIES,
  CATEGORIES,
  GOALS,
  ENTITY_TYPES as STRATEGY_ENTITY_TYPES,
  getStrategy,
  getAllStrategies,
  setCustomStrategies,
  isCustomStrategy,
  deadlineDateFor,
  PTE_ELECTION_DEADLINE_BY_STATE,
  TIER_META,
} from "../lib/strategies.js";
import {
  computePlanWithSavings,
  newPlan,
  newEntity,
  ENTITY_TYPE_OPTIONS,
  LIFECYCLE_STAGES,
  strategyReduction,
  strategyCredit,
  strategyManualSavings,
} from "../lib/compute.js";
import { generateAlerts } from "../lib/alerts.js";
import { FILING_STATUSES, TAX_YEAR_OPTIONS, getFederalData } from "../lib/federal.js";
import {
  STATE_OPTIONS,
  STATES,
  getStateConformityNotes,
  hasConformityIssues,
} from "../lib/states.js";

// ════════════════════════════════════════════════════════════════════════
// Utilities
// ════════════════════════════════════════════════════════════════════════
const fmt = (n) => {
  if (!isFinite(n) || n === 0) return "0";
  return Math.round(n).toLocaleString("en-US");
};
const fmtPct = (n, d = 1) => `${(n * 100).toFixed(d)}%`;

const uid = (prefix) =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

// ════════════════════════════════════════════════════════════════════════
// Reusable form primitives
// ════════════════════════════════════════════════════════════════════════
function Field({ label, hint, children, className = "" }) {
  return (
    <div className={className}>
      <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1">
        {label}
      </div>
      {children}
      {hint && <div className="sans text-[11px] text-stone-500 mt-1">{hint}</div>}
    </div>
  );
}

// Format a numeric value with thousand separators for display in inputs.
// Preserves trailing decimal point during entry (e.g. "1,234." while typing).
function formatForDisplay(value) {
  if (value === 0 || value === "0" || value === "" || value == null) return "";
  const num = typeof value === "number" ? value : parseFloat(String(value).replace(/,/g, ""));
  if (!isFinite(num)) return "";
  // Preserve up to 2 decimal places when present
  const str = String(value).replace(/,/g, "");
  if (str.includes(".")) {
    const [whole, frac] = str.split(".");
    const wholeNum = parseFloat(whole) || 0;
    return wholeNum.toLocaleString("en-US") + "." + (frac || "");
  }
  return Math.round(num).toLocaleString("en-US");
}

function MoneyInput({ value, onChange, placeholder = "0", className = "" }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
      <input
        type="text"
        inputMode="decimal"
        value={formatForDisplay(value)}
        onChange={(e) => {
          // Strip everything except digits, decimal point, and minus sign
          const cleaned = e.target.value.replace(/[^0-9.-]/g, "");
          if (cleaned === "" || cleaned === "-") {
            onChange(0);
            return;
          }
          // Allow trailing decimal point during entry (e.g. "1234.")
          if (cleaned.endsWith(".")) {
            onChange(cleaned);
            return;
          }
          const num = parseFloat(cleaned);
          onChange(isFinite(num) ? num : 0);
        }}
        placeholder={placeholder}
        className={`w-full pl-7 pr-3 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-sky-500 ${className}`}
      />
    </div>
  );
}

function NumberInput({ value, onChange, placeholder = "0", suffix, className = "" }) {
  return (
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={formatForDisplay(value)}
        onChange={(e) => {
          const cleaned = e.target.value.replace(/[^0-9.-]/g, "");
          if (cleaned === "" || cleaned === "-") {
            onChange(0);
            return;
          }
          if (cleaned.endsWith(".")) {
            onChange(cleaned);
            return;
          }
          const num = parseFloat(cleaned);
          onChange(isFinite(num) ? num : 0);
        }}
        placeholder={placeholder}
        className={`w-full px-3 py-2 ${suffix ? "pr-8" : ""} border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-sky-500 ${className}`}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">
          {suffix}
        </span>
      )}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, className = "" }) {
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500 ${className}`}
    />
  );
}

// ════════════════════════════════════════════════════════════════════════
// OtherLineItemsField — stackable +/− line items for any "Other ___" field.
// Each row has a sign toggle, description, and amount; rows sum to a net total
// which flows into the existing single numeric field for backward compat with
// the compute pipeline. Migrates legacy {amount, note} data on first edit.
// ════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════
// ItemizedDeductionsField — dedicated stackable list for Schedule A items.
// Standard categories seeded with $10K SALT cap auto-enforcement on the SALT
// row. Sum flows into the itemizedDeductions total; if that total exceeds
// the standard deduction, federal.js uses itemized.
// ════════════════════════════════════════════════════════════════════════
function ItemizedDeductionsField({
  items,
  legacyAmount = 0,
  onChange,
}) {
  const CATEGORIES = [
    {
      id: "medical",
      label: "Medical & Dental (over 7.5% AGI floor)",
      helper: "Enter the deductible portion after the AGI floor",
    },
    {
      id: "salt",
      label: "State & Local Taxes (SALT cap $10K)",
      helper: "Auto-capped at $10K per §164(b)(6)",
      cap: 10000,
    },
    {
      id: "mortgage",
      label: "Home Mortgage Interest",
      helper: "Qualified residence interest per §163(h)(3)",
    },
    {
      id: "charitable",
      label: "Charitable Contributions",
      helper: "Cash to 60%-AGI ceiling; appreciated 30%-AGI ceiling",
    },
    {
      id: "casualty",
      label: "Federal Disaster Casualty Losses",
      helper: "Federally-declared disaster area only post-TCJA",
    },
    {
      id: "other",
      label: "Other Itemized (rare)",
      helper: "Investment interest, gambling losses to gambling winnings, etc.",
    },
  ];

  const effectiveItems = useMemo(() => {
    if (Array.isArray(items) && items.length > 0) return items;
    return CATEGORIES.map((c) => ({ id: c.id, amount: 0 }));
  }, [items]);

  const applyCap = (id, raw) => {
    const cat = CATEGORIES.find((c) => c.id === id);
    const v = parseFloat(raw) || 0;
    return cat?.cap != null ? Math.min(v, cat.cap) : v;
  };

  const sumOf = (list) =>
    list.reduce(
      (acc, it) => acc + applyCap(it.id, it.amount),
      0,
    );

  const writeChange = (nextList) => {
    onChange(nextList, sumOf(nextList));
  };

  const updateItem = (idx, patch) => {
    writeChange(
      effectiveItems.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    );
  };

  const total = sumOf(effectiveItems);
  const willUse = total > legacyAmount ? total : legacyAmount;

  return (
    <div className="border border-stone-300 rounded-sm bg-white">
      <div className="px-3 py-2 flex items-center justify-between border-b border-stone-200 bg-stone-50/50">
        <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-600">
          Itemized Deductions (Schedule A)
        </div>
        <div className="sans text-xs text-stone-600">
          Total:{" "}
          <span className="mono num-tabular font-bold text-emerald-700">
            ${fmt(total)}
          </span>
        </div>
      </div>
      <div className="px-3 py-2 space-y-1.5">
        {CATEGORIES.map((cat) => {
          const idx = effectiveItems.findIndex((i) => i.id === cat.id);
          const it =
            idx >= 0 ? effectiveItems[idx] : { id: cat.id, amount: 0 };
          const raw = parseFloat(it.amount) || 0;
          const capped = applyCap(cat.id, it.amount);
          const isCapped = cat.cap != null && raw > cat.cap;
          return (
            <div key={cat.id} className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="sans text-xs font-semibold text-stone-700 truncate">
                  {cat.label}
                </div>
                <div className="sans text-[10px] text-stone-500 truncate">
                  {cat.helper}
                </div>
              </div>
              <div className="relative shrink-0">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 sans text-xs text-stone-400 pointer-events-none">
                  $
                </span>
                <input
                  type="number"
                  value={raw || 0}
                  onChange={(e) =>
                    updateItem(idx >= 0 ? idx : effectiveItems.length, {
                      id: cat.id,
                      amount: e.target.value,
                    })
                  }
                  className="w-28 pl-5 pr-2 py-1 border border-stone-300 rounded-sm mono num-tabular text-sm text-right focus:outline-none focus:border-sky-500"
                />
              </div>
              {isCapped && (
                <span
                  className="sans text-[9px] uppercase tracking-wider font-bold text-amber-700 shrink-0"
                  title={`Capped at $${fmt(cat.cap)}`}
                >
                  Cap → ${fmt(capped)}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="px-3 py-2 border-t border-stone-200 bg-stone-50/50 sans text-[10px] text-stone-600 leading-relaxed">
        Tool uses the greater of itemized total (${fmt(willUse)}) and the
        standard deduction. SALT auto-caps at $10K. Enter values; the total
        flows into the tax calc automatically.
      </div>
    </div>
  );
}

function OtherLineItemsField({
  label,
  items,
  legacyAmount = 0,
  legacyNote = "",
  signHelperPlus = "Adds to the total",
  signHelperMinus = "Subtracts from the total",
  emptyPrompt = "No items yet. Click below to add one.",
  onChange,
}) {
  const effectiveItems = useMemo(() => {
    if (Array.isArray(items) && items.length > 0) return items;
    const legacyValue = parseFloat(legacyAmount) || 0;
    if (legacyValue !== 0) {
      return [
        {
          id: "legacy",
          description: legacyNote || "",
          amount: Math.abs(legacyValue),
          sign: legacyValue < 0 ? "-" : "+",
        },
      ];
    }
    return [];
  }, [items, legacyAmount, legacyNote]);

  const sumOf = (list) =>
    list.reduce(
      (acc, it) =>
        acc + (it.sign === "-" ? -1 : 1) * (parseFloat(it.amount) || 0),
      0,
    );
  const total = sumOf(effectiveItems);

  const writeChange = (nextList) => {
    onChange(nextList, sumOf(nextList));
  };

  const addItem = () => {
    writeChange([
      ...effectiveItems,
      { id: uid("oli"), description: "", amount: 0, sign: "+" },
    ]);
  };
  const updateItem = (idx, patch) => {
    writeChange(
      effectiveItems.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    );
  };
  const removeItem = (idx) => {
    writeChange(effectiveItems.filter((_, i) => i !== idx));
  };

  return (
    <div className="border border-stone-300 rounded-sm bg-white">
      <div className="px-3 py-2 flex items-center justify-between border-b border-stone-200 bg-stone-50/50">
        <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-600">
          {label}
        </div>
        <div className="sans text-xs text-stone-600">
          Net total:{" "}
          <span
            className={`mono num-tabular font-bold ${
              total > 0
                ? "text-stone-900"
                : total < 0
                  ? "text-rose-700"
                  : "text-stone-400"
            }`}
          >
            {total < 0 ? "−" : ""}${fmt(Math.abs(total))}
          </span>
        </div>
      </div>
      <div className="px-2 py-2 space-y-1.5">
        {effectiveItems.length === 0 ? (
          <div className="px-2 py-2 sans text-xs text-stone-500 italic">
            {emptyPrompt}
          </div>
        ) : (
          effectiveItems.map((it, idx) => (
            <div key={it.id} className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() =>
                  updateItem(idx, { sign: it.sign === "-" ? "+" : "-" })
                }
                className={`w-7 h-7 rounded-sm flex items-center justify-center font-bold sans text-base shrink-0 transition-colors ${
                  it.sign === "-"
                    ? "bg-rose-600 text-white hover:bg-rose-700"
                    : "bg-emerald-600 text-white hover:bg-emerald-700"
                }`}
                title={it.sign === "-" ? signHelperMinus : signHelperPlus}
              >
                {it.sign === "-" ? "−" : "+"}
              </button>
              <input
                type="text"
                value={it.description || ""}
                onChange={(e) =>
                  updateItem(idx, { description: e.target.value })
                }
                placeholder="Describe this item"
                className="flex-1 min-w-0 px-2 py-1.5 border border-stone-300 rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
              />
              <div className="relative shrink-0">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 sans text-xs text-stone-400 pointer-events-none">
                  $
                </span>
                <input
                  type="number"
                  value={it.amount || 0}
                  onChange={(e) =>
                    updateItem(idx, { amount: e.target.value })
                  }
                  className="w-28 pl-5 pr-2 py-1.5 border border-stone-300 rounded-sm mono num-tabular text-sm text-right focus:outline-none focus:border-sky-500"
                />
              </div>
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="w-7 h-7 rounded-sm flex items-center justify-center text-stone-400 hover:text-rose-600 hover:bg-rose-50 shrink-0"
                title="Remove this row"
              >
                <X size={14} />
              </button>
            </div>
          ))
        )}
      </div>
      <div className="px-2 pb-2">
        <button
          type="button"
          onClick={addItem}
          className="w-full px-2 py-1.5 sans text-xs font-semibold text-sky-700 hover:text-sky-900 border border-dashed border-stone-300 hover:border-sky-400 rounded-sm flex items-center justify-center gap-1"
        >
          <Plus size={12} />
          Add line item
        </button>
      </div>
    </div>
  );
}

function Select({ value, onChange, options, className = "" }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500 ${className}`}
    >
      {options.map((opt) => (
        <option key={opt.id || opt.value} value={opt.id || opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Stat tiles
// ════════════════════════════════════════════════════════════════════════
function StatCard({ label, value, prefix = "", suffix = "", color = "stone" }) {
  const colors = {
    stone: "bg-white border-stone-300 text-stone-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
  };
  return (
    <div className={`p-4 border rounded-sm ${colors[color]}`}>
      <div className="sans text-[10px] uppercase tracking-widest font-semibold opacity-70">
        {label}
      </div>
      <div className="serif text-3xl font-bold mt-1 num-tabular">
        {prefix}
        {value}
        {suffix}
      </div>
    </div>
  );
}

function HeroTile({ label, value, sub }) {
  return (
    <div className="p-6 bg-gradient-to-br from-stone-900 via-stone-900 to-sky-900 text-stone-100 rounded-sm relative overflow-hidden">
      <div className="sans text-[11px] uppercase tracking-[0.25em] text-sky-300 mb-2 font-semibold">
        {label}
      </div>
      <div className="serif text-6xl font-bold text-sky-400 num-tabular mono">
        ${fmt(value)}
      </div>
      {sub && <div className="sans text-sm text-stone-400 mt-2">{sub}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Header
// ════════════════════════════════════════════════════════════════════════
function AppHeader({
  activePlan,
  mainView = "plans",
  onSwitchView,
  onBack,
  onUpdatePlan,
  onOpenFeedback,
  onSignOut,
  userEmail,
  feedbackOpenCount = 0,
  feedbackTotalCount = 0,
}) {
  const inBank = mainView === "bank";
  return (
    <header className="sticky top-0 z-30 bg-stone-900 border-b border-stone-800 px-6 py-3 flex items-center gap-4">
      <div className="flex items-center gap-2">
        <Hexagon size={22} className="text-sky-400" strokeWidth={2.5} />
        <div>
          <div className="serif text-lg font-bold text-stone-100 leading-none">
            Tax Planning Workbench
          </div>
          <div className="sans text-[10px] uppercase tracking-widest text-sky-300/80 mt-0.5">
            Linked Accounting LLP
          </div>
        </div>
      </div>

      {/* Tab navigation — always visible */}
      <nav className="flex items-center ml-3 border border-stone-700 rounded-sm overflow-hidden">
        <button
          onClick={() => onSwitchView && onSwitchView("plans")}
          className={`flex items-center gap-1.5 px-3 py-1.5 sans text-sm font-semibold transition-colors ${
            mainView === "plans"
              ? "bg-stone-700 text-sky-400"
              : "text-stone-300 hover:text-sky-400"
          }`}
        >
          <FileText size={14} />
          Plans
        </button>
        <button
          onClick={() => onSwitchView && onSwitchView("bank")}
          className={`flex items-center gap-1.5 px-3 py-1.5 sans text-sm font-semibold transition-colors border-l border-stone-700 ${
            mainView === "bank"
              ? "bg-stone-700 text-sky-400"
              : "text-stone-300 hover:text-sky-400"
          }`}
        >
          <Library size={14} />
          Strategy Bank
        </button>
        <button
          onClick={() => onSwitchView && onSwitchView("analytics")}
          className={`flex items-center gap-1.5 px-3 py-1.5 sans text-sm font-semibold transition-colors border-l border-stone-700 ${
            mainView === "analytics"
              ? "bg-stone-700 text-sky-400"
              : "text-stone-300 hover:text-sky-400"
          }`}
        >
          <BarChart3 size={14} />
          Analytics
        </button>
      </nav>

      {mainView === "plans" && activePlan && (
        <>
          <div className="w-px h-8 bg-stone-700 mx-2" />
          <button
            onClick={onBack}
            className="flex items-center gap-1 px-3 py-1.5 text-stone-300 hover:text-sky-400 sans text-sm"
          >
            <ChevronLeft size={16} />
            All Plans
          </button>
          <div className="flex-1 flex items-center gap-3 min-w-0">
            <input
              value={activePlan.clientName}
              onChange={(e) => onUpdatePlan({ ...activePlan, clientName: e.target.value })}
              className="serif text-2xl font-bold text-stone-100 bg-transparent border-b border-transparent hover:border-stone-600 focus:border-sky-400 focus:outline-none px-1 min-w-0"
            />
            <Select
              value={activePlan.taxYear}
              onChange={(v) => onUpdatePlan({ ...activePlan, taxYear: parseInt(v, 10) })}
              options={TAX_YEAR_OPTIONS.map((y) => ({ id: y, label: `TY ${y}` }))}
              className="!w-28"
            />
            <Select
              value={activePlan.state}
              onChange={(v) => onUpdatePlan({ ...activePlan, state: v })}
              options={STATE_OPTIONS.map((s) => ({ id: s.id, label: `${s.id} — ${s.name}` }))}
              className="!w-44"
            />
            <Select
              value={activePlan.individual?.filingStatus || "mfj"}
              onChange={(v) =>
                onUpdatePlan({
                  ...activePlan,
                  individual: { ...activePlan.individual, filingStatus: v },
                })
              }
              options={FILING_STATUSES.map((f) => ({ id: f.id, label: f.label }))}
              className="!w-56"
            />
            <div className="relative flex items-center">
              <Mail
                size={14}
                className={`absolute left-2.5 ${activePlan.clientEmail ? "text-sky-400" : "text-stone-500"}`}
              />
              <input
                type="email"
                value={activePlan.clientEmail || ""}
                onChange={(e) =>
                  onUpdatePlan({ ...activePlan, clientEmail: e.target.value })
                }
                placeholder="Client email"
                className="w-56 pl-8 pr-2 py-1.5 bg-stone-800 border border-stone-700 hover:border-stone-600 focus:border-sky-400 rounded-sm sans text-sm text-stone-100 placeholder:text-stone-500 focus:outline-none"
              />
            </div>
          </div>
        </>
      )}

      {(mainView !== "plans" || !activePlan) && <div className="flex-1" />}

      {/* Feedback moved to floating action button (FeedbackFAB) so it stays
          visible regardless of header crowding when viewing a plan. */}

      {/* Right-side utility controls */}
      <div className="flex items-center gap-2">
        {/* Compact in-header Feedback button — backup for users whose
            adblocker hides the floating FAB. Icon + count badge only. */}
        <button
          onClick={onOpenFeedback}
          className="relative flex items-center gap-1.5 px-2.5 py-1.5 sans text-xs font-bold text-stone-900 bg-sky-400 hover:bg-sky-300 rounded-sm"
          title={
            feedbackTotalCount > 0
              ? `${feedbackOpenCount} open · ${feedbackTotalCount} total feedback items`
              : "Send feedback — bugs, ideas, planning improvements"
          }
        >
          <MessageSquare size={14} />
          <span className="hidden lg:inline">Feedback</span>
          {feedbackOpenCount > 0 && (
            <span className="sans text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-rose-500 text-white num-tabular">
              {feedbackOpenCount}
            </span>
          )}
        </button>
        <span className="sans text-xs text-stone-400 hidden md:inline">
          {userEmail}
        </span>
        <button
          onClick={onSignOut}
          className="flex items-center gap-1 px-2 py-1.5 sans text-sm text-stone-400 hover:text-rose-400"
          title="Sign out"
        >
          <LogOut size={14} />
        </button>
      </div>
    </header>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Plans Home
// ════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════
// How To Use / Best Practices / Limitations panel — shown on the home
// screen so anyone landing in the tool can ramp themselves.
// Collapsible; preference persisted in localStorage.
// ════════════════════════════════════════════════════════════════════════
const HOWTO_COLLAPSED_KEY = "tp_howto_collapsed_v1";

function HowToUsePanel({ context = "home" }) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      // Default to collapsed; only show expanded if the user has explicitly
      // expanded it previously.
      const stored = localStorage.getItem(HOWTO_COLLAPSED_KEY);
      if (stored === "0") return false; // user explicitly expanded
      return true; // default collapsed
    } catch {
      return true;
    }
  });

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(HOWTO_COLLAPSED_KEY, next ? "1" : "0");
    } catch {
      /* no-op */
    }
  };

  return (
    <section className="mb-6 bg-white border border-stone-300 rounded-sm">
      <button
        onClick={toggle}
        className="w-full px-5 py-3 flex items-center justify-between hover:bg-stone-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-sm bg-sky-100 flex items-center justify-center">
            <Hexagon size={16} className="text-sky-600" strokeWidth={2.5} />
          </div>
          <div className="text-left">
            <div className="serif text-base font-bold text-stone-900">
              {context === "plan"
                ? "Help & Tips for This Tool"
                : "How to Use This Tool"}
            </div>
            <div className="sans text-[11px] text-stone-500">
              {context === "plan"
                ? "Quick reference — workflow, best practices, and known limits."
                : "Read this first — workflow, best practices, and what this tool isn't."}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 sans text-xs text-stone-500 font-semibold">
          {collapsed ? "Show" : "Hide"}
          <ChevronDown
            size={16}
            className={`transition-transform ${collapsed ? "" : "rotate-180"}`}
          />
        </div>
      </button>

      {!collapsed && (
        <div className="border-t border-stone-200 grid grid-cols-3 divide-x divide-stone-200">
          {/* How to use */}
          <div className="px-5 py-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-sm bg-sky-600 flex items-center justify-center text-white font-bold sans text-xs">
                1
              </div>
              <div className="sans text-[10px] uppercase tracking-widest font-bold text-stone-700">
                How to use
              </div>
            </div>
            <ol className="space-y-2.5 sans text-sm text-stone-700 leading-snug">
              <li>
                <strong className="text-stone-900">Create a plan.</strong> Click{" "}
                <em>New Plan</em>, name the client, pick the tax year and home
                state.
              </li>
              <li>
                <strong className="text-stone-900">
                  Use the three workspace tabs.
                </strong>{" "}
                <em className="text-stone-900">Build</em> for data entry,{" "}
                <em className="text-stone-900">Discuss</em> for the client
                meeting, <em className="text-stone-900">Action Items</em> for
                year-end + estimated tax. The sticky KPI bar at the top stays
                visible across all tabs.
              </li>
              <li>
                <strong className="text-stone-900">Add entities.</strong> Each
                business (1120-S, 1065, 1120, Sched C). Fill in revenue,
                expenses, depreciation, K-1 cap gains, and{" "}
                <strong className="text-emerald-700">named pass-through
                credits</strong> (R&amp;D §41, Energy §45 — each flows to the
                1040 with its name + source entity).
              </li>
              <li>
                <strong className="text-stone-900">
                  Reconcile book → tax → cash.
                </strong>{" "}
                Book-to-tax addbacks first (use{" "}
                <em>Add another addback</em> for named items), then the
                cash/accrual table with{" "}
                <strong className="text-sky-700">Prior Year · Current · Year-End
                Forecast</strong>. The Adjustment uses the YE forecast when
                entered (full-year tax-relevant), otherwise Current YTD.
              </li>
              <li>
                <strong className="text-stone-900">Fill the 1040.</strong> Wages,
                investment income, AMT preferences, QBI override, NIIT-excluded
                LTCG, prior-year tax / withholding for the safe-harbor calc.
                K-1s flow up automatically.
              </li>
              <li>
                <strong className="text-stone-900">Attach strategies.</strong>{" "}
                Click <em>+ Add Strategy</em>. Each strategy gets{" "}
                <strong>three chips</strong> (S1 / S2 / S3) that cycle{" "}
                <strong className="text-emerald-700">Y</strong> /{" "}
                <strong className="text-amber-700">M</strong> /{" "}
                <strong className="text-stone-500">—</strong>. Enter
                Implementation Cost, Recurring Cost, and Years of Savings to
                drive the ROI table and the 3-year outlook.
              </li>
              <li>
                <strong className="text-stone-900">
                  Build & name the scenarios.
                </strong>{" "}
                Click any scenario name in the Scenario Comparison panel to
                rename it (Conservative / Balanced / Aggressive). The
                sticky bar shows the winning scenario by name above the Max
                Tax Saved number.
              </li>
              <li>
                <strong className="text-stone-900">
                  Walk the client through Discuss tab.
                </strong>{" "}
                Use Scenario Comparison + Plan Callouts + multi-year outlook
                + Tax Bill Breakdown + Bracket Position live. Flip strategies
                between Y / M / — as the client commits.
              </li>
              <li>
                <strong className="text-stone-900">Close on Action Items.</strong>{" "}
                Action Items tab: estimated tax schedule, year-end punch list,
                implementation timeline, plan lifecycle. Then click{" "}
                <em>Client Deliverable</em> to print.
              </li>
            </ol>
          </div>

          {/* Best practices */}
          <div className="px-5 py-4 bg-emerald-50/30">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-sm bg-emerald-600 flex items-center justify-center text-white font-bold sans text-xs">
                2
              </div>
              <div className="sans text-[10px] uppercase tracking-widest font-bold text-emerald-800">
                Best practices
              </div>
            </div>
            <ul className="space-y-2.5 sans text-sm text-stone-700 leading-snug">
              <li>
                <strong className="text-stone-900">
                  Be directionally correct — not exact.
                </strong>{" "}
                This is a conversation tool, not a return. The goal is to get
                "close enough" in 15 minutes so a partner can drive a planning
                discussion.
              </li>
              <li>
                <strong className="text-stone-900">
                  Cap data entry at 30 minutes.
                </strong>{" "}
                If you're past that, you're doing it wrong. Skip line items the
                client hasn't given you and flag them in the entity notes.
              </li>
              <li>
                <strong className="text-stone-900">
                  Use Maybes liberally during the pitch.
                </strong>{" "}
                Load 5–8 strategies as Maybes across scenarios, then flip to Yes
                live as the client commits. The Scenario Comparison updates
                instantly so they see the savings move.
              </li>
              <li>
                <strong className="text-stone-900">
                  Name every K-1 credit.
                </strong>{" "}
                On every pass-through entity, list each credit by name
                (R&amp;D §41, Energy §45, Solar ITC). Named credits flow to the
                1040 forecast showing source entity — much easier to defend in
                conversation than a single "K-1 credits" lump sum.
              </li>
              <li>
                <strong className="text-stone-900">
                  Enter Year-End Forecast when you can.
                </strong>{" "}
                The Cash↔Accrual table's <em>Year-End Forecast</em> column
                drives a full-year tax-relevant adjustment instead of a YTD
                snapshot. Even rough projections beat using current month-end.
              </li>
              <li>
                <strong className="text-stone-900">
                  Eyeball QBI on every pass-through.
                </strong>{" "}
                Enter W-2 wages + UBIA. If the simplified 20% × QBI auto-calc
                clearly busts the wage/UBIA ceiling, set the{" "}
                <em>QBI Override</em> on the 1040 with the right number.
              </li>
              <li>
                <strong className="text-stone-900">
                  Stack "Other" line items by name.
                </strong>{" "}
                Other Income, Other Deductions, Federal/State Credits, and
                Book→Tax Other addbacks all support +/− rows with descriptions.
                Use them — clients ask "what's this $X?" and you need an answer.
              </li>
              <li>
                <strong className="text-stone-900">
                  Set Years of Savings per strategy.
                </strong>{" "}
                Cost seg = 1 year, PTE election / retirement / R&amp;D = 3
                years. Wrong setting overstates or understates the multi-year
                outlook by tens of thousands.
              </li>
              <li>
                <strong className="text-stone-900">
                  Watch the Plan Callouts panel.
                </strong>{" "}
                It auto-surfaces AMT exposure, reasonable comp risk, PTE
                opportunity, NIIT proximity, year-end deadlines. Walk the items
                tagged <em>For Client</em> live; use <em>For You</em> as your
                prep notes.
              </li>
              <li>
                <strong className="text-stone-900">
                  Stage your plans honestly.
                </strong>{" "}
                Prospect → Conversation → Plan Built → Presented → Instituted →
                Delivered. Lifecycle feeds firm-wide analytics.
              </li>
              <li>
                <strong className="text-stone-900">
                  Verify before printing.
                </strong>{" "}
                Sanity-check baseline tax against the client's prior-year 1040.
                If they're materially off, fix inputs before the deliverable
                lands in the client's inbox.
              </li>
            </ul>
          </div>

          {/* Limitations */}
          <div className="px-5 py-4 bg-amber-50/30">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-sm bg-amber-600 flex items-center justify-center text-white font-bold sans text-xs">
                3
              </div>
              <div className="sans text-[10px] uppercase tracking-widest font-bold text-amber-800">
                Known limitations
              </div>
            </div>
            <ul className="space-y-2.5 sans text-sm text-stone-700 leading-snug">
              <li>
                <strong className="text-stone-900">
                  Graduated-bracket states are top-rate flat.
                </strong>{" "}
                UT/ID/AZ/CO/NM are flat or near-flat so they're accurate.
                Graduated states (CA, NY, OR, MN, etc.) compute at the top
                marginal rate — overstates state tax for lower incomes.
              </li>
              <li>
                <strong className="text-stone-900">
                  AMT and NIIT are both modeled.
                </strong>{" "}
                NIIT uses the §1411 MAGI threshold ($250K MFJ / $200K Single)
                with the lesser-of rule and the LTCG-excluded carve-out you
                enter on the 1040. AMT (§55) uses the 2025 exemption with the
                25¢ phase-out, the 26%/28% rate breakpoint, and gives LTCG /
                qualified dividends their regular preferential rates within
                AMT. Enter ISO bargain element, PAB interest, depreciation
                adjustments in the <em>AMT Adjustments</em> field on the 1040.
              </li>
              <li>
                <strong className="text-stone-900">AMT caveats.</strong>{" "}
                AMT-NOL isn't tracked separately, ISO lot-level basis isn't
                modeled (enter the bargain element directly), and the AMT
                Credit (Form 8801) carryforward against future regular tax
                isn't tracked. For ISO-heavy or AMT-credit-heavy returns,
                verify against the workpaper before finalizing.
              </li>
              <li>
                <strong className="text-stone-900">QBI auto-calc is simplified.</strong>{" "}
                No SSTB phase-out logic. The W-2/UBIA ceiling is shown for
                reference but the auto-calc uses straight 20% × QBI. Use the
                <em> QBI Override</em> on the 1040 when needed.
              </li>
              <li>
                <strong className="text-stone-900">
                  Multi-year is straight-line projection.
                </strong>{" "}
                The 3-year outlook scales gross income forward at the income
                growth rate and drops strategies whose Years of Savings has
                elapsed. It does NOT model cost-seg recapture, NOL absorption
                across years, or capital-loss carryforward sequencing.
                Directionally correct for planning conversations; not a
                multi-year compliance projection.
              </li>
              <li>
                <strong className="text-stone-900">
                  Federal credits modeled: CTC / CFOD + K-1 pass-through
                  credits.
                </strong>{" "}
                Child Tax Credit ($2K/qualifying child under 17) and Credit
                for Other Dependents ($500 each) compute with the standard
                phase-out. K-1 pass-through credits flow to the 1040 with
                source-entity attribution.{" "}
                <strong>NOT modeled:</strong> Earned Income Credit, foreign
                tax credit, education credits (AOC / LLC), residential
                energy / EV / solar credits, premium tax credit, prior-year
                AMT credit (Form 8801), retirement savings contribution
                credit, child &amp; dependent care credit. Partner adjusts
                total tax manually if these apply.
              </li>
              <li>
                <strong className="text-stone-900">
                  Tax years supported: 2024, 2025, 2026.
                </strong>{" "}
                2024 numbers from Rev. Proc. 2023-34 (exact). 2025 from Rev.
                Proc. 2024-40 (exact). 2026 is COLA-scaled placeholder until
                Rev. Proc. 2025-XX is verified. Change the plan's tax year at
                the top of the workspace to switch bracket schedules and
                thresholds.
              </li>
              <li>
                <strong className="text-stone-900">
                  Year-End Forecast is partner-entered.
                </strong>{" "}
                The Cash↔Accrual <em>Year-End Forecast</em> column doesn't auto-
                project from YTD trends — it's the partner's judgment-call
                number. Garbage in, garbage out.
              </li>
              <li>
                <strong className="text-stone-900">No PDF import yet.</strong>{" "}
                Prior-year 1040s, 1120-S, 1065 must be keyed in manually.
                Roadmap item.
              </li>
              <li>
                <strong className="text-stone-900">
                  1120 C-corps don't flow dividends to the 1040.
                </strong>{" "}
                Entity-level 21% only; distribution tax computed separately
                if needed.
              </li>
              <li>
                <strong className="text-stone-900">
                  2026 brackets are placeholder.
                </strong>{" "}
                COLA-scaled until Rev. Proc. 2025-XX is verified. 2025 numbers
                are exact (Rev. Proc. 2024-40).
              </li>
              <li>
                <strong className="text-stone-900">
                  Marginal contribution can show $0.
                </strong>{" "}
                If the marginal rate is already 0 (loss year), per-strategy
                marginal savings reads $0. That's correct math; cross-check
                against the plan-level total.
              </li>
              <li>
                <strong className="text-stone-900">
                  Estimated tax is federal only.
                </strong>{" "}
                Safe harbor and the Q1–Q4 schedule cover federal under §6654.
                State estimated tax schedules aren't generated (yet).
              </li>
            </ul>
          </div>
        </div>
      )}

      {!collapsed && (
        <div className="border-t border-stone-200 px-5 py-2.5 bg-stone-50/50 sans text-[11px] text-stone-600 flex items-center justify-between">
          <div>
            Questions, bugs, or strategy requests → click the{" "}
            <strong className="text-sky-700">Feedback</strong> button at the top
            right.
          </div>
          <div className="text-stone-500">
            Linked Accounting LLP · Internal use only
          </div>
        </div>
      )}
    </section>
  );
}

function PlansHome({ plans, onOpenPlan, onNewPlan, onDeletePlan }) {
  const totals = useMemo(() => {
    let saved = 0,
      instituted = 0,
      delivered = 0,
      prospects = 0;
    for (const p of plans) {
      saved += p.savings || 0;
      const stage = p.stage || "prospect";
      if (stage === "instituted") instituted++;
      else if (stage === "delivered") delivered++;
      else if (stage === "prospect" || stage === "conversation") prospects++;
    }
    return { saved, instituted, delivered, prospects };
  }, [plans]);

  return (
    <main className="max-w-[1400px] mx-auto px-6 py-8">
      <HowToUsePanel />

      {/* Hero metrics */}
      <section className="grid grid-cols-4 gap-4 mb-6">
        <div className="col-span-2">
          <HeroTile
            label="Total Tax Saved"
            value={totals.saved}
            sub="Across all active and delivered plans"
          />
        </div>
        <StatCard label="Plans Instituted" value={totals.instituted} color="sky" />
        <StatCard label="Plans Delivered" value={totals.delivered} color="emerald" />
      </section>

      {/* Plans list */}
      <section className="bg-white border border-stone-300 rounded-sm">
        <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
          <div className="serif text-xl font-bold text-stone-900">All Tax Plans</div>
          <button
            onClick={onNewPlan}
            className="flex items-center gap-1.5 bg-stone-900 text-sky-400 px-3 py-1.5 rounded-sm sans text-sm font-semibold hover:bg-stone-800"
          >
            <Plus size={16} />
            New Plan
          </button>
        </div>
        {plans.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="serif text-2xl text-stone-700 mb-2">No plans yet</div>
            <div className="sans text-sm text-stone-500 mb-4">
              Create your first tax plan to start modeling client savings.
            </div>
            <button
              onClick={onNewPlan}
              className="inline-flex items-center gap-1.5 bg-stone-900 text-sky-400 px-4 py-2 rounded-sm sans text-sm font-semibold hover:bg-stone-800"
            >
              <Plus size={16} />
              Create First Plan
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-5 py-2.5 text-stone-600">
                  Client
                </th>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5 text-stone-600">
                  Year
                </th>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5 text-stone-600">
                  State
                </th>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5 text-stone-600">
                  Entities
                </th>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5 text-stone-600">
                  Stage
                </th>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5 text-stone-600">
                  Tax Saved
                </th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} className="border-b border-stone-100 hover:bg-sky-50/30">
                  <td
                    className="px-5 py-3 sans text-sm font-semibold text-stone-900 cursor-pointer"
                    onClick={() => onOpenPlan(p.id)}
                  >
                    {p.clientName || "Untitled"}
                  </td>
                  <td className="px-3 py-3 sans text-sm text-stone-700 mono num-tabular">
                    {p.taxYear}
                  </td>
                  <td className="px-3 py-3 sans text-sm text-stone-700">{p.state}</td>
                  <td className="px-3 py-3 sans text-sm text-stone-700 num-tabular">
                    {p.entityCount || 0}
                  </td>
                  <td className="px-3 py-3">
                    <StageBadge stage={p.stage} />
                  </td>
                  <td className="px-3 py-3 sans text-sm font-bold text-emerald-700 mono num-tabular text-right">
                    ${fmt(p.savings || 0)}
                  </td>
                  <td className="px-3 py-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Delete plan "${p.clientName}"? This cannot be undone.`))
                          onDeletePlan(p.id);
                      }}
                      className="text-stone-400 hover:text-rose-600 p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}

// Ensure a plan has the scenarios array. Older plans saved before the
// multi-scenario feature don't have it; seed defaults on first load.
function migrateScenarios(plan) {
  if (!plan) return plan;
  if (Array.isArray(plan.scenarios) && plan.scenarios.length > 0) return plan;
  return {
    ...plan,
    scenarios: [
      { id: "s1", name: "Scenario 1" },
      { id: "s2", name: "Scenario 2" },
      { id: "s3", name: "Scenario 3" },
    ],
  };
}

function StageBadge({ stage }) {
  const map = {
    prospect: { label: "Prospect", color: "bg-stone-100 text-stone-700" },
    conversation: { label: "Conversation", color: "bg-stone-100 text-stone-700" },
    data_received: { label: "Data Received", color: "bg-amber-100 text-amber-800" },
    plan_built: { label: "Plan Built", color: "bg-amber-100 text-amber-800" },
    presented: { label: "Presented", color: "bg-sky-100 text-sky-800" },
    accepted: { label: "Accepted", color: "bg-sky-100 text-sky-800" },
    instituted: { label: "Instituted", color: "bg-emerald-100 text-emerald-800" },
    delivered: { label: "Delivered", color: "bg-emerald-200 text-emerald-900" },
  };
  const s = map[stage] || map.prospect;
  return (
    <span className={`sans text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm ${s.color}`}>
      {s.label}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Plan Workspace — single-page dashboard
// ════════════════════════════════════════════════════════════════════════
function PlanWorkspace({
  plan,
  onUpdatePlan,
  customStrategies = [],
  onSaveCustomStrategy,
  onDeleteCustomStrategy,
}) {
  const result = useMemo(() => computePlanWithSavings(plan), [plan]);
  const [pickerHost, setPickerHost] = useState(null); // { kind: 'entity'|'individual', id? }
  const [showDeliverable, setShowDeliverable] = useState(false);
  const [activeSectionTab, setActiveSectionTab] = useState("entities"); // "entities" | "personal"
  const [strategyDocOpen, setStrategyDocOpen] = useState(null); // null | strategy object

  // Main workspace tabs: build (data entry) | discuss (client meeting) | actions (close)
  const mainTabStorageKey = `tp_main_tab__${plan.id}`;
  const [activeMainTab, setActiveMainTab] = useState(() => {
    try {
      return localStorage.getItem(mainTabStorageKey) || "build";
    } catch {
      return "build";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(mainTabStorageKey, activeMainTab);
    } catch {
      /* no-op */
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [activeMainTab, mainTabStorageKey]);

  // Timeline items + per-strategy savings — used by the Action Items tab
  // (year-end checklist) and the Discuss tab (impact summary lookups).
  const fmtDeadline = (item) => {
    if (item.deadline)
      return item.deadline.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    if (item.kind === "pre_transaction") return "Before triggering transaction";
    if (item.kind === "ongoing") return "Ongoing discipline";
    return "Flexible";
  };
  const itemsWithSavings = useMemo(() => {
    const items = gatherTimelineItems(plan);
    return items.map((item) => {
      if (item.isDerived) return { ...item, savings: 0 };
      const stratRef = item.stratRef;
      if (!stratRef) return { ...item, savings: 0 };
      const marginal = result.marginalById?.[stratRef.strategyId];
      if (marginal != null) return { ...item, savings: marginal };
      const reduction = strategyReduction(stratRef);
      const credit = strategyCredit(stratRef);
      const manual = strategyManualSavings(stratRef);
      const savings =
        manual > 0
          ? manual
          : credit > 0
            ? credit
            : reduction *
              ((result.planned.federal.marginalRate || 0) +
                (STATES[plan.state]?.topRate || 0));
      return { ...item, savings };
    });
  }, [plan, result.marginalById, result.planned.federal.marginalRate]);

  // Combined marginal rate = federal marginal + state top rate.
  // Used to display per-strategy estimated tax savings inline.
  const combinedMarginalRate =
    (result.planned.federal.marginalRate || 0) +
    (STATES[plan.state]?.topRate || 0);

  const updateEntity = (entityId, patch) => {
    onUpdatePlan({
      ...plan,
      entities: plan.entities.map((e) => (e.id === entityId ? { ...e, ...patch } : e)),
    });
  };

  const removeEntity = (entityId) => {
    onUpdatePlan({
      ...plan,
      entities: plan.entities.filter((e) => e.id !== entityId),
    });
  };

  const addEntity = () => {
    const e = newEntity({ id: uid("ent"), name: `Entity ${plan.entities.length + 1}` });
    onUpdatePlan({ ...plan, entities: [...plan.entities, e] });
  };

  const addStrategiesToHost = (hostKind, hostId, strategyIds) => {
    const ids = Array.isArray(strategyIds) ? strategyIds : [strategyIds];
    const scenarioIds = (plan.scenarios || []).map((sc) => sc.id);
    // Default new strategies to "maybe" across every scenario.
    const defaultStatusByScenario = scenarioIds.reduce((acc, id) => {
      acc[id] = "maybe";
      return acc;
    }, {});
    const newRefs = ids.map((strategyId) => ({
      strategyId,
      applied: true,
      status: "maybe", // legacy single-status (still written for backward compat)
      statusByScenario: defaultStatusByScenario,
      inputs: {},
      yearsActive: 3, // multi-year: how many years this strategy keeps generating savings
      addedAt: new Date().toISOString(),
    }));
    if (hostKind === "individual") {
      onUpdatePlan({
        ...plan,
        individual: {
          ...plan.individual,
          strategies: [...(plan.individual.strategies || []), ...newRefs],
        },
      });
    } else {
      const ent = plan.entities.find((e) => e.id === hostId);
      if (!ent) return;
      updateEntity(hostId, { strategies: [...(ent.strategies || []), ...newRefs] });
    }
    // Fire engagement events for each strategy added — fire-and-forget
    for (const sid of ids) {
      logStrategyEvent({
        strategyId: sid,
        eventType: "strategy_added",
        source: "picker",
        planId: plan.id,
      });
    }
    setPickerHost(null);
  };

  const updateHostStrategy = (hostKind, hostId, idx, patch) => {
    if (hostKind === "individual") {
      const next = [...(plan.individual.strategies || [])];
      next[idx] = { ...next[idx], ...patch };
      onUpdatePlan({ ...plan, individual: { ...plan.individual, strategies: next } });
    } else {
      const ent = plan.entities.find((e) => e.id === hostId);
      if (!ent) return;
      const next = [...(ent.strategies || [])];
      next[idx] = { ...next[idx], ...patch };
      updateEntity(hostId, { strategies: next });
    }
  };

  const removeHostStrategy = (hostKind, hostId, idx) => {
    if (hostKind === "individual") {
      const next = (plan.individual.strategies || []).filter((_, i) => i !== idx);
      onUpdatePlan({ ...plan, individual: { ...plan.individual, strategies: next } });
    } else {
      const ent = plan.entities.find((e) => e.id === hostId);
      if (!ent) return;
      const next = (ent.strategies || []).filter((_, i) => i !== idx);
      updateEntity(hostId, { strategies: next });
    }
  };

  return (
    <main className="max-w-[1400px] mx-auto px-6 py-6">
      {/* State conformity banner — appears when state has known federal/state decoupling */}
      {hasConformityIssues(plan.state) && (
        <StateConformityBanner state={plan.state} />
      )}

      {/* Sticky stats bar — Baseline vs. Max Tax Saved, 50/50 split.
          Max Tax Saved names the winning scenario explicitly so it's never
          ambiguous which scenario the headline number comes from. */}
      {(() => {
        const bestScenario =
          (result.scenarios || []).reduce(
            (best, sc) =>
              !best || sc.fullSavings > best.fullSavings ? sc : best,
            null,
          );
        const bestSavings = bestScenario?.fullSavings ?? result.savings;
        const bestPct =
          result.baseline.totalTax > 0
            ? (bestSavings / result.baseline.totalTax) * 100
            : 0;
        const bestTax = bestScenario?.fullTax;
        return (
          <div className="sticky top-0 z-20 -mx-6 px-6 pt-2 pb-3 bg-[#F1F7FC]/95 backdrop-blur-sm border-b border-stone-200 mb-4">
            <section className="grid grid-cols-2 gap-4 relative">
              {/* Baseline — visually balanced with Max Tax Saved */}
              <div className="p-6 bg-rose-50 border-2 border-rose-200 rounded-sm">
                <div className="sans text-[11px] uppercase tracking-[0.25em] text-rose-700 mb-2 font-semibold">
                  Baseline Tax · No Planning
                </div>
                <div className="serif text-6xl font-bold text-rose-700 num-tabular mono">
                  ${fmt(result.baseline.totalTax)}
                </div>
                <div className="sans text-sm text-rose-700/70 mt-2">
                  What the client would owe with no strategies applied
                </div>
              </div>

              {/* Max Tax Saved — names the winning scenario */}
              <div className="p-6 bg-gradient-to-br from-stone-900 via-stone-900 to-sky-900 text-stone-100 rounded-sm relative overflow-hidden">
                <div className="sans text-[11px] uppercase tracking-[0.25em] text-sky-300 mb-2 font-semibold">
                  Max Tax Saved
                </div>
                <div className="serif text-6xl font-bold text-sky-400 num-tabular mono">
                  ${fmt(bestSavings)}
                </div>
                <div className="sans text-sm text-stone-400 mt-2 leading-relaxed">
                  {bestScenario ? (
                    <>
                      From{" "}
                      <strong className="text-sky-300">
                        {bestScenario.name}
                      </strong>{" "}
                      (Yes + Maybe) · {bestPct.toFixed(1)}% off baseline ·
                      tax owed ${fmt(bestTax)}
                    </>
                  ) : (
                    <>
                      Federal ${fmt(result.fedSavings)} · State ${fmt(result.stateSavings)}
                    </>
                  )}
                </div>
                <button
                  onClick={() => setShowDeliverable(true)}
                  className="absolute top-4 right-4 flex items-center gap-1.5 bg-sky-400 text-stone-900 px-3 py-1.5 rounded-sm sans text-xs font-bold hover:bg-sky-300"
                >
                  <FileText size={14} />
                  Client Deliverable
                </button>
              </div>
            </section>
          </div>
        );
      })()}

      {/* Main workspace tabs */}
      <div className="flex items-stretch border border-stone-300 rounded-sm overflow-hidden bg-white mb-6 shadow-sm">
        {[
          {
            id: "build",
            label: "Build",
            sub: "Enter data",
            icon: PenLine,
          },
          {
            id: "discuss",
            label: "Discuss",
            sub: "Walk the client through",
            icon: MessageSquare,
          },
          {
            id: "actions",
            label: "Action Items",
            sub: "Year-end + estimated tax",
            icon: CheckSquare,
          },
        ].map((tab, idx) => {
          const Icon = tab.icon;
          const active = activeMainTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveMainTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-3 px-4 py-3 transition-colors ${
                idx > 0 ? "border-l border-stone-300" : ""
              } ${
                active
                  ? "bg-stone-900 text-sky-400"
                  : "bg-white text-stone-700 hover:bg-stone-50"
              }`}
            >
              <Icon size={20} />
              <div className="text-left">
                <div className="sans text-sm font-bold">{tab.label}</div>
                <div
                  className={`sans text-[10px] uppercase tracking-wider ${active ? "text-sky-300/80" : "text-stone-500"}`}
                >
                  {tab.sub}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ───── BUILD TAB ───── */}
      {activeMainTab === "build" && (
        <>
          {/* In-plan How To panel — collapsible, defaults collapsed. */}
          <HowToUsePanel context="plan" />

          {/* Tabs: Business Entities ↔ Personal Return */}
      <section className="mb-6">
        <div className="flex items-stretch border border-stone-300 rounded-sm overflow-hidden bg-white mb-4">
          <button
            onClick={() => setActiveSectionTab("entities")}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 sans text-sm font-semibold transition-colors ${
              activeSectionTab === "entities"
                ? "bg-stone-900 text-sky-400"
                : "bg-white text-stone-700 hover:bg-stone-50"
            }`}
          >
            <Building2 size={18} />
            Business Entities
            <span
              className={`sans text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm ${
                activeSectionTab === "entities"
                  ? "bg-sky-400/20 text-sky-300"
                  : "bg-stone-200 text-stone-700"
              }`}
            >
              {plan.entities.length}
            </span>
          </button>
          <button
            onClick={() => setActiveSectionTab("personal")}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 sans text-sm font-semibold transition-colors border-l border-stone-300 ${
              activeSectionTab === "personal"
                ? "bg-stone-900 text-sky-400"
                : "bg-white text-stone-700 hover:bg-stone-50"
            }`}
          >
            <User size={18} />
            Personal Return (1040)
            {result.planned.k1OrdinaryTotal > 0 && (
              <span
                className={`sans text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm mono num-tabular ${
                  activeSectionTab === "personal"
                    ? "bg-sky-400/20 text-sky-300"
                    : "bg-emerald-100 text-emerald-800"
                }`}
              >
                K-1 ${fmt(result.planned.k1OrdinaryTotal)}
              </span>
            )}
          </button>
        </div>

        {activeSectionTab === "entities" ? (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Building2 size={20} className="text-sky-400" />
                <div className="serif text-2xl font-bold text-stone-100">Business Entities</div>
                <span className="sans text-xs text-stone-400">
                  ({plan.entities.length}{" "}
                  {plan.entities.length === 1 ? "entity" : "entities"})
                </span>
              </div>
              <div className="flex items-center gap-2">
                {plan.entities.length > 1 && (
                  <button
                    onClick={() => {
                      const anyExpanded = plan.entities.some((e) => !e.collapsed);
                      onUpdatePlan({
                        ...plan,
                        entities: plan.entities.map((e) => ({
                          ...e,
                          collapsed: anyExpanded,
                        })),
                      });
                    }}
                    className="sans text-xs text-stone-300 hover:text-sky-400 font-semibold"
                  >
                    {plan.entities.some((e) => !e.collapsed)
                      ? "Collapse all"
                      : "Expand all"}
                  </button>
                )}
                <button
                  onClick={addEntity}
                  className="flex items-center gap-1.5 bg-sky-400 text-stone-900 px-3 py-1.5 rounded-sm sans text-sm font-bold hover:bg-sky-300"
                >
                  <Plus size={16} />
                  Add Entity
                </button>
              </div>
            </div>

            {plan.entities.length === 0 && (
              <div className="bg-white/5 border border-dashed border-stone-700 rounded-sm p-8 text-center">
                <Building2 size={32} className="text-stone-500 mx-auto mb-2" />
                <div className="sans text-sm text-stone-300">
                  No business entities yet. Add an S-corp, partnership, C-corp, or sole prop
                  to start attaching strategies that flow to the personal return.
                </div>
              </div>
            )}

            <div className="space-y-4">
              {plan.entities.map((ent, idx) => (
                <EntityCard
                  key={ent.id}
                  entity={ent}
                  planId={plan.id}
                  planState={plan.state}
                  scenarios={plan.scenarios}
                  result={result.planned.entities[idx]}
                  baselineResult={result.baseline.entities[idx]}
                  combinedMarginalRate={combinedMarginalRate}
                  marginalById={result.marginalById}
                  onUpdate={(patch) => updateEntity(ent.id, patch)}
                  onRemove={() => {
                    if (confirm(`Remove entity "${ent.name}"?`)) removeEntity(ent.id);
                  }}
                  onAddStrategy={() => setPickerHost({ kind: "entity", id: ent.id })}
                  onUpdateStrategy={(i, patch) =>
                    updateHostStrategy("entity", ent.id, i, patch)
                  }
                  onRemoveStrategy={(i) => removeHostStrategy("entity", ent.id, i)}
                  onOpenStrategyDoc={setStrategyDocOpen}
                />
              ))}
            </div>

            {plan.entities.length > 0 && (
              <div className="flex items-center justify-center mt-4">
                <button
                  onClick={() => setActiveSectionTab("personal")}
                  className="flex items-center gap-2 sans text-xs uppercase tracking-widest font-semibold text-stone-400 hover:text-sky-400"
                >
                  K-1 income flows to Personal Return
                  <ArrowDown size={14} />
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <User size={20} className="text-sky-400" />
              <div className="serif text-2xl font-bold text-stone-100">
                Personal Return (Form 1040)
              </div>
              {result.planned.k1OrdinaryTotal > 0 && (
                <span className="sans text-xs text-stone-400">
                  · receiving{" "}
                  <span className="mono num-tabular font-semibold text-emerald-400">
                    ${fmt(result.planned.k1OrdinaryTotal)}
                  </span>{" "}
                  in K-1 income from {plan.entities.length}{" "}
                  {plan.entities.length === 1 ? "entity" : "entities"}
                </span>
              )}
            </div>
            <IndividualCard
              plan={plan}
              planId={plan.id}
              scenarios={plan.scenarios}
              result={result.planned}
              baselineResult={result.baseline}
              combinedMarginalRate={combinedMarginalRate}
              marginalById={result.marginalById}
              onUpdate={(patch) =>
                onUpdatePlan({ ...plan, individual: { ...plan.individual, ...patch } })
              }
              onAddStrategy={() => setPickerHost({ kind: "individual" })}
              onUpdateStrategy={(i, patch) =>
                updateHostStrategy("individual", null, i, patch)
              }
              onRemoveStrategy={(i) => removeHostStrategy("individual", null, i)}
              onOpenStrategyDoc={setStrategyDocOpen}
            />
          </>
        )}
      </section>
        </>
      )}

      {/* ───── DISCUSS TAB ───── */}
      {activeMainTab === "discuss" && (
        <>
          <ScenarioComparison
            result={result}
            plan={plan}
            onUpdatePlan={onUpdatePlan}
          />

          <PlanCallouts alerts={generateAlerts(plan, result)} audience="all" />

          <MultiYearProjection
            multiYear={result.multiYear}
            plan={plan}
            onUpdatePlan={onUpdatePlan}
            compact
          />

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-white border border-stone-300 rounded-sm p-4">
              <TaxBillBreakdown result={result} compact />
            </div>
            <div className="bg-white border border-stone-300 rounded-sm p-4">
              <BracketPosition result={result} plan={plan} compact />
            </div>
          </div>

          {/* Impact summary */}
          <section className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown size={20} className="text-stone-700" />
              <div className="serif text-xl font-bold text-stone-900">
                Strategy Impact Summary
              </div>
            </div>
            <ImpactSummary plan={plan} result={result} />
          </section>
        </>
      )}

      {/* ───── ACTION ITEMS TAB ───── */}
      {activeMainTab === "actions" && (
        <>
          {/* Estimated tax schedule (same component as in the deliverable) */}
          <div className="bg-white border border-stone-300 rounded-sm p-5 mb-6">
            <EstimatedTaxSchedule
              estimatedTax={result.estimatedTax}
              taxYear={plan.taxYear}
              plannedTotalTax={result.planned.totalTax}
            />
          </div>

          {/* Year-end action punch list */}
          <div className="bg-white border border-stone-300 rounded-sm p-5 mb-6">
            <YearEndChecklist
              items={itemsWithSavings}
              estimatedTax={result.estimatedTax}
              taxYear={plan.taxYear}
              fmtDeadline={fmtDeadline}
            />
          </div>

          {/* Implementation Timeline */}
          <section className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={20} className="text-stone-700" />
              <div className="serif text-xl font-bold text-stone-900">
                Implementation Timeline
              </div>
            </div>
            <ImplementationTimeline plan={plan} />
          </section>

          {/* Lifecycle */}
          <section className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <CircleDot size={20} className="text-stone-700" />
              <div className="serif text-xl font-bold text-stone-900">
                Plan Lifecycle
              </div>
            </div>
            <LifecycleBar
              stage={plan.lifecycle?.stage || "prospect"}
              onChange={(stage) =>
                onUpdatePlan({ ...plan, lifecycle: { ...plan.lifecycle, stage } })
              }
            />
          </section>
        </>
      )}

      {/* Strategy Picker Modal */}
      {pickerHost && (
        <StrategyPicker
          host={pickerHost}
          alreadyAppliedIds={new Set(
            (pickerHost.kind === "individual"
              ? plan.individual?.strategies || []
              : plan.entities.find((e) => e.id === pickerHost.id)?.strategies || []
            ).map((s) => s.strategyId),
          )}
          combinedMarginalRate={combinedMarginalRate}
          customStrategies={customStrategies}
          onSaveCustomStrategy={onSaveCustomStrategy}
          onDeleteCustomStrategy={onDeleteCustomStrategy}
          onPickMany={(strategyIds) =>
            addStrategiesToHost(pickerHost.kind, pickerHost.id, strategyIds)
          }
          onClose={() => setPickerHost(null)}
        />
      )}

      {/* Client Deliverable Modal */}
      {showDeliverable && (
        <ClientDeliverable
          plan={plan}
          result={result}
          combinedMarginalRate={combinedMarginalRate}
          onClose={() => setShowDeliverable(false)}
        />
      )}

      {/* Strategy Brief Modal — opens from any applied strategy that has a detailTemplate */}
      {strategyDocOpen && (
        <StrategyDetailModal
          strategy={strategyDocOpen}
          plan={plan}
          source="plan_workspace"
          onClose={() => setStrategyDocOpen(null)}
        />
      )}
    </main>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Book → Tax Reconciliation — addbacks applied BEFORE cash/accrual.
// Standard items the partner disregards from book to reach an adjusted
// accrual taxable income starting point. Each row is a current-year dollar
// addback. Highlighted because most reconciliations carry at least some
// non-zero figure (depreciation, etc.).
// ════════════════════════════════════════════════════════════════════════
// K-1 Federal Credits Pass-Through — stackable named credits
// Each row: name + amount. Names flow through to the 1040 alongside the
// source entity so the partner can show credits by identity, not lump sum.
// Backward-compat: legacy single passthroughCredits seeds a "Pass-through
// credits" row on first edit if no items array exists.
// ════════════════════════════════════════════════════════════════════════
function K1CreditsList({ entity, onUpdate }) {
  const legacy = parseFloat(entity.passthroughCredits) || 0;
  const items = useMemo(() => {
    if (Array.isArray(entity.passthroughCreditItems) && entity.passthroughCreditItems.length > 0)
      return entity.passthroughCreditItems;
    if (legacy !== 0)
      return [{ id: "legacy", name: "Pass-through credits", amount: legacy }];
    return [];
  }, [entity.passthroughCreditItems, legacy]);

  const total = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  const write = (next) => {
    // Always write to the new array. Clear the legacy single field when items
    // exist so we don't double-count on the next render.
    onUpdate({ passthroughCreditItems: next, passthroughCredits: 0 });
  };
  const addRow = () =>
    write([...items, { id: uid("k1cr"), name: "", amount: 0 }]);
  const updateRow = (idx, patch) =>
    write(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeRow = (idx) => write(items.filter((_, i) => i !== idx));

  return (
    <div className="col-span-3 mt-2 border border-stone-300 rounded-sm bg-white">
      <div className="px-3 py-2 flex items-center justify-between border-b border-stone-200 bg-stone-50/60">
        <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-600">
          Federal Credits Pass-Through{" "}
          <span className="text-stone-500 normal-case font-normal">
            — R&amp;D §41, §45, etc.
          </span>
        </div>
        <div className="sans text-xs text-stone-600">
          Total:{" "}
          <span className="mono num-tabular font-bold text-emerald-700">
            ${fmt(total)}
          </span>
        </div>
      </div>
      <div className="px-2 py-2 space-y-1.5">
        {items.length === 0 ? (
          <div className="px-2 py-2 sans text-xs text-stone-500 italic">
            No credits yet. Click below to add one (named credits flow to the
            1040 with the source entity).
          </div>
        ) : (
          items.map((it, idx) => (
            <div key={it.id} className="flex items-center gap-1.5">
              <input
                type="text"
                value={it.name || ""}
                onChange={(e) => updateRow(idx, { name: e.target.value })}
                placeholder="Credit name (e.g. R&D §41, Energy §45)"
                className="flex-1 min-w-0 px-2 py-1.5 border border-stone-300 rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
              />
              <div className="relative shrink-0">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 sans text-xs text-stone-400 pointer-events-none">
                  $
                </span>
                <input
                  type="number"
                  value={it.amount || 0}
                  onChange={(e) => updateRow(idx, { amount: e.target.value })}
                  className="w-28 pl-5 pr-2 py-1.5 border border-stone-300 rounded-sm mono num-tabular text-sm text-right focus:outline-none focus:border-sky-500"
                />
              </div>
              <button
                type="button"
                onClick={() => removeRow(idx)}
                className="w-7 h-7 rounded-sm flex items-center justify-center text-stone-400 hover:text-rose-600 hover:bg-rose-50 shrink-0"
                title="Remove this credit"
              >
                <X size={14} />
              </button>
            </div>
          ))
        )}
      </div>
      <div className="px-2 pb-2">
        <button
          type="button"
          onClick={addRow}
          className="w-full px-2 py-1.5 sans text-xs font-semibold text-sky-700 hover:text-sky-900 border border-dashed border-stone-300 hover:border-sky-400 rounded-sm flex items-center justify-center gap-1"
        >
          <Plus size={12} />
          Add credit
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
function BookToTaxSection({ entity, onUpdate, result }) {
  const [open, setOpen] = useState(false);
  const bt = entity.bookToTax || {};
  const n = (v) => parseFloat(v) || 0;
  const lines = [
    { key: "bookDepreciation", label: "Book Depreciation" },
    { key: "bookAmortization", label: "Book Amortization" },
    { key: "principalPayments", label: "Principal Payments (Expensed)" },
    { key: "mortgagePayments", label: "Mortgage Payments through P&L" },
    { key: "nonDeductibleTax", label: "Non-Deductible Tax Payments" },
  ];
  const otherItems = Array.isArray(bt.otherItems) ? bt.otherItems : [];
  const otherItemsSum = otherItems.reduce((s, i) => s + n(i.amount), 0);
  const totalAddback =
    lines.reduce((s, l) => s + n(bt[l.key]), 0) + n(bt.other) + otherItemsSum;
  const hasAny = totalAddback !== 0;
  const updateBT = (patch) => onUpdate({ bookToTax: { ...bt, ...patch } });
  const addOtherRow = () =>
    updateBT({
      otherItems: [
        ...otherItems,
        { id: uid("bto"), description: "", amount: 0 },
      ],
    });
  const updateOtherRow = (idx, patch) =>
    updateBT({
      otherItems: otherItems.map((it, i) =>
        i === idx ? { ...it, ...patch } : it,
      ),
    });
  const removeOtherRow = (idx) =>
    updateBT({ otherItems: otherItems.filter((_, i) => i !== idx) });

  return (
    <div
      className={`mx-5 mb-4 border rounded-sm ${
        hasAny ? "border-sky-300 bg-sky-50/30" : "border-stone-200 bg-stone-50/40"
      }`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-stone-100/50"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-600">
            Book → Tax Reconciliation
          </span>
          <span className="sans text-xs text-stone-500">
            (book addbacks applied BEFORE cash/accrual)
          </span>
        </div>
        <div className="sans text-xs text-stone-600">
          Total addback:{" "}
          <span
            className={`mono num-tabular font-semibold ${
              totalAddback > 0 ? "text-emerald-700" : totalAddback < 0 ? "text-rose-700" : "text-stone-900"
            }`}
          >
            {totalAddback < 0 ? "−" : "+"}${fmt(Math.abs(totalAddback))}
          </span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3">
          <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-100">
                <tr>
                  <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-left px-3 py-2">
                    Item
                  </th>
                  <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-right px-3 py-2 w-40">
                    Addback
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.key} className="border-t border-stone-100">
                    <td className="px-3 py-1.5 sans text-sm text-stone-700">
                      {line.label}
                    </td>
                    <td className="px-2 py-1">
                      <MoneyInput
                        value={bt[line.key] || 0}
                        onChange={(v) => updateBT({ [line.key]: v })}
                      />
                    </td>
                  </tr>
                ))}
                {/* Legacy single Other row — kept only when it carries a non-zero
                    value so old plans don't lose data. New plans should add via
                    the stackable rows below. */}
                {(n(bt.other) !== 0 || (bt.otherNote || "").length > 0) && (
                  <tr className="border-t border-stone-100">
                    <td className="px-3 py-1.5 sans text-sm text-stone-700">
                      <div className="flex items-center gap-2">
                        <span>Other (legacy)</span>
                        <input
                          type="text"
                          value={bt.otherNote || ""}
                          onChange={(e) => updateBT({ otherNote: e.target.value })}
                          placeholder="Describe..."
                          className="flex-1 px-2 py-1 border border-stone-300 rounded-sm sans text-xs focus:outline-none focus:border-sky-500"
                        />
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <MoneyInput
                        value={bt.other || 0}
                        onChange={(v) => updateBT({ other: v })}
                      />
                    </td>
                  </tr>
                )}
                {/* Stackable additional addback rows */}
                {otherItems.map((it, idx) => (
                  <tr key={it.id} className="border-t border-stone-100">
                    <td className="px-3 py-1.5 sans text-sm text-stone-700">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => removeOtherRow(idx)}
                          className="w-5 h-5 rounded-sm flex items-center justify-center text-stone-400 hover:text-rose-600 hover:bg-rose-50 shrink-0"
                          title="Remove this row"
                        >
                          <X size={12} />
                        </button>
                        <input
                          type="text"
                          value={it.description || ""}
                          onChange={(e) =>
                            updateOtherRow(idx, { description: e.target.value })
                          }
                          placeholder="Describe this addback…"
                          className="flex-1 px-2 py-1 border border-stone-300 rounded-sm sans text-xs focus:outline-none focus:border-sky-500"
                        />
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <MoneyInput
                        value={it.amount || 0}
                        onChange={(v) => updateOtherRow(idx, { amount: v })}
                      />
                    </td>
                  </tr>
                ))}
                {/* Add Item button row */}
                <tr className="border-t border-stone-100">
                  <td colSpan={2} className="px-2 py-1.5">
                    <button
                      type="button"
                      onClick={addOtherRow}
                      className="w-full px-2 py-1.5 sans text-xs font-semibold text-sky-700 hover:text-sky-900 border border-dashed border-stone-300 hover:border-sky-400 rounded-sm flex items-center justify-center gap-1"
                    >
                      <Plus size={12} />
                      Add another addback
                    </button>
                  </td>
                </tr>
                <tr className="bg-stone-50 border-t-2 border-stone-300">
                  <td className="px-3 py-2 sans text-sm font-semibold text-stone-900 text-right">
                    Total addback to book accrual
                  </td>
                  <td className="px-3 py-2 text-right sans text-base mono num-tabular font-bold text-stone-900">
                    {totalAddback < 0 ? "−" : "+"}${fmt(Math.abs(totalAddback))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-2">
            <Field label="Notes">
              <TextInput
                value={bt.notes || ""}
                onChange={(v) => updateBT({ notes: v })}
                placeholder="Why these items are being added back, sources, prior-year vs this year nuance..."
              />
            </Field>
          </div>
          <div className="sans text-[11px] text-stone-500 mt-2 leading-relaxed">
            These addbacks are applied to the book accrual baseline BEFORE the cash↔accrual
            section runs. The result is the starting point that ties back to what an adjusted
            Schedule L computation would show — exposing the differences between the client's
            books and what's deductible for tax.
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// QBI Inputs — W-2 wages allocable to QBI + UBIA of qualified property.
// Surfaces the wage/UBIA ceiling so partners can sanity-check the
// QBI deduction (or apply the override on the 1040).
// ════════════════════════════════════════════════════════════════════════
function QBIInputsSection({ entity, onUpdate }) {
  const [open, setOpen] = useState(false);
  const wages = parseFloat(entity.qbiWages) || 0;
  const ubia = parseFloat(entity.qbiUBIA) || 0;
  // §199A wage/UBIA limit: greater of (50% × W-2 wages) and (25% × W-2 wages + 2.5% × UBIA)
  const wageOnly = 0.5 * wages;
  const wageUBIA = 0.25 * wages + 0.025 * ubia;
  const wageUBIALimit = Math.max(wageOnly, wageUBIA);

  return (
    <div className="mx-5 mb-4 border border-stone-200 rounded-sm bg-stone-50/40">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-stone-100/50"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-600">
            QBI Wage / UBIA Limit Inputs
          </span>
          <span className="sans text-xs text-stone-500">
            (§199A — apply when taxable income exceeds the threshold)
          </span>
        </div>
        <div className="sans text-xs text-stone-600">
          Wage/UBIA ceiling:{" "}
          <span className="mono num-tabular font-semibold text-stone-900">
            ${fmt(wageUBIALimit)}
          </span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3 grid grid-cols-12 gap-3">
          <div className="col-span-4">
            <Field label="W-2 Wages allocable to QBI activity">
              <MoneyInput
                value={entity.qbiWages || 0}
                onChange={(v) => onUpdate({ qbiWages: v })}
              />
            </Field>
          </div>
          <div className="col-span-4">
            <Field label="UBIA of Qualified Property">
              <MoneyInput
                value={entity.qbiUBIA || 0}
                onChange={(v) => onUpdate({ qbiUBIA: v })}
              />
            </Field>
          </div>
          <div className="col-span-4 flex flex-col justify-end">
            <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1">
              §199A Wage/UBIA Ceiling
            </div>
            <div className="mono num-tabular text-lg font-bold text-stone-900">
              ${fmt(wageUBIALimit)}
            </div>
            <div className="sans text-[10px] text-stone-500 mt-0.5">
              greater of 50% wages (${fmt(wageOnly)}) and 25% wages + 2.5% UBIA (${fmt(wageUBIA)})
            </div>
          </div>
          <div className="col-span-12 sans text-[11px] text-stone-500 leading-relaxed">
            QBI deduction is the lesser of 20% × QBI and this wage/UBIA ceiling (when above the
            §199A taxable-income threshold). If the auto-calc on the 1040 produces a number that
            ignores these limits, override QBI directly on the personal return.
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Cash ↔ Accrual Adjustment Section
// ════════════════════════════════════════════════════════════════════════
function CashAccrualSection({ entity, onUpdate, result }) {
  const [open, setOpen] = useState(false);
  const ca = entity.cashAccrual || {};
  const n = (v) => parseFloat(v) || 0;
  const taxMethod = entity.taxMethod || "accrual";
  const willApply = taxMethod === "cash" || ca.appliedAdjustmentOverride !== "";
  const updateCA = (patch) => onUpdate({ cashAccrual: { ...ca, ...patch } });

  // Six standard cash↔accrual lines. Direction comment shows the sign that
  // gets ADDED to accrual income to get cash income for that line item.
  const lines = [
    { key: "AR", label: "Accounts Receivable", priorKey: "priorAR", currKey: "currentAR", yeKey: "yeAR", direction: "minus" },
    { key: "AP", label: "Accounts Payable", priorKey: "priorAP", currKey: "currentAP", yeKey: "yeAP", direction: "plus" },
    { key: "AccruedLiab", label: "Accrued Liabilities", priorKey: "priorAccruedLiab", currKey: "currentAccruedLiab", yeKey: "yeAccruedLiab", direction: "plus" },
    { key: "Prepaid", label: "Prepaid Expenses", priorKey: "priorPrepaid", currKey: "currentPrepaid", yeKey: "yePrepaid", direction: "minus" },
    { key: "ContractAsset", label: "Contract Assets", priorKey: "priorContractAsset", currKey: "currentContractAsset", yeKey: "yeContractAsset", direction: "minus" },
    { key: "ContractLiab", label: "Contract Liabilities (Deferred Revenue)", priorKey: "priorContractLiab", currKey: "currentContractLiab", yeKey: "yeContractLiab", direction: "plus" },
  ];

  // The "ending" balance the adjustment uses: prefer Year-End forecast when
  // entered (full-year projection), else fall back to Current YTD.
  const endingFor = (line) => {
    const ye = n(ca[line.yeKey]);
    return ye !== 0 ? ye : n(ca[line.currKey]);
  };
  // Whether this line is using the YE forecast (true) or YTD current (false)
  const usingForecast = (line) => n(ca[line.yeKey]) !== 0;

  // Per-line adjustment: direction "minus" means subtract Δ (PriorCurr style);
  // direction "plus" means add Δ.
  const lineAdj = (line) => {
    const prior = n(ca[line.priorKey]);
    const ending = endingFor(line);
    return line.direction === "minus" ? prior - ending : ending - prior;
  };

  // Custom partner-added lines
  const customLines = Array.isArray(ca.customLines) ? ca.customLines : [];
  const customLineAdj = (cl) => {
    const prior = n(cl.prior);
    const ye = n(cl.ye);
    const curr = n(cl.current);
    const ending = ye !== 0 ? ye : curr;
    return cl.direction === "liability" ? ending - prior : prior - ending;
  };
  const customLineUsingForecast = (cl) => n(cl.ye) !== 0;

  const computedDelta =
    lines.reduce((sum, l) => sum + lineAdj(l), 0) +
    customLines.reduce((sum, cl) => sum + customLineAdj(cl), 0);
  const anyForecastUsed =
    lines.some(usingForecast) || customLines.some(customLineUsingForecast);

  const addCustomLine = () =>
    updateCA({
      customLines: [
        ...customLines,
        {
          id: uid("cax"),
          label: "",
          prior: 0,
          current: 0,
          ye: 0,
          direction: "asset",
        },
      ],
    });
  const updateCustomLine = (idx, patch) =>
    updateCA({
      customLines: customLines.map((cl, i) =>
        i === idx ? { ...cl, ...patch } : cl,
      ),
    });
  const removeCustomLine = (idx) =>
    updateCA({ customLines: customLines.filter((_, i) => i !== idx) });

  return (
    <div className="mx-5 mb-4 border border-stone-200 rounded-sm bg-stone-50/40">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-stone-100/50"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-600">
            Cash ↔ Accrual Adjustments
          </span>
          <span className="sans text-xs text-stone-500">
            (prior year vs forecast — for cash-basis tax adjustment)
          </span>
        </div>
        <div className="sans text-xs text-stone-600">
          Computed delta:{" "}
          <span className="mono num-tabular font-semibold text-stone-900">
            {computedDelta < 0 ? "−" : "+"}${fmt(Math.abs(computedDelta))}
          </span>
          {willApply && (
            <span className="ml-2 text-emerald-700 sans text-[10px] uppercase tracking-wider font-semibold">
              Applied
            </span>
          )}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3">
          <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-100">
                <tr>
                  <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-left px-3 py-2">
                    Account
                  </th>
                  <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-right px-3 py-2 w-32">
                    Prior Year
                    <div className="sans text-[8px] font-normal text-stone-400 normal-case tracking-normal mt-0.5">
                      12/31 last yr
                    </div>
                  </th>
                  <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-right px-3 py-2 w-32">
                    Current
                    <div className="sans text-[8px] font-normal text-stone-400 normal-case tracking-normal mt-0.5">
                      Latest month-end
                    </div>
                  </th>
                  <th className="sans text-[10px] uppercase tracking-widest font-semibold text-sky-700 text-right px-3 py-2 w-32 bg-sky-50/60">
                    Year-End Forecast
                    <div className="sans text-[8px] font-normal text-sky-600 normal-case tracking-normal mt-0.5">
                      Projected 12/31
                    </div>
                  </th>
                  <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-right px-3 py-2 w-32">
                    Adjustment
                    <div className="sans text-[8px] font-normal text-stone-400 normal-case tracking-normal mt-0.5">
                      Uses YE if entered
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  const adj = lineAdj(line);
                  const forecasted = usingForecast(line);
                  return (
                    <tr key={line.key} className="border-t border-stone-100">
                      <td className="px-3 py-1.5 sans text-sm text-stone-700">
                        {line.label}
                      </td>
                      <td className="px-2 py-1">
                        <MoneyInput
                          value={ca[line.priorKey] || 0}
                          onChange={(v) => updateCA({ [line.priorKey]: v })}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <MoneyInput
                          value={ca[line.currKey] || 0}
                          onChange={(v) => updateCA({ [line.currKey]: v })}
                        />
                      </td>
                      <td className="px-2 py-1 bg-sky-50/30">
                        <MoneyInput
                          value={ca[line.yeKey] || 0}
                          onChange={(v) => updateCA({ [line.yeKey]: v })}
                        />
                      </td>
                      <td
                        className={`px-3 py-1.5 text-right sans text-sm mono num-tabular font-semibold ${
                          adj > 0
                            ? "text-emerald-700"
                            : adj < 0
                              ? "text-rose-700"
                              : "text-stone-500"
                        }`}
                        title={forecasted ? "Uses Year-End forecast" : "Uses Current (YTD)"}
                      >
                        {adj === 0
                          ? "—"
                          : `${adj < 0 ? "−" : "+"}$${fmt(Math.abs(adj))}`}
                        {forecasted && adj !== 0 && (
                          <span
                            className="ml-1 sans text-[8px] uppercase tracking-wider font-bold text-sky-600 align-top"
                            title="Adjustment based on Year-End forecast"
                          >
                            YE
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {/* Custom partner-added rows */}
                {customLines.map((cl, idx) => {
                  const adj = customLineAdj(cl);
                  const forecasted = customLineUsingForecast(cl);
                  return (
                    <tr key={cl.id} className="border-t border-stone-100">
                      <td className="px-3 py-1.5 sans text-sm text-stone-700">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => removeCustomLine(idx)}
                            className="w-5 h-5 rounded-sm flex items-center justify-center text-stone-400 hover:text-rose-600 hover:bg-rose-50 shrink-0"
                            title="Remove this row"
                          >
                            <X size={12} />
                          </button>
                          <input
                            type="text"
                            value={cl.label || ""}
                            onChange={(e) =>
                              updateCustomLine(idx, { label: e.target.value })
                            }
                            placeholder="Account name (e.g. WIP, Inventory)"
                            className="flex-1 min-w-0 px-2 py-1 border border-stone-300 rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              updateCustomLine(idx, {
                                direction:
                                  cl.direction === "liability"
                                    ? "asset"
                                    : "liability",
                              })
                            }
                            className={`sans text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm shrink-0 ${
                              cl.direction === "liability"
                                ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                                : "bg-sky-100 text-sky-800 hover:bg-sky-200"
                            }`}
                            title="Asset = adj is prior−ending (AR-like). Liability = adj is ending−prior (AP-like). Click to flip."
                          >
                            {cl.direction === "liability" ? "Liab" : "Asset"}
                          </button>
                        </div>
                      </td>
                      <td className="px-2 py-1">
                        <MoneyInput
                          value={cl.prior || 0}
                          onChange={(v) => updateCustomLine(idx, { prior: v })}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <MoneyInput
                          value={cl.current || 0}
                          onChange={(v) =>
                            updateCustomLine(idx, { current: v })
                          }
                        />
                      </td>
                      <td className="px-2 py-1 bg-sky-50/30">
                        <MoneyInput
                          value={cl.ye || 0}
                          onChange={(v) => updateCustomLine(idx, { ye: v })}
                        />
                      </td>
                      <td
                        className={`px-3 py-1.5 text-right sans text-sm mono num-tabular font-semibold ${
                          adj > 0
                            ? "text-emerald-700"
                            : adj < 0
                              ? "text-rose-700"
                              : "text-stone-500"
                        }`}
                      >
                        {adj === 0
                          ? "—"
                          : `${adj < 0 ? "−" : "+"}$${fmt(Math.abs(adj))}`}
                        {forecasted && adj !== 0 && (
                          <span className="ml-1 sans text-[8px] uppercase tracking-wider font-bold text-sky-600 align-top">
                            YE
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {/* Add another account button */}
                <tr className="border-t border-stone-100">
                  <td colSpan={5} className="px-2 py-1.5">
                    <button
                      type="button"
                      onClick={addCustomLine}
                      className="w-full px-2 py-1.5 sans text-xs font-semibold text-sky-700 hover:text-sky-900 border border-dashed border-stone-300 hover:border-sky-400 rounded-sm flex items-center justify-center gap-1"
                    >
                      <Plus size={12} />
                      Add another account (WIP, Inventory, custom item, etc.)
                    </button>
                  </td>
                </tr>
                <tr className="bg-stone-50 border-t-2 border-stone-300">
                  <td
                    colSpan={4}
                    className="px-3 py-2 sans text-sm font-semibold text-stone-900 text-right"
                  >
                    Total cash-basis adjustment
                  </td>
                  <td className="px-3 py-2 text-right sans text-base mono num-tabular font-bold text-stone-900">
                    {computedDelta === 0
                      ? "—"
                      : `${computedDelta < 0 ? "−" : "+"}$${fmt(Math.abs(computedDelta))}`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="sans text-xs text-stone-600">
              Override (overrides computed total):
            </div>
            <div className="w-32">
              <MoneyInput
                value={ca.appliedAdjustmentOverride || 0}
                onChange={(v) => updateCA({ appliedAdjustmentOverride: v })}
                placeholder="auto"
              />
            </div>
          </div>
          <div className="sans text-[11px] text-stone-500 mt-2 leading-relaxed">
            Each row's <strong>Adjustment</strong> uses the{" "}
            <span className="text-sky-700 font-semibold">Year-End Forecast</span>{" "}
            balance when entered (the projected 12/31 number — the most accurate
            full-year tax adjustment), otherwise falls back to the{" "}
            <strong>Current</strong> month-end balance. Rows using the forecast
            show a small{" "}
            <span className="sans text-[8px] uppercase tracking-wider font-bold text-sky-600">
              YE
            </span>{" "}
            chip. Adjustment is applied automatically when the entity files on{" "}
            <strong>cash basis for tax</strong>; to force-apply on accrual entities,
            enter a manual override above. Applied adjustment for this entity:{" "}
            <strong>
              {willApply
                ? `${result?.cashAccrualAdjustment < 0 ? "−" : "+"}$${fmt(Math.abs(result?.cashAccrualAdjustment || 0))}`
                : "None"}
            </strong>
            {anyForecastUsed && (
              <span className="text-sky-700">
                {" "}
                · forecast in use on {lines.filter(usingForecast).length} of{" "}
                {lines.length} lines
              </span>
            )}
            .
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Entity Card
// ════════════════════════════════════════════════════════════════════════
function EntityCard({
  entity,
  planId,
  planState,
  scenarios,
  result,
  baselineResult,
  combinedMarginalRate,
  marginalById,
  onUpdate,
  onRemove,
  onAddStrategy,
  onUpdateStrategy,
  onRemoveStrategy,
  onOpenStrategyDoc,
}) {
  const k1Saved = (baselineResult?.ownerK1Ordinary || 0) - (result?.ownerK1Ordinary || 0);
  const collapsed = !!entity.collapsed;
  const toggleCollapsed = () => onUpdate({ collapsed: !collapsed });
  const [collapsedStrategies, setCollapsedStrategies] = useState(false);

  // Per-entity savings for summary header
  const appliedStrategies = (entity.strategies || []).filter((s) => s.applied);
  const strategyCount = appliedStrategies.length;
  const entitySavings = appliedStrategies.reduce((sum, s) => {
    const reduction = strategyReduction(s);
    const credit = strategyCredit(s);
    const manual = strategyManualSavings(s);
    return (
      sum + (manual > 0 ? manual : credit > 0 ? credit : reduction * combinedMarginalRate)
    );
  }, 0);

  // Carryforward total (informational warning if any)
  const carryforwardTotal =
    (parseFloat(entity.federalNOL) || 0) +
    (parseFloat(entity.stateNOL) || 0) +
    (parseFloat(entity.unusedFedCredits) || 0) +
    (parseFloat(entity.unusedStateCredits) || 0);

  return (
    <div className="bg-white border border-stone-300 rounded-sm">
      {/* Header */}
      <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-3">
        <button
          onClick={toggleCollapsed}
          className="text-stone-400 hover:text-stone-700 p-1 -ml-1"
          title={collapsed ? "Expand entity" : "Collapse entity"}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
        </button>
        <Building2 size={18} className="text-sky-500 shrink-0" />
        <input
          value={entity.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          className="serif text-xl font-bold text-stone-900 bg-transparent border-b border-transparent hover:border-stone-300 focus:border-sky-500 focus:outline-none px-1 flex-1 min-w-0"
        />
        <Select
          value={entity.type}
          onChange={(v) => onUpdate({ type: v })}
          options={ENTITY_TYPE_OPTIONS}
          className="!w-48"
        />
        <div className="flex items-center gap-1">
          <span className="sans text-[10px] uppercase tracking-widest text-stone-500">Owner %</span>
          <NumberInput
            value={entity.ownershipPct}
            onChange={(v) => onUpdate({ ownershipPct: v })}
            suffix="%"
            className="!w-20"
          />
        </div>
        {entity.type === "1065" && (
          <label className="flex items-center gap-1.5 cursor-pointer" title="General partner share is subject to SE tax">
            <input
              type="checkbox"
              checked={!!entity.generalPartner}
              onChange={(e) => onUpdate({ generalPartner: e.target.checked })}
              className="w-3.5 h-3.5"
            />
            <span className="sans text-[10px] uppercase tracking-widest text-stone-600 font-semibold">
              GP (SE-taxed)
            </span>
          </label>
        )}
        <button
          onClick={onRemove}
          className="text-stone-400 hover:text-rose-600 p-1.5"
          title="Remove entity"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Collapsed summary strip */}
      {collapsed && (
        <button
          onClick={toggleCollapsed}
          className="w-full px-5 py-3 grid grid-cols-12 gap-3 text-left hover:bg-sky-50/30"
        >
          <SummaryStat label="Taxable Income" value={result?.taxableIncome} cols={3} />
          {result?.isPassthrough && (
            <SummaryStat
              label={`K-1 (${fmtPct(result.ownership)})`}
              value={result?.ownerK1Ordinary}
              color="sky"
              cols={3}
            />
          )}
          {result?.isCCorp && (
            <SummaryStat label="Corp Tax" value={result?.entityTax} color="rose" cols={3} />
          )}
          <SummaryStat
            label="Strategies"
            value={strategyCount}
            isCount
            cols={2}
          />
          <SummaryStat
            label="Est. Saved"
            value={entitySavings}
            color="emerald"
            cols={2}
          />
          {carryforwardTotal > 0 && (
            <SummaryStat
              label="Carryforwards"
              value={carryforwardTotal}
              color="amber"
              cols={2}
            />
          )}
        </button>
      )}

      {/* Body — hidden when collapsed */}
      {!collapsed && (
      <>
      <div className="px-5 py-4 grid grid-cols-12 gap-4">
        {/* Income inputs */}
        <div className="col-span-7 grid grid-cols-2 gap-3">
          <div className="col-span-2 flex items-center gap-3">
            <span className="sans text-[10px] uppercase tracking-widest text-stone-500">
              Tax Method
            </span>
            <div className="flex border border-stone-300 rounded-sm overflow-hidden">
              {["accrual", "cash"].map((m) => (
                <button
                  key={m}
                  onClick={() => onUpdate({ taxMethod: m })}
                  className={`px-3 py-1 sans text-xs font-semibold capitalize ${
                    (entity.taxMethod || "accrual") === m
                      ? "bg-stone-900 text-sky-400"
                      : "bg-white text-stone-600 hover:bg-stone-50"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <Field label="Forecast Revenue">
            <MoneyInput value={entity.revenue} onChange={(v) => onUpdate({ revenue: v })} />
          </Field>
          <Field label="Operating Expenses">
            <MoneyInput value={entity.expenses} onChange={(v) => onUpdate({ expenses: v })} />
          </Field>
          <Field label="Depreciation">
            <MoneyInput
              value={entity.depreciation}
              onChange={(v) => onUpdate({ depreciation: v })}
            />
          </Field>
          <div className="col-span-2">
            <OtherLineItemsField
              label="Other Deductions"
              items={entity.otherDeductionsItems}
              legacyAmount={entity.otherDeductions}
              legacyNote={entity.otherDeductionsNote}
              signHelperPlus="Adds to deductions (reduces taxable income)"
              signHelperMinus="Reduces deductions (increases taxable income)"
              emptyPrompt="No 'Other Deductions' yet — add line items like M-1 adj, owner perks, prior-period corrections."
              onChange={(items, total) =>
                onUpdate({
                  otherDeductionsItems: items,
                  otherDeductions: total,
                })
              }
            />
          </div>
          <Field label="PTE Credit Used (this year)">
            <MoneyInput
              value={entity.pteCreditUsed}
              onChange={(v) => onUpdate({ pteCreditUsed: v })}
            />
          </Field>
          <Field label="PTE Credit Carryforward">
            <MoneyInput
              value={entity.pteCreditCarryforward}
              onChange={(v) => onUpdate({ pteCreditCarryforward: v })}
            />
          </Field>
          <Field
            label="State Adjustment (+/-)"
            hint="State-only addback/deduction — does not affect federal"
          >
            <MoneyInput
              value={entity.stateAdjustment || 0}
              onChange={(v) => onUpdate({ stateAdjustment: v })}
            />
          </Field>
          <Field label="State Adjustment Notes">
            <TextInput
              value={entity.stateAdjustmentNotes || ""}
              onChange={(v) => onUpdate({ stateAdjustmentNotes: v })}
              placeholder="e.g. NC 85% bonus dep addback"
            />
          </Field>
          {/* K-1 flow-through items (separate tax character) */}
          <div className="col-span-2 mt-2 pt-2 border-t border-stone-200">
            <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-2">
              K-1 Pass-Through Items (separate from ordinary income)
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Long-Term Cap Gains" hint="Taxed at LTCG rate on owner 1040">
                <MoneyInput
                  value={entity.ltcg || 0}
                  onChange={(v) => onUpdate({ ltcg: v })}
                />
              </Field>
              <Field label="Short-Term Cap Gains" hint="Taxed at ordinary rate on owner 1040">
                <MoneyInput
                  value={entity.stcg || 0}
                  onChange={(v) => onUpdate({ stcg: v })}
                />
              </Field>
              <div /> {/* spacer — Federal Credits moved to its own full-width row below */}
            </div>
            <K1CreditsList entity={entity} onUpdate={onUpdate} />
          </div>
        </div>

        {/* Computed flow */}
        <div className="col-span-5 bg-stone-50 border border-stone-200 rounded-sm p-4">
          <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-3">
            Forecast Flow
          </div>
          <FlowRow label="Baseline ordinary income" value={baselineResult?.baselineIncome} />
          <FlowRow
            label="Strategy reductions"
            value={-(result?.reductions || 0)}
            highlight={result?.reductions > 0 ? "emerald" : null}
          />
          <FlowRow
            label="Planned ordinary income"
            value={result?.taxableIncome}
            bold
          />
          {result?.isPassthrough && (
            <>
              <div className="border-t border-stone-300 my-2" />
              <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1">
                K-1 to Owner ({fmtPct(result.ownership)})
              </div>
              <FlowRow
                label="Ordinary income"
                value={result?.ownerK1Ordinary}
                color="sky"
                bold
              />
              {(result?.ownerK1LTCG || 0) !== 0 && (
                <FlowRow label="Long-term cap gains" value={result?.ownerK1LTCG} color="sky" />
              )}
              {(result?.ownerK1STCG || 0) !== 0 && (
                <FlowRow label="Short-term cap gains" value={result?.ownerK1STCG} color="sky" />
              )}
              {(result?.ownerK1Credits || 0) > 0 && (
                <FlowRow
                  label="Federal credits"
                  value={result?.ownerK1Credits}
                  color="emerald"
                />
              )}
              {k1Saved > 0 && (
                <div className="sans text-xs text-emerald-700 mt-1">
                  ↓ ${fmt(k1Saved)} less ordinary K-1 than baseline
                </div>
              )}
            </>
          )}
          {result?.isCCorp && (
            <>
              <div className="border-t border-stone-300 my-2" />
              <FlowRow label="Corporate tax (21%)" value={result?.entityTax} bold color="rose" />
            </>
          )}
        </div>
      </div>

      {/* Book → Tax reconciliation (addbacks applied BEFORE cash/accrual) */}
      <BookToTaxSection entity={entity} onUpdate={onUpdate} result={result} />

      {/* Cash↔Accrual adjustments */}
      <CashAccrualSection entity={entity} onUpdate={onUpdate} result={result} />

      {/* Multi-state apportionment */}
      <MultiStateApportionmentSection
        entity={entity}
        planState={planState}
        onUpdate={onUpdate}
      />

      {/* QBI structural inputs (W-2 wages + UBIA per QBI activity) */}
      <QBIInputsSection entity={entity} onUpdate={onUpdate} />

      {/* Carryforwards (NOLs + unused credits) */}
      <CarryforwardsSection entity={entity} onUpdate={onUpdate} />

      {/* Notes & file attachments for this entity */}
      <NotesAndDocsSection
        host={entity}
        hostKey={`entities/${entity.id}`}
        planId={planId}
        onUpdate={onUpdate}
      />

      {/* Strategies on this entity */}
      <div className="px-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500">
              Strategies Applied to This Entity
            </div>
            {(entity.strategies || []).length > 0 && (
              <button
                onClick={() => setCollapsedStrategies(!collapsedStrategies)}
                className="flex items-center gap-1 text-stone-500 hover:text-sky-600 sans text-[10px] font-semibold uppercase tracking-wider"
                title={
                  collapsedStrategies
                    ? "Expand strategies to edit inputs"
                    : "Collapse strategies to one-line rows"
                }
              >
                {collapsedStrategies ? (
                  <>
                    <ChevronDown size={12} />
                    Expand all ({(entity.strategies || []).length})
                  </>
                ) : (
                  <>
                    <ChevronUp size={12} />
                    Collapse all
                  </>
                )}
              </button>
            )}
          </div>
          <button
            onClick={onAddStrategy}
            className="flex items-center gap-1 text-sky-600 hover:text-sky-700 sans text-xs font-semibold"
          >
            <Plus size={14} />
            Add Strategy
          </button>
        </div>
        {(entity.strategies || []).length === 0 ? (
          <div className="text-stone-400 sans text-xs italic py-2">
            No strategies attached. Click "Add Strategy" to apply one.
          </div>
        ) : (
          <div className="space-y-2">
            {(entity.strategies || []).map((stratRef, idx) => (
              <StrategyRow
                key={idx}
                stratRef={stratRef}
                hostType="entity"
                scenarios={scenarios}
                combinedMarginalRate={combinedMarginalRate}
                marginalSavings={marginalById ? (marginalById[stratRef.strategyId] ?? 0) : null}
                onUpdate={(patch) => onUpdateStrategy(idx, patch)}
                onRemove={() => onRemoveStrategy(idx)}
                onOpenDoc={onOpenStrategyDoc}
                compact={collapsedStrategies}
              />
            ))}
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// State Conformity Banner — surfaces federal/state decoupling issues
// for the plan's selected state so the planner remembers to enter
// state adjustments on the right entity.
// ════════════════════════════════════════════════════════════════════════
function StateConformityBanner({ state }) {
  const [open, setOpen] = useState(false);
  const notes = getStateConformityNotes(state);
  if (notes.length === 0) return null;
  const stateName = STATES[state]?.name || state;
  return (
    <div className="mb-4 bg-amber-50 border border-amber-300 rounded-sm">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-amber-100/60"
      >
        <div className="flex items-center gap-2 text-left">
          <AlertCircle size={16} className="text-amber-700 shrink-0" />
          <span className="sans text-sm font-semibold text-amber-900">
            {stateName} has {notes.length} federal/state decoupling{" "}
            {notes.length === 1 ? "issue" : "issues"} to consider
          </span>
          <span className="sans text-xs text-amber-700">
            — use the State Adjustment field on each entity to account for them
          </span>
        </div>
        {open ? (
          <ChevronDown size={16} className="text-amber-700" />
        ) : (
          <ChevronRight size={16} className="text-amber-700" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 border-t border-amber-200">
          <ul className="sans text-sm text-amber-900 space-y-1.5 leading-snug">
            {notes.map((n, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-amber-600 shrink-0">•</span>
                <span>{n}</span>
              </li>
            ))}
          </ul>
          <div className="sans text-[11px] text-amber-700 mt-3 italic">
            These are common pitfalls — verify the current year's exact rules
            before any final return work. Enter the dollar adjustment per
            entity below to apply it to state taxable income only (federal
            calc is unaffected).
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Summary stat — used in collapsed entity header strip
// ════════════════════════════════════════════════════════════════════════
function SummaryStat({ label, value, color = "stone", isCount = false, cols = 3 }) {
  const colors = {
    stone: "text-stone-900",
    sky: "text-sky-700",
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    amber: "text-amber-800",
  };
  const colSpans = {
    2: "col-span-2",
    3: "col-span-3",
    4: "col-span-4",
  };
  return (
    <div className={colSpans[cols] || "col-span-3"}>
      <div className="sans text-[9px] uppercase tracking-widest font-semibold text-stone-500">
        {label}
      </div>
      <div className={`mono num-tabular text-base font-bold mt-0.5 ${colors[color]}`}>
        {isCount ? fmt(value || 0) : `$${fmt(value || 0)}`}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Multi-State Apportionment Section — distribute entity income across
// multiple states with percentages
// ════════════════════════════════════════════════════════════════════════
function MultiStateApportionmentSection({ entity, planState, onUpdate }) {
  const [open, setOpen] = useState(false);
  const apportionment = entity.apportionment || [];
  const isMulti = apportionment.length > 0;
  const totalPct = apportionment.reduce(
    (s, a) => s + (parseFloat(a.pct) || 0),
    0,
  );
  const totalOk = Math.abs(totalPct - 100) < 0.01;
  const homeState = planState || "UT";

  const addState = () => {
    const used = new Set(apportionment.map((a) => a.state));
    const firstUnused =
      STATE_OPTIONS.find((s) => !used.has(s.id))?.id || homeState;
    onUpdate({
      apportionment: [...apportionment, { state: firstUnused, pct: 0 }],
    });
  };

  const updateRow = (idx, patch) => {
    const next = [...apportionment];
    next[idx] = { ...next[idx], ...patch };
    onUpdate({ apportionment: next });
  };

  const removeRow = (idx) => {
    onUpdate({ apportionment: apportionment.filter((_, i) => i !== idx) });
  };

  const clearAll = () => {
    if (!confirm("Reset this entity to single-state (the plan's home state)?"))
      return;
    onUpdate({ apportionment: [] });
  };

  const startMulti = () => {
    onUpdate({
      apportionment: [{ state: homeState, pct: 100 }],
    });
    setOpen(true);
  };

  return (
    <div
      className={`mx-5 mb-4 border rounded-sm ${
        isMulti
          ? totalOk
            ? "border-sky-300 bg-sky-50/30"
            : "border-amber-300 bg-amber-50/40"
          : "border-stone-200 bg-stone-50/40"
      }`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-stone-100/40"
      >
        <div className="flex items-center gap-2 text-left">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-600">
            Multi-State Apportionment
          </span>
          {isMulti ? (
            <span
              className={`sans text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm ${
                totalOk
                  ? "bg-sky-200 text-sky-900"
                  : "bg-amber-200 text-amber-900"
              }`}
            >
              {apportionment.length} state{apportionment.length === 1 ? "" : "s"}
              {!totalOk && ` · ${totalPct.toFixed(1)}% (≠100)`}
            </span>
          ) : (
            <span className="sans text-xs text-stone-500">
              (single-state: {homeState} — click to add states)
            </span>
          )}
        </div>
        {isMulti && (
          <span className="sans text-xs text-stone-600">
            Sourced to {apportionment.length} state
            {apportionment.length === 1 ? "" : "s"}
          </span>
        )}
      </button>
      {open && (
        <div className="px-4 pb-3">
          {!isMulti ? (
            <div className="py-4 text-center">
              <div className="sans text-sm text-stone-700 mb-3">
                This entity's income is currently sourced 100% to the plan's
                home state (<strong>{homeState}</strong>). For clients doing
                business in multiple states, add each state with its
                apportionment percentage so non-resident state tax is captured.
              </div>
              <button
                onClick={startMulti}
                className="inline-flex items-center gap-1.5 bg-stone-900 text-sky-400 px-3 py-2 rounded-sm sans text-xs font-bold hover:bg-stone-800"
              >
                <Plus size={14} />
                Enable Multi-State Apportionment
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-1.5 mb-3">
                <div className="grid grid-cols-12 gap-2 sans text-[9px] uppercase tracking-wider font-semibold text-stone-500 px-1">
                  <div className="col-span-7">State</div>
                  <div className="col-span-3">Apportionment %</div>
                  <div className="col-span-2"></div>
                </div>
                {apportionment.map((row, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-12 gap-2 items-center"
                  >
                    <div className="col-span-7">
                      <Select
                        value={row.state}
                        onChange={(v) => updateRow(idx, { state: v })}
                        options={STATE_OPTIONS.map((s) => ({
                          id: s.id,
                          label: `${s.id} — ${s.name}${
                            s.topRate === 0 ? " (no income tax)" : ""
                          }`,
                        }))}
                      />
                    </div>
                    <div className="col-span-3">
                      <NumberInput
                        value={row.pct}
                        onChange={(v) => updateRow(idx, { pct: v })}
                        suffix="%"
                      />
                    </div>
                    <div className="col-span-2 flex items-center gap-1">
                      <span className="sans text-[10px] text-stone-500 mono num-tabular flex-1">
                        {STATES[row.state]?.topRate
                          ? `${(STATES[row.state].topRate * 100).toFixed(2)}%`
                          : "—"}
                      </span>
                      <button
                        onClick={() => removeRow(idx)}
                        className="text-stone-400 hover:text-rose-600 p-1"
                        title="Remove this state"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={addState}
                  className="flex items-center gap-1 text-sky-600 hover:text-sky-700 sans text-xs font-semibold"
                >
                  <Plus size={14} />
                  Add State
                </button>
                <div className="flex-1 sans text-xs text-stone-600 text-right">
                  Total apportioned:{" "}
                  <span
                    className={`mono num-tabular font-semibold ${
                      totalOk ? "text-stone-900" : "text-amber-700"
                    }`}
                  >
                    {totalPct.toFixed(1)}%
                  </span>{" "}
                  {totalOk ? (
                    <span className="text-emerald-700">✓</span>
                  ) : (
                    <span className="text-amber-700">— should equal 100%</span>
                  )}
                </div>
                <button
                  onClick={clearAll}
                  className="sans text-xs text-stone-500 hover:text-rose-700 underline"
                >
                  Reset to single-state
                </button>
              </div>
              <div className="sans text-[11px] text-stone-500 leading-relaxed bg-white border border-stone-200 rounded-sm px-3 py-2">
                Each state's portion of this entity's income is taxed at that
                state's top rate. Sum becomes the state tax attributable to
                this entity (paid by the owner via K-1 for pass-throughs, or
                the entity itself for C-corps). Simplified model — does not
                compute the credit for taxes paid to non-resident states.
                Useful for planning approximations; verify with the actual
                returns for precision.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Carryforwards Section — NOLs and unused tax credits
// ════════════════════════════════════════════════════════════════════════
function CarryforwardsSection({ entity, onUpdate }) {
  const [open, setOpen] = useState(false);
  // NOLs are 1040 attributes for pass-throughs (they flow up); only C-corps
  // accrue NOLs at the entity level. Hide the NOL fields for pass-throughs
  // to direct partners to the personal-return carryforwards section.
  const isCCorp = entity.type === "1120";
  const total =
    (isCCorp ? (parseFloat(entity.federalNOL) || 0) + (parseFloat(entity.stateNOL) || 0) : 0) +
    (parseFloat(entity.unusedFedCredits) || 0) +
    (parseFloat(entity.unusedStateCredits) || 0);
  const hasAny = total > 0;

  return (
    <div
      className={`mx-5 mb-4 border rounded-sm ${
        hasAny ? "border-amber-300 bg-amber-50/40" : "border-stone-200 bg-stone-50/40"
      }`}
    >
      <button
        onClick={() => setOpen(!open)}
        className={`w-full px-4 py-2 flex items-center justify-between ${
          hasAny ? "hover:bg-amber-100/50" : "hover:bg-stone-100/50"
        }`}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-600">
            Carryforwards — NOLs & Unused Credits
          </span>
          {hasAny && (
            <span className="sans text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm bg-amber-200 text-amber-900">
              Don't lose ${fmt(total)}
            </span>
          )}
          <span className="sans text-xs text-stone-500">
            (track unused balances from prior years)
          </span>
        </div>
        <div className="sans text-xs text-stone-600">
          Total tracked:{" "}
          <span className="mono num-tabular font-semibold text-stone-900">${fmt(total)}</span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3 grid grid-cols-12 gap-3">
          {isCCorp && (
            <>
              <div className="col-span-3">
                <Field label="Federal NOL Carryforward">
                  <MoneyInput
                    value={entity.federalNOL || 0}
                    onChange={(v) => onUpdate({ federalNOL: v })}
                  />
                </Field>
              </div>
              <div className="col-span-3">
                <Field label="State NOL Carryforward">
                  <MoneyInput
                    value={entity.stateNOL || 0}
                    onChange={(v) => onUpdate({ stateNOL: v })}
                  />
                </Field>
              </div>
            </>
          )}
          <div className="col-span-6">
            <OtherLineItemsField
              label="Unused Federal Credits — named line items"
              items={entity.unusedFedCreditsItems}
              legacyAmount={entity.unusedFedCredits}
              legacyNote={entity.carryforwardNotes}
              signHelperPlus="Adds to available federal credit carryforward"
              signHelperMinus="Reduces available federal credit"
              emptyPrompt="No federal credit carryforwards yet — add items like 'R&D credit 2023 ($45K, expires 2043)'."
              onChange={(items, total) =>
                onUpdate({
                  unusedFedCreditsItems: items,
                  unusedFedCredits: total,
                })
              }
            />
          </div>
          <div className="col-span-6">
            <OtherLineItemsField
              label="Unused State Credits — named line items"
              items={entity.unusedStateCreditsItems}
              legacyAmount={entity.unusedStateCredits}
              legacyNote={entity.carryforwardNotes}
              signHelperPlus="Adds to available state credit carryforward"
              signHelperMinus="Reduces available state credit"
              emptyPrompt="No state credit carryforwards yet — add items like 'UT enterprise zone 2024 ($12K)'."
              onChange={(items, total) =>
                onUpdate({
                  unusedStateCreditsItems: items,
                  unusedStateCredits: total,
                })
              }
            />
          </div>
          <div className="col-span-12">
            <Field label="Additional Notes (overall plan to use carryforwards, expiration concerns, etc.)">
              <TextInput
                value={entity.carryforwardNotes || ""}
                onChange={(v) => onUpdate({ carryforwardNotes: v })}
                placeholder="e.g. Plan: apply $20K of R&D credit against this year's tax; rest carries to TY2027."
              />
            </Field>
          </div>
          <div className="col-span-12 sans text-[11px] text-stone-500 leading-relaxed">
            {isCCorp
              ? "C-corp NOLs and unused credits live at the entity level. Each named credit row carries its own dollar amount and flows into the running total at the top of the section."
              : "Pass-through NOLs are 1040 attributes — track them under the Personal Return → Carryforwards section. Entity-level unused credits (R&D §41, Energy §45, etc.) flow to the owner but are tracked here for visibility — name each credit by row so totals stay clean."}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Notes & Documents Section — per-entity free-text notes + file uploads
// (Excel, PDFs, anything) stored in Supabase Storage
// ════════════════════════════════════════════════════════════════════════
function NotesAndDocsSection({ host, hostKey, planId, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  const attachments = host?.attachments || [];
  const hasNotes = (host?.notes || "").trim().length > 0;
  const attachmentCount = attachments.length;
  const hasContent = hasNotes || attachmentCount > 0;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (!planId) {
      setUploadError("Plan ID missing — save the plan and reopen.");
      return;
    }
    setUploading(true);
    setUploadError("");
    const newAttachments = [...attachments];
    try {
      for (const f of files) {
        // 50MB default Supabase limit per file
        if (f.size > 50 * 1024 * 1024) {
          setUploadError(`${f.name} exceeds 50MB upload limit.`);
          continue;
        }
        const meta = await uploadAttachment(planId, hostKey, f);
        if (meta) newAttachments.push(meta);
      }
      onUpdate({ attachments: newAttachments });
    } catch (e) {
      setUploadError(e?.message || "Upload failed.");
    } finally {
      setUploading(false);
      // Reset the file input so re-uploading the same file works
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (att) => {
    const url = await getAttachmentDownloadUrl(att.storagePath, att.fileName);
    if (url) window.open(url, "_blank");
    else alert("Could not generate download link.");
  };

  const handleDelete = async (att) => {
    if (!confirm(`Delete attachment "${att.fileName}"?`)) return;
    const ok = await deleteAttachment(att.storagePath);
    if (ok) {
      onUpdate({ attachments: attachments.filter((a) => a.id !== att.id) });
    }
  };

  const fmtFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="mx-5 mb-4 border border-stone-200 rounded-sm bg-stone-50/40">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-stone-100/50"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-600">
            Notes & Documents
          </span>
          <span className="sans text-xs text-stone-500">
            (free-text notes plus attached Excel docs, PDFs, anything)
          </span>
        </div>
        <div className="flex items-center gap-2">
          {hasNotes && (
            <span className="sans text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm bg-sky-100 text-sky-800">
              Has Notes
            </span>
          )}
          {attachmentCount > 0 && (
            <span className="sans text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 flex items-center gap-1">
              <Paperclip size={10} />
              {attachmentCount} file{attachmentCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-3">
          <Field label="Notes">
            <textarea
              value={host?.notes || ""}
              onChange={(e) => onUpdate({ notes: e.target.value })}
              placeholder="Anything worth remembering — depreciation method history, prior-year disputes, owner notes, follow-ups, etc."
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500 resize-y"
            />
          </Field>

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-600">
                Attached Files
              </span>
              <label
                className={`flex items-center gap-1.5 px-3 py-1.5 sans text-xs font-semibold rounded-sm cursor-pointer ${
                  uploading
                    ? "bg-stone-300 text-stone-500 cursor-wait"
                    : "bg-stone-900 text-sky-400 hover:bg-stone-800"
                }`}
              >
                {uploading ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    Uploading…
                  </>
                ) : (
                  <>
                    <Upload size={12} />
                    Upload File
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  disabled={uploading}
                  className="hidden"
                  onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                />
              </label>
            </div>

            {uploadError && (
              <div className="mb-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-sm sans text-xs text-rose-800">
                {uploadError}
              </div>
            )}

            {attachmentCount === 0 ? (
              <div className="text-stone-400 sans text-xs italic py-2 text-center bg-white border border-dashed border-stone-200 rounded-sm">
                No files attached. Upload depreciation schedules, K-1s, cost
                seg studies, prior returns — anything that travels with this
                entity.
              </div>
            ) : (
              <div className="space-y-1.5">
                {attachments.map((att) => (
                  <div
                    key={att.id}
                    className="flex items-center gap-2 bg-white border border-stone-200 rounded-sm px-3 py-2"
                  >
                    <Paperclip
                      size={14}
                      className="text-stone-500 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="sans text-sm font-semibold text-stone-900 truncate">
                        {att.fileName}
                      </div>
                      <div className="sans text-[11px] text-stone-500">
                        {fmtFileSize(att.fileSize)}
                        {att.uploadedAt && (
                          <>
                            {" · "}
                            {new Date(att.uploadedAt).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </>
                        )}
                        {att.uploadedBy && <> · by {att.uploadedBy}</>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(att)}
                      className="p-1.5 text-stone-500 hover:text-sky-700"
                      title="Download"
                    >
                      <Download size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(att)}
                      className="p-1.5 text-stone-400 hover:text-rose-600"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="sans text-[11px] text-stone-500 mt-2 leading-relaxed">
              Files are private to the firm and visible to all approved users.
              50MB per file. Stored in Supabase with row-level security.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Personal Carryforwards Section (1040)
// ════════════════════════════════════════════════════════════════════════
function IndividualCarryforwardsSection({ ind, onUpdate }) {
  const [open, setOpen] = useState(false);
  const total =
    (parseFloat(ind.federalNOL) || 0) +
    (parseFloat(ind.stateNOL) || 0) +
    (parseFloat(ind.capitalLossCarryforward) || 0) +
    (parseFloat(ind.charitableCarryforward) || 0) +
    (parseFloat(ind.section163jCarryforward) || 0) +
    (parseFloat(ind.passiveLossCarryforward) || 0) +
    (parseFloat(ind.unusedFedCredits) || 0) +
    (parseFloat(ind.unusedStateCredits) || 0);
  const hasAny = total > 0;

  return (
    <div
      className={`mx-5 mb-4 border rounded-sm ${
        hasAny ? "border-amber-300 bg-amber-50/40" : "border-stone-200 bg-stone-50/40"
      }`}
    >
      <button
        onClick={() => setOpen(!open)}
        className={`w-full px-4 py-2 flex items-center justify-between ${
          hasAny ? "hover:bg-amber-100/50" : "hover:bg-stone-100/50"
        }`}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-600">
            Personal Carryforwards — NOLs, Cap Loss & Unused Credits
          </span>
          {hasAny && (
            <span className="sans text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm bg-amber-200 text-amber-900">
              Don't lose ${fmt(total)}
            </span>
          )}
        </div>
        <div className="sans text-xs text-stone-600">
          Total tracked:{" "}
          <span className="mono num-tabular font-semibold text-stone-900">${fmt(total)}</span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3 grid grid-cols-12 gap-3">
          <div className="col-span-3">
            <Field label="Federal NOL Carryforward">
              <MoneyInput
                value={ind.federalNOL || 0}
                onChange={(v) => onUpdate({ federalNOL: v })}
              />
            </Field>
          </div>
          <div className="col-span-3">
            <Field label="State NOL Carryforward">
              <MoneyInput
                value={ind.stateNOL || 0}
                onChange={(v) => onUpdate({ stateNOL: v })}
              />
            </Field>
          </div>
          <div className="col-span-3">
            <Field label="Capital Loss Carryforward">
              <MoneyInput
                value={ind.capitalLossCarryforward || 0}
                onChange={(v) => onUpdate({ capitalLossCarryforward: v })}
              />
            </Field>
          </div>
          <div className="col-span-3">
            <Field label="Charitable Carryforward" hint="Excess contribution amounts carried over (5-year limit for cash; 5-year for capital gain property)">
              <MoneyInput
                value={ind.charitableCarryforward || 0}
                onChange={(v) => onUpdate({ charitableCarryforward: v })}
              />
            </Field>
          </div>
          <div className="col-span-3">
            <Field label="§163(j) Interest Carryforward" hint="Disallowed business interest expense carryover">
              <MoneyInput
                value={ind.section163jCarryforward || 0}
                onChange={(v) => onUpdate({ section163jCarryforward: v })}
              />
            </Field>
          </div>
          <div className="col-span-3">
            <Field label="Passive Loss Carryforward" hint="Suspended §469 losses awaiting passive income or full disposition">
              <MoneyInput
                value={ind.passiveLossCarryforward || 0}
                onChange={(v) => onUpdate({ passiveLossCarryforward: v })}
              />
            </Field>
          </div>
          <div className="col-span-3"></div>
          <div className="col-span-6">
            <OtherLineItemsField
              label="Unused Federal Credits"
              items={ind.unusedFedCreditsItems}
              legacyAmount={ind.unusedFedCredits}
              legacyNote={ind.unusedFedCreditsNote}
              signHelperPlus="Adds to available federal credits"
              signHelperMinus="Reduces available federal credits"
              emptyPrompt="No federal credit carryforwards yet — add line items like 'R&D credit 2023', 'energy credit 2022'."
              onChange={(items, total) =>
                onUpdate({
                  unusedFedCreditsItems: items,
                  unusedFedCredits: total,
                })
              }
            />
          </div>
          <div className="col-span-6">
            <OtherLineItemsField
              label="Unused State Credits"
              items={ind.unusedStateCreditsItems}
              legacyAmount={ind.unusedStateCredits}
              legacyNote={ind.unusedStateCreditsNote}
              signHelperPlus="Adds to available state credits"
              signHelperMinus="Reduces available state credits"
              emptyPrompt="No state credit carryforwards yet — add line items like 'UT enterprise zone', 'CA hiring credit'."
              onChange={(items, total) =>
                onUpdate({
                  unusedStateCreditsItems: items,
                  unusedStateCredits: total,
                })
              }
            />
          </div>
          <div className="col-span-12">
            <Field label="Notes (type, year, expiration, plan to use)">
              <TextInput
                value={ind.carryforwardNotes || ""}
                onChange={(v) => onUpdate({ carryforwardNotes: v })}
                placeholder="e.g. $18K capital loss carryforward from 2024 stock loss; AMT credit $5K from 2022."
              />
            </Field>
          </div>
          <div className="col-span-12 sans text-[11px] text-stone-500 leading-relaxed">
            Capital loss carryforward applies $3K/year against ordinary income; remainder offsets
            capital gains. NOLs post-2017 are limited to 80% of taxable income. Charitable
            carryforwards have 5-year limits per category. §163(j) disallowed interest carries forward
            indefinitely. Passive losses release in full when activity is fully disposed.
          </div>
        </div>
      )}
    </div>
  );
}

function FlowRow({ label, value, bold = false, highlight = null, color = "stone" }) {
  const colors = {
    stone: "text-stone-900",
    sky: "text-sky-700",
    emerald: "text-emerald-700",
    rose: "text-rose-700",
  };
  return (
    <div className="flex items-center justify-between text-sm py-0.5">
      <span className={`sans ${bold ? "font-semibold text-stone-900" : "text-stone-600"}`}>
        {label}
      </span>
      <span
        className={`mono num-tabular ${bold ? "font-bold" : ""} ${
          highlight ? `text-${highlight}-700` : colors[color]
        }`}
      >
        {value < 0 ? "−" : ""}${fmt(Math.abs(value || 0))}
      </span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Plan Callouts — auto-generated alerts to discuss with the client.
// Severity: urgent | warning | watch | info.
// Audience: planner | client | both. The deliverable filters out
// planner-only items.
// ════════════════════════════════════════════════════════════════════════
const ALERT_SEV_CONFIG = {
  urgent: {
    border: "border-rose-300",
    bg: "bg-rose-50/60",
    headerBg: "bg-rose-100",
    titleColor: "text-rose-900",
    icon: AlertOctagon,
    iconColor: "text-rose-600",
    label: "Urgent",
    labelBg: "bg-rose-600 text-white",
  },
  warning: {
    border: "border-amber-300",
    bg: "bg-amber-50/60",
    headerBg: "bg-amber-100",
    titleColor: "text-amber-900",
    icon: AlertTriangle,
    iconColor: "text-amber-600",
    label: "Warning",
    labelBg: "bg-amber-500 text-white",
  },
  watch: {
    border: "border-sky-300",
    bg: "bg-sky-50/60",
    headerBg: "bg-sky-100",
    titleColor: "text-sky-900",
    icon: Eye,
    iconColor: "text-sky-600",
    label: "Watch",
    labelBg: "bg-sky-600 text-white",
  },
  info: {
    border: "border-stone-300",
    bg: "bg-stone-50/60",
    headerBg: "bg-stone-100",
    titleColor: "text-stone-900",
    icon: Info,
    iconColor: "text-stone-500",
    label: "FYI",
    labelBg: "bg-stone-600 text-white",
  },
};

function PlanCallouts({ alerts, audience = "all", title, defaultCollapsed = false }) {
  const filtered =
    audience === "all"
      ? alerts
      : alerts.filter(
          (a) => a.audience === audience || a.audience === "both",
        );

  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  if (!filtered || filtered.length === 0) return null;

  const counts = filtered.reduce(
    (acc, a) => ({ ...acc, [a.severity]: (acc[a.severity] || 0) + 1 }),
    {},
  );
  const urgentCount = counts.urgent || 0;
  const warningCount = counts.warning || 0;

  return (
    <section className="mb-6 bg-white border border-stone-300 rounded-sm">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="w-full px-4 py-2.5 border-b border-stone-200 flex items-center justify-between hover:bg-stone-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Bell size={16} className="text-stone-700" />
          <div className="serif text-sm font-bold text-stone-900">
            {title ||
              (audience === "client"
                ? "Items to Discuss"
                : "Plan Callouts")}
          </div>
          <div className="sans text-[11px] text-stone-500 ml-1">
            {filtered.length} item{filtered.length === 1 ? "" : "s"}
            {urgentCount > 0 && (
              <span className="ml-2 px-1.5 py-0.5 rounded-sm bg-rose-600 text-white font-bold text-[9px] uppercase">
                {urgentCount} urgent
              </span>
            )}
            {warningCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-amber-500 text-white font-bold text-[9px] uppercase">
                {warningCount} warning
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 sans text-xs text-stone-500 font-semibold">
          {collapsed ? "Show" : "Hide"}
          <ChevronDown
            size={14}
            className={`transition-transform ${collapsed ? "" : "rotate-180"}`}
          />
        </div>
      </button>
      {!collapsed && (
        <div className="divide-y divide-stone-100">
          {filtered.map((a) => {
            const cfg = ALERT_SEV_CONFIG[a.severity] || ALERT_SEV_CONFIG.info;
            const Icon = cfg.icon;
            return (
              <div
                key={a.id}
                className={`flex items-start gap-3 px-4 py-3 ${cfg.bg} border-l-4 ${cfg.border}`}
              >
                <Icon size={18} className={`${cfg.iconColor} mt-0.5 shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span
                      className={`sans text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm ${cfg.labelBg}`}
                    >
                      {cfg.label}
                    </span>
                    <span
                      className={`sans text-sm font-bold ${cfg.titleColor}`}
                    >
                      {a.title}
                    </span>
                  </div>
                  <div className="sans text-xs text-stone-700 leading-snug">
                    {a.detail}
                  </div>
                  {a.action && audience !== "client" && (
                    <div className="sans text-xs text-stone-600 italic mt-1.5 leading-snug">
                      <strong className="not-italic font-semibold text-stone-800">
                        Next step:
                      </strong>{" "}
                      {a.action}
                    </div>
                  )}
                </div>
                {audience === "all" && (
                  <div className="sans text-[9px] uppercase tracking-widest font-bold text-stone-400 shrink-0 mt-0.5">
                    {a.audience === "both"
                      ? "Both"
                      : a.audience === "planner"
                        ? "For You"
                        : "For Client"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Scenario Comparison — Baseline + N independent scenarios, each showing
// Yes-only AND Yes+Maybe outcomes side by side.
// ════════════════════════════════════════════════════════════════════════
function ScenarioComparison({ result, plan, onUpdatePlan }) {
  if (!result) return null;
  const scenarios = result.scenarios || [];
  const baselineTax = result.baseline.totalTax;
  // Don't show if literally nothing's applied
  const anyApplied = scenarios.some(
    (s) => s.yesCount + s.maybeCount > 0,
  );
  if (!anyApplied) return null;

  const renameScenario = (scenarioId, name) => {
    const next = (plan.scenarios || []).map((sc) =>
      sc.id === scenarioId ? { ...sc, name } : sc,
    );
    onUpdatePlan({ ...plan, scenarios: next });
  };

  return (
    <section className="mb-6 bg-white border border-stone-300 rounded-sm">
      <div className="px-4 py-2.5 border-b border-stone-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers size={16} className="text-stone-700" />
          <div className="serif text-sm font-bold text-stone-900">
            Scenario Comparison
          </div>
        </div>
        <div className="sans text-[11px] text-stone-500">
          Each strategy carries 3 chips — <strong>S1 S2 S3</strong> — that
          cycle <strong className="text-emerald-700">Y</strong> /{" "}
          <strong className="text-amber-700">M</strong> /{" "}
          <strong className="text-stone-500">—</strong>. Click any scenario
          name to rename it.
        </div>
      </div>
      {/* Baseline row */}
      <div className="px-4 py-3 border-b border-stone-200 bg-rose-50/30 flex items-center justify-between">
        <div>
          <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500">
            Baseline · No Action Taken
          </div>
          <div className="sans text-[11px] text-stone-500">
            What the client would pay with no planning
          </div>
        </div>
        <div className="mono num-tabular text-2xl font-bold text-rose-700">
          ${fmt(baselineTax)}
        </div>
      </div>
      <div
        className={`grid divide-x divide-stone-200`}
        style={{
          gridTemplateColumns: `repeat(${Math.max(scenarios.length, 1)}, minmax(0, 1fr))`,
        }}
      >
        {scenarios.map((sc, idx) => {
          const fullPct =
            baselineTax > 0 ? (sc.fullSavings / baselineTax) * 100 : 0;
          const yesPct =
            baselineTax > 0 ? (sc.yesOnlySavings / baselineTax) * 100 : 0;
          return (
            <div key={sc.id} className="px-4 py-4 bg-white">
              {/* Header row */}
              <div className="flex items-center justify-between mb-3">
                <input
                  type="text"
                  value={sc.name}
                  onChange={(e) => renameScenario(sc.id, e.target.value)}
                  className="sans text-[11px] uppercase tracking-widest font-bold text-stone-700 bg-transparent border-b border-transparent hover:border-stone-300 focus:border-sky-400 focus:outline-none px-0.5 -ml-0.5 w-full"
                  title="Click to rename"
                />
                <div className="sans text-[10px] text-stone-400 shrink-0 ml-2">
                  S{idx + 1}
                </div>
              </div>
              <div className="sans text-[10px] text-stone-500 mb-3 flex items-center gap-2">
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                  {sc.yesCount} Yes
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  {sc.maybeCount} Maybe
                </span>
              </div>
              {/* Yes-only block */}
              <div className="mb-3 pb-3 border-b border-stone-200">
                <div className="sans text-[9px] uppercase tracking-widest font-bold text-sky-700 mb-1.5">
                  Yes only
                </div>
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="sans text-[10px] uppercase tracking-wider font-bold text-emerald-700 shrink-0">
                    Tax saved
                  </span>
                  <span className="mono num-tabular text-2xl font-bold text-emerald-700 leading-none">
                    ${fmt(sc.yesOnlySavings)}{" "}
                    <span className="text-xs font-semibold text-emerald-700/70">
                      ({yesPct.toFixed(1)}%)
                    </span>
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="sans text-[10px] uppercase tracking-wider font-semibold text-rose-700 shrink-0">
                    Tax owed
                  </span>
                  <span className="mono num-tabular text-sm font-semibold text-rose-700">
                    ${fmt(sc.yesOnlyTax)}
                  </span>
                </div>
              </div>
              {/* Yes + Maybe block */}
              <div>
                <div className="sans text-[9px] uppercase tracking-widest font-bold text-emerald-800 mb-1.5">
                  Yes + Maybe
                </div>
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="sans text-[10px] uppercase tracking-wider font-bold text-emerald-700 shrink-0">
                    Tax saved
                  </span>
                  <span className="mono num-tabular text-2xl font-bold text-emerald-700 leading-none">
                    ${fmt(sc.fullSavings)}{" "}
                    <span className="text-xs font-semibold text-emerald-700/70">
                      ({fullPct.toFixed(1)}%)
                    </span>
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="sans text-[10px] uppercase tracking-wider font-semibold text-rose-700 shrink-0">
                    Tax owed
                  </span>
                  <span className="mono num-tabular text-sm font-semibold text-rose-700">
                    ${fmt(sc.fullTax)}
                  </span>
                </div>
                {sc.maybeSavings > 0 && (
                  <div className="sans text-[10px] text-stone-500 mt-1 text-right">
                    M adds ${fmt(sc.maybeSavings)} on top of Yes
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-4 py-2 border-t border-stone-200 bg-stone-50/50 sans text-[11px] text-stone-600 leading-relaxed">
        Each scenario is independent — a strategy can be Yes in one scenario,
        Maybe in another, and excluded from a third. Use scenarios to model
        conservative / balanced / aggressive pitches for the same client.
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Strategy Row (inline, within an entity or 1040 card)
// ════════════════════════════════════════════════════════════════════════
// Resolve a strategy's status in a given scenario. Falls back to legacy
// single `status` field when statusByScenario isn't populated.
function getStatusForScenario(stratRef, scenarioId) {
  const sbS = stratRef.statusByScenario;
  if (sbS && sbS[scenarioId]) return sbS[scenarioId];
  if (
    stratRef.status === "yes" ||
    stratRef.status === "committed" ||
    stratRef.status === "implemented"
  )
    return "yes";
  return "maybe";
}

// Cycle a scenario status through maybe → yes → out → maybe.
function nextScenarioStatus(current) {
  if (current === "maybe") return "yes";
  if (current === "yes") return "out";
  return "maybe";
}

function StrategyRow({
  stratRef,
  hostType,
  scenarios = [],
  combinedMarginalRate = 0,
  marginalSavings = null,
  onUpdate,
  onRemove,
  onOpenDoc,
  compact = false,
}) {
  const strat = getStrategy(stratRef.strategyId);
  if (!strat) {
    return (
      <div className="flex items-center justify-between bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm sans text-sm text-rose-700">
        <span>Unknown strategy: {stratRef.strategyId}</span>
        <button onClick={onRemove} className="text-rose-600 hover:text-rose-800">
          <X size={14} />
        </button>
      </div>
    );
  }
  const docAvailable = hasStrategyDoc(strat);

  const goalLabels = strat.goals
    .map((g) => GOALS.find((x) => x.id === g)?.label.split(" ")[0])
    .join(" · ");

  // Marginal contribution from compute pipeline takes precedence over the
  // simple reduction × marginal-rate estimate. Marginal contribution is the
  // savings the strategy actually adds at the margin given everything else
  // is already applied — these correctly sum to the plan-level total.
  const reduction = strategyReduction(stratRef);
  const credit = strategyCredit(stratRef);
  const manual = strategyManualSavings(stratRef);
  const computedFromReduction = reduction * combinedMarginalRate;
  const useMarginal = marginalSavings != null;
  const savingsEstimate = useMarginal
    ? marginalSavings
    : manual > 0
      ? manual
      : credit > 0
        ? credit
        : computedFromReduction;
  const savingsLabel = useMarginal
    ? "Marginal contribution"
    : manual > 0
      ? "Manual override"
      : credit > 0
        ? "Direct credit"
        : `${fmt(reduction)} × ${fmtPct(combinedMarginalRate, 1)}`;

  // Per-scenario status. Each scenario gets a chip showing Y / M / —.
  const scenarioList =
    Array.isArray(scenarios) && scenarios.length > 0
      ? scenarios
      : [{ id: "s1", name: "Scenario 1" }];

  const setScenarioStatus = (scenarioId, nextStatus) => {
    const current = stratRef.statusByScenario || {};
    // Seed missing scenarios from legacy status so we never lose existing intent
    const seeded = scenarioList.reduce((acc, sc) => {
      acc[sc.id] = current[sc.id] || getStatusForScenario(stratRef, sc.id);
      return acc;
    }, {});
    seeded[scenarioId] = nextStatus;
    onUpdate({ statusByScenario: seeded });
  };

  const STATUS_CHIP_STYLES = {
    yes: "bg-emerald-600 text-white hover:bg-emerald-700",
    maybe: "bg-amber-500 text-white hover:bg-amber-600",
    out: "bg-stone-200 text-stone-500 hover:bg-stone-300",
  };
  const STATUS_CHIP_LABELS = { yes: "Y", maybe: "M", out: "—" };
  const STATUS_CHIP_LONG = {
    yes: "Yes (committed)",
    maybe: "Maybe (considering)",
    out: "Excluded from this scenario",
  };

  return (
    <div className="border border-stone-200 rounded-sm bg-stone-50/50">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-stone-200">
        <button
          onClick={() => onUpdate({ applied: !stratRef.applied })}
          className={`w-5 h-5 rounded-sm flex items-center justify-center transition-colors ${
            stratRef.applied
              ? "bg-emerald-500 text-white"
              : "bg-white border border-stone-300 hover:border-stone-400"
          }`}
        >
          {stratRef.applied && <Check size={12} strokeWidth={3} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="sans text-sm font-semibold text-stone-900 truncate">{strat.name}</div>
          <div className="sans text-[11px] text-stone-500">
            {goalLabels} {strat.citation && <span>· {strat.citation}</span>}
          </div>
        </div>
        {stratRef.applied && (
          <div className="flex items-center gap-1 shrink-0">
            {scenarioList.map((sc, idx) => {
              const st = getStatusForScenario(stratRef, sc.id);
              return (
                <button
                  key={sc.id}
                  onClick={() =>
                    setScenarioStatus(sc.id, nextScenarioStatus(st))
                  }
                  className={`w-10 flex flex-col items-center justify-center rounded-sm transition-colors py-0.5 ${STATUS_CHIP_STYLES[st]}`}
                  title={`${sc.name}: ${STATUS_CHIP_LONG[st]} (click to cycle)`}
                >
                  <div className="sans text-[8px] uppercase tracking-wider font-bold leading-none opacity-80">
                    S{idx + 1}
                  </div>
                  <div className="sans text-xs font-bold leading-tight">
                    {STATUS_CHIP_LABELS[st]}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {stratRef.applied && savingsEstimate > 0 && (
          <div className="text-right shrink-0">
            <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500">
              Est. Saved
            </div>
            <div className="mono num-tabular text-base font-bold text-emerald-700">
              ${fmt(savingsEstimate)}
            </div>
            <div className="sans text-[10px] text-stone-500">{savingsLabel}</div>
          </div>
        )}
        {docAvailable && onOpenDoc && (
          <button
            onClick={() => onOpenDoc(strat)}
            className="flex items-center gap-1 px-2 py-1 sans text-[11px] font-semibold text-sky-700 hover:text-stone-900 border border-sky-300 hover:border-sky-500 rounded-sm bg-sky-50/40 hover:bg-sky-50"
            title="Open client-facing strategy brief — preview, download, or email"
          >
            <BookOpen size={12} />
            Strategy Brief
          </button>
        )}
        <button onClick={onRemove} className="text-stone-400 hover:text-rose-600 p-1">
          <X size={14} />
        </button>
      </div>

      {stratRef.applied && !compact && (
        <div className="px-3 py-2 grid grid-cols-12 gap-2">
          {strat.calcType === "reduceTaxableIncome" && strat.id !== "augusta_rule" && (
            <div className="col-span-4">
              <Field label="Reduction to taxable income">
                <MoneyInput
                  value={stratRef.inputs?.amount || 0}
                  onChange={(v) =>
                    onUpdate({ inputs: { ...stratRef.inputs, amount: v } })
                  }
                />
              </Field>
            </div>
          )}
          {strat.id === "augusta_rule" && (
            <>
              <div className="col-span-3">
                <Field label="Days rented (≤14)">
                  <NumberInput
                    value={stratRef.inputs?.days || 0}
                    onChange={(v) =>
                      onUpdate({ inputs: { ...stratRef.inputs, days: Math.min(14, v) } })
                    }
                    suffix="days"
                  />
                </Field>
              </div>
              <div className="col-span-3">
                <Field label="FMV daily rate">
                  <MoneyInput
                    value={stratRef.inputs?.dailyRate || 0}
                    onChange={(v) =>
                      onUpdate({ inputs: { ...stratRef.inputs, dailyRate: v } })
                    }
                  />
                </Field>
              </div>
            </>
          )}
          {strat.calcType === "taxCredit" && (
            <div className="col-span-4">
              <Field label="Credit amount">
                <MoneyInput
                  value={stratRef.inputs?.amount || 0}
                  onChange={(v) =>
                    onUpdate({ inputs: { ...stratRef.inputs, amount: v } })
                  }
                />
              </Field>
            </div>
          )}
          {strat.calcType === "manual" && (
            <>
              <div className="col-span-4">
                <Field label="Manual reduction to TI">
                  <MoneyInput
                    value={stratRef.manualReduction || 0}
                    onChange={(v) => onUpdate({ manualReduction: v })}
                  />
                </Field>
              </div>
              <div className="col-span-4">
                <Field label="Manual tax-savings $">
                  <MoneyInput
                    value={stratRef.manualSavings || 0}
                    onChange={(v) => onUpdate({ manualSavings: v })}
                  />
                </Field>
              </div>
            </>
          )}
          <div className="col-span-3">
            <Field
              label="Implementation Cost"
              hint="One-time fee (study, attorney, election)"
            >
              <MoneyInput
                value={stratRef.implementationCost || 0}
                onChange={(v) => onUpdate({ implementationCost: v })}
              />
            </Field>
          </div>
          <div className="col-span-3">
            <Field
              label="Recurring Cost"
              hint="Annual ongoing cost (admin, plan fees)"
            >
              <MoneyInput
                value={stratRef.recurringCost || 0}
                onChange={(v) => onUpdate({ recurringCost: v })}
              />
            </Field>
          </div>
          <div className="col-span-2">
            <Field
              label="Years of Savings"
              hint="1 = Y1 only · 3 = annual"
            >
              <Select
                value={stratRef.yearsActive ?? 3}
                onChange={(v) => onUpdate({ yearsActive: parseInt(v, 10) })}
                options={[
                  { value: 1, label: "1 year (Y1 only)" },
                  { value: 2, label: "2 years" },
                  { value: 3, label: "3 years (annual)" },
                ]}
              />
            </Field>
          </div>
          <div className="col-span-12">
            <Field label="Notes">
              <TextInput
                value={stratRef.notes || ""}
                onChange={(v) => onUpdate({ notes: v })}
                placeholder="Implementation notes, deadlines, who's responsible..."
              />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Individual (1040) Card
// ════════════════════════════════════════════════════════════════════════
function IndividualCard({
  plan,
  planId,
  scenarios,
  result,
  baselineResult,
  combinedMarginalRate,
  marginalById,
  onUpdate,
  onAddStrategy,
  onUpdateStrategy,
  onRemoveStrategy,
  onOpenStrategyDoc,
}) {
  const ind = plan.individual || {};
  const fed = result.federal;
  const [collapsedStrategies, setCollapsedStrategies] = useState(false);
  return (
    <div className="bg-white border border-stone-300 rounded-sm">
      <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-3">
        <User size={18} className="text-sky-500" />
        <div className="flex-1 min-w-0">
          <div className="serif text-xl font-bold text-stone-900 truncate">
            {ind.ownerNames || plan.clientName || "Individual"}
          </div>
          <div className="sans text-xs text-stone-500">
            Filing status drives federal brackets, NIIT & AMT thresholds, and
            SE tax
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label className="sans text-[10px] uppercase tracking-widest font-bold text-stone-500">
            Filing status
          </label>
          <Select
            value={ind.filingStatus || "mfj"}
            onChange={(v) => onUpdate({ filingStatus: v })}
            options={FILING_STATUSES.map((f) => ({ id: f.id, label: f.label }))}
            className="!w-52"
          />
        </div>
      </div>

      <div className="px-5 py-4 grid grid-cols-12 gap-4">
        {/* Personal income inputs */}
        <div className="col-span-7 grid grid-cols-3 gap-3">
          <div className="col-span-3">
            <Field label="Owner Names (e.g., John & Jane Smith)">
              <TextInput
                value={ind.ownerNames || ""}
                onChange={(v) => onUpdate({ ownerNames: v })}
                placeholder="Owner name(s)"
              />
            </Field>
          </div>
          <Field label="W-2 Wages">
            <MoneyInput value={ind.wages} onChange={(v) => onUpdate({ wages: v })} />
          </Field>
          <Field label="Interest">
            <MoneyInput value={ind.interest} onChange={(v) => onUpdate({ interest: v })} />
          </Field>
          <Field label="Ordinary Dividends">
            <MoneyInput
              value={ind.ordinaryDividends}
              onChange={(v) => onUpdate({ ordinaryDividends: v })}
            />
          </Field>
          <Field label="Qualified Dividends">
            <MoneyInput
              value={ind.qualifiedDividends}
              onChange={(v) => onUpdate({ qualifiedDividends: v })}
            />
          </Field>
          <Field label="Short-Term Cap Gains">
            <MoneyInput
              value={ind.shortTermGains}
              onChange={(v) => onUpdate({ shortTermGains: v })}
            />
          </Field>
          <Field label="Long-Term Cap Gains">
            <MoneyInput
              value={ind.longTermGains}
              onChange={(v) => onUpdate({ longTermGains: v })}
            />
          </Field>
          <div className="col-span-3">
            <OtherLineItemsField
              label="Other Income"
              items={ind.otherIncomeItems}
              legacyAmount={ind.otherIncome}
              legacyNote={ind.otherIncomeNote}
              signHelperPlus="Adds to income (increases tax)"
              signHelperMinus="Reduces income (decreases tax)"
              emptyPrompt="No 'Other Income' yet — add line items like royalty, settlement, gambling, hobby."
              onChange={(items, total) =>
                onUpdate({
                  otherIncomeItems: items,
                  otherIncome: total,
                })
              }
            />
          </div>
          <Field label="LTCG Excluded from NIIT" hint="Active T/B sale gain, etc.">
            <MoneyInput
              value={ind.ltcgExcludedFromNIIT || 0}
              onChange={(v) => onUpdate({ ltcgExcludedFromNIIT: v })}
            />
          </Field>
          <Field label="QBI Deduction Override" hint="Leave blank for auto; partner-controlled when W-2/UBIA limits apply">
            <MoneyInput
              value={ind.qbiOverride || 0}
              onChange={(v) => onUpdate({ qbiOverride: v })}
            />
          </Field>
          <Field
            label="AMT Adjustments & Preferences"
            hint={
              ind.amtPreferencesNote
                ? ind.amtPreferencesNote
                : "Net add-back to AMTI (ISO bargain, PAB interest, depr adj, AMT NOL, etc.)"
            }
          >
            <MoneyInput
              value={ind.amtPreferences || 0}
              onChange={(v) => onUpdate({ amtPreferences: v })}
            />
          </Field>
          <Field label="Describe AMT Adjustment">
            <TextInput
              value={ind.amtPreferencesNote || ""}
              onChange={(v) => onUpdate({ amtPreferencesNote: v })}
              placeholder="e.g. $85K ISO bargain element from Q3 exercise"
            />
          </Field>
          <Field
            label="Qualifying Children (under 17)"
            hint="Each = $2K CTC; phases out at $400K MFJ / $200K other"
          >
            <NumberInput
              value={ind.qualifyingChildren || 0}
              onChange={(v) => onUpdate({ qualifyingChildren: v })}
              placeholder="0"
            />
          </Field>
          <Field
            label="Other Dependents"
            hint="Each = $500 CFOD (older kids, parents, etc.); same phase-out"
          >
            <NumberInput
              value={ind.otherDependents || 0}
              onChange={(v) => onUpdate({ otherDependents: v })}
              placeholder="0"
            />
          </Field>
          <Field label="Above-Line Adjustments">
            <MoneyInput
              value={ind.adjustments}
              onChange={(v) => onUpdate({ adjustments: v })}
            />
          </Field>
          <Field
            label="SE Earner's Own W-2 Wages"
            hint="For SE-tax SS cap coordination (Sched SE Line 8a). Leave 0 to skip."
          >
            <MoneyInput
              value={ind.seEarnerWages || 0}
              onChange={(v) => onUpdate({ seEarnerWages: v })}
            />
          </Field>
          <Field label="Itemized Deductions Total (auto if items listed below)">
            <MoneyInput
              value={ind.itemizedDeductions}
              onChange={(v) => onUpdate({ itemizedDeductions: v })}
            />
          </Field>
          <div className="col-span-3">
            <ItemizedDeductionsField
              items={ind.itemizedDeductionsItems}
              legacyAmount={ind.itemizedDeductions}
              onChange={(items, total) =>
                onUpdate({
                  itemizedDeductionsItems: items,
                  itemizedDeductions: total,
                })
              }
            />
          </div>

          {/* Safe-harbor / estimated tax inputs — drives the quarterly schedule on the deliverable */}
          <div className="col-span-3 mt-2 pt-2 border-t border-stone-200">
            <div className="sans text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">
              Safe Harbor / Estimated Tax Inputs
            </div>
          </div>
          <Field
            label="Prior-Year Total Tax"
            hint="Line 24 of last year's 1040 — drives §6654 safe harbor"
          >
            <MoneyInput
              value={ind.priorYearTotalTax || 0}
              onChange={(v) => onUpdate({ priorYearTotalTax: v })}
            />
          </Field>
          <Field
            label="Prior-Year AGI"
            hint="If > $150K, the safe harbor is 110% (not 100%) of prior tax"
          >
            <MoneyInput
              value={ind.priorYearAGI || 0}
              onChange={(v) => onUpdate({ priorYearAGI: v })}
            />
          </Field>
          <Field
            label="Expected Withholding (W-2/1099)"
            hint="Counts against the safe-harbor target"
          >
            <MoneyInput
              value={ind.expectedWithholding || 0}
              onChange={(v) => onUpdate({ expectedWithholding: v })}
            />
          </Field>
          <Field
            label="Estimated Payments Already Made"
            hint="Running total of Q1-Q4 sent so far"
          >
            <MoneyInput
              value={ind.estimatedPaymentsMade || 0}
              onChange={(v) => onUpdate({ estimatedPaymentsMade: v })}
            />
          </Field>
        </div>

        {/* Computed 1040 */}
        <div className="col-span-5 bg-stone-900 text-stone-100 rounded-sm p-4">
          <div className="sans text-[10px] uppercase tracking-widest font-semibold text-sky-300 mb-3">
            1040 Forecast — TY {result.year}
          </div>

          {/* Per-entity K-1 detail */}
          {result.entities && result.entities.length > 0 && (
            <div className="mb-3 pb-2 border-b border-stone-700">
              <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-400 mb-1.5">
                K-1s Flowing In
              </div>
              {result.entities.filter((e) => e.isPassthrough).length > 0 && (
                <div className="grid grid-cols-12 gap-1 sans text-[9px] uppercase tracking-wider font-semibold text-stone-500 mb-1 pb-1 border-b border-stone-800">
                  <div className="col-span-5">Entity</div>
                  <div className="col-span-3 text-right">Ordinary</div>
                  <div className="col-span-2 text-right">LTCG</div>
                  <div className="col-span-2 text-right">Credits</div>
                </div>
              )}
              {result.entities
                .filter((e) => e.isPassthrough)
                .map((e, i) => (
                  <K1DetailRow key={i} entityResult={e} />
                ))}
              {result.entities.filter((e) => e.isPassthrough).length === 0 && (
                <div className="sans text-[11px] text-stone-500 italic">
                  No pass-through entities yet.
                </div>
              )}
              {/* Totals row */}
              {result.entities.filter((e) => e.isPassthrough).length > 1 && (
                <div className="mt-1 pt-1 border-t border-stone-800">
                  <div className="grid grid-cols-12 gap-1 sans text-[10px]">
                    <div className="col-span-5 text-stone-400 uppercase tracking-wider font-semibold">
                      Total
                    </div>
                    <div className="col-span-3 text-right mono num-tabular text-sky-400 font-semibold">
                      ${fmt(result.k1OrdinaryTotal)}
                    </div>
                    <div className="col-span-2 text-right mono num-tabular text-sky-400 font-semibold">
                      ${fmt(result.k1LTCGTotal || 0)}
                    </div>
                    <div className="col-span-2 text-right mono num-tabular text-emerald-400 font-semibold">
                      ${fmt(result.k1CreditsTotal || 0)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Income summary */}
          <Flow1040
            label="K-1 ordinary income"
            value={result.k1OrdinaryTotal}
          />
          {(result.k1LTCGTotal || 0) !== 0 && (
            <Flow1040 label="K-1 long-term cap gains" value={result.k1LTCGTotal} />
          )}
          {(result.k1STCGTotal || 0) !== 0 && (
            <Flow1040 label="K-1 short-term cap gains" value={result.k1STCGTotal} />
          )}
          {/* K-1 credits — show each named credit with its source entity */}
          {(result.k1CreditItems || []).length > 0 && (
            <div className="my-2 pl-3 border-l-2 border-emerald-700/40">
              <div className="sans text-[9px] uppercase tracking-widest font-semibold text-emerald-300/80 mb-1">
                K-1 Federal Credits Pass-Through
              </div>
              {result.k1CreditItems.map((c, i) => (
                <div
                  key={`${c.id}-${i}`}
                  className="flex items-baseline justify-between sans text-[11px] py-0.5"
                >
                  <span className="text-stone-300">
                    {c.name}{" "}
                    <span className="text-stone-500">
                      ({c.sourceEntityName})
                    </span>
                  </span>
                  <span className="mono num-tabular text-emerald-300">
                    ${fmt(c.ownerAmount)}
                  </span>
                </div>
              ))}
              {(result.k1CreditItems || []).length > 1 && (
                <div className="flex items-baseline justify-between sans text-[10px] py-0.5 mt-1 pt-1 border-t border-emerald-700/30 font-semibold">
                  <span className="text-emerald-200">K-1 credits total</span>
                  <span className="mono num-tabular text-emerald-200">
                    ${fmt(result.k1CreditsTotal || 0)}
                  </span>
                </div>
              )}
            </div>
          )}
          <Flow1040
            label="W-2 wages"
            value={+ind.wages || 0}
          />
          {((+ind.interest || 0) +
            (+ind.ordinaryDividends || 0) +
            (+ind.qualifiedDividends || 0) +
            (+ind.otherIncome || 0)) !== 0 && (
            <Flow1040
              label="Interest + dividends + other"
              value={
                (+ind.interest || 0) +
                (+ind.ordinaryDividends || 0) +
                (+ind.qualifiedDividends || 0) +
                (+ind.otherIncome || 0)
              }
            />
          )}
          {((+ind.shortTermGains || 0) + (+ind.longTermGains || 0)) !== 0 && (
            <Flow1040
              label="Personal capital gains"
              value={(+ind.shortTermGains || 0) + (+ind.longTermGains || 0)}
            />
          )}
          <div className="border-t border-stone-700 my-2" />
          <Flow1040 label="AGI" value={fed.agi} bold />
          <Flow1040 label={fed.usedStandard ? "Standard deduction" : "Itemized deduction"} value={-fed.deduction} />
          {fed.qbiDeduction > 0 && (
            <Flow1040 label="QBI deduction (§199A)" value={-fed.qbiDeduction} />
          )}
          <Flow1040 label="Taxable income" value={fed.taxableIncome} bold />
          <div className="border-t border-stone-700 my-2" />
          <Flow1040 label="Federal income tax" value={fed.incomeTax} />
          {fed.ctcApplied > 0 && (
            <Flow1040
              label="Child Tax Credit / CFOD (§24)"
              value={-fed.ctcApplied}
            />
          )}
          {fed.amtTax > 0 && (
            <Flow1040 label="AMT (§55)" value={fed.amtTax} />
          )}
          {fed.niitTax > 0 && <Flow1040 label="NIIT (3.8%)" value={fed.niitTax} />}
          {fed.seTax > 0 && <Flow1040 label="SE tax" value={fed.seTax} />}
          {(result.stateAdjustmentsTotal || 0) !== 0 && (
            <Flow1040
              label="State adjustment (addback / decoupling)"
              value={result.stateAdjustmentsTotal}
            />
          )}
          {result.stateTax.multiState ? (
            <>
              <Flow1040
                label={`State tax — total (${
                  Object.keys(result.stateTax.byState || {}).filter(
                    (s) => result.stateTax.byState[s] > 0,
                  ).length
                } states)`}
                value={result.stateTax.totalTax}
              />
              {Object.entries(result.stateTax.byState || {})
                .filter(([, v]) => v > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([st, tax]) => (
                  <div
                    key={st}
                    className="flex items-center justify-between sans text-[11px] text-stone-400 pl-3 -mt-0.5"
                  >
                    <span>↳ {st}</span>
                    <span className="mono num-tabular">${fmt(tax)}</span>
                  </div>
                ))}
            </>
          ) : (
            <Flow1040
              label={`State tax (${result.state})`}
              value={result.stateTax.totalTax}
            />
          )}
          {result.creditsApplied > 0 && (
            <Flow1040 label="Credits applied" value={-result.creditsApplied} highlight="emerald" />
          )}
          <div className="border-t border-stone-700 my-2" />
          <Flow1040 label="Total tax" value={result.totalTax} bold huge />
          <div className="sans text-[11px] text-stone-400 mt-2">
            Marginal {fmtPct(fed.marginalRate, 0)} · Effective {fmtPct(fed.effectiveRate)}
          </div>
        </div>
      </div>

      {/* Carryforwards (NOLs + capital loss + unused credits) */}
      <IndividualCarryforwardsSection ind={ind} onUpdate={onUpdate} />

      {/* Notes & file attachments for the personal return */}
      <NotesAndDocsSection
        host={ind}
        hostKey="individual"
        planId={planId}
        onUpdate={onUpdate}
      />

      {/* Personal-level strategies */}
      <div className="px-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500">
              Personal-Level Strategies (1040)
            </div>
            {(ind.strategies || []).length > 0 && (
              <button
                onClick={() => setCollapsedStrategies(!collapsedStrategies)}
                className="flex items-center gap-1 text-stone-500 hover:text-sky-600 sans text-[10px] font-semibold uppercase tracking-wider"
                title={
                  collapsedStrategies
                    ? "Expand all strategies"
                    : "Collapse all strategies for a cleaner view"
                }
              >
                {collapsedStrategies ? (
                  <>
                    <ChevronDown size={12} />
                    Expand all ({(ind.strategies || []).length})
                  </>
                ) : (
                  <>
                    <ChevronUp size={12} />
                    Collapse all
                  </>
                )}
              </button>
            )}
          </div>
          <button
            onClick={onAddStrategy}
            className="flex items-center gap-1 text-sky-600 hover:text-sky-700 sans text-xs font-semibold"
          >
            <Plus size={14} />
            Add Strategy
          </button>
        </div>
        {(ind.strategies || []).length === 0 ? (
          <div className="text-stone-400 sans text-xs italic py-2">
            No personal strategies. Roth conversions, LTCG harvesting, charitable bunching, etc. go
            here.
          </div>
        ) : (
          <div className="space-y-2">
            {(ind.strategies || []).map((stratRef, idx) => (
              <StrategyRow
                key={idx}
                stratRef={stratRef}
                hostType="individual"
                scenarios={scenarios}
                combinedMarginalRate={combinedMarginalRate}
                marginalSavings={marginalById ? (marginalById[stratRef.strategyId] ?? 0) : null}
                onUpdate={(patch) => onUpdateStrategy(idx, patch)}
                onRemove={() => onRemoveStrategy(idx)}
                onOpenDoc={onOpenStrategyDoc}
                compact={collapsedStrategies}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// One row in the per-entity K-1 detail list at the top of the 1040 sidebar.
function K1DetailRow({ entityResult }) {
  const e = entityResult || {};
  const ordinary = e.ownerK1Ordinary || 0;
  const ltcg = e.ownerK1LTCG || 0;
  const credits = e.ownerK1Credits || 0;
  const stcg = e.ownerK1STCG || 0;
  const isLoss = ordinary < 0;
  const typeShort =
    {
      "1120S": "S-Corp",
      "1065": "Partnership",
      "1120": "C-Corp",
      sched_c: "Sole Prop",
    }[e.type] || e.type;

  return (
    <div className="mb-1.5 last:mb-0">
      <div className="grid grid-cols-12 gap-1 items-baseline sans text-[11px]">
        <div className="col-span-5 truncate text-stone-200 font-semibold">
          {e.name || "Unnamed"}
          <span className="text-stone-500 font-normal">
            {" "}
            · {typeShort} · {fmtPct(e.ownership || 0, 0)}
          </span>
        </div>
        <div
          className={`col-span-3 text-right mono num-tabular ${
            isLoss ? "text-rose-400" : "text-stone-100"
          }`}
        >
          {ordinary < 0 ? "−" : ""}${fmt(Math.abs(ordinary))}
        </div>
        <div className="col-span-2 text-right mono num-tabular text-stone-300">
          {ltcg !== 0 ? `${ltcg < 0 ? "−" : ""}$${fmt(Math.abs(ltcg))}` : "—"}
        </div>
        <div className="col-span-2 text-right mono num-tabular text-emerald-400">
          {credits > 0 ? `$${fmt(credits)}` : "—"}
        </div>
      </div>
      {stcg !== 0 && (
        <div className="grid grid-cols-12 gap-1 sans text-[10px] text-stone-500 italic">
          <div className="col-span-5">↳ short-term cap gain</div>
          <div className="col-span-3 text-right mono num-tabular">
            {stcg < 0 ? "−" : ""}${fmt(Math.abs(stcg))}
          </div>
        </div>
      )}
    </div>
  );
}

function Flow1040({ label, value, bold = false, huge = false, highlight = null }) {
  const cls = highlight === "emerald" ? "text-emerald-400" : "text-sky-400";
  return (
    <div className="flex items-center justify-between text-sm py-0.5">
      <span className={`sans ${bold ? "font-semibold text-stone-100" : "text-stone-400"}`}>
        {label}
      </span>
      <span
        className={`mono num-tabular ${huge ? "text-2xl" : ""} ${
          bold ? `font-bold ${cls}` : "text-stone-100"
        }`}
      >
        {value < 0 ? "−" : ""}${fmt(Math.abs(value || 0))}
      </span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Impact Summary
// ════════════════════════════════════════════════════════════════════════
function ImpactSummary({ plan, result }) {
  const lines = [];
  for (const ent of plan.entities) {
    for (const s of ent.strategies || []) {
      if (!s.applied) continue;
      const strat = getStrategy(s.strategyId);
      if (!strat) continue;
      lines.push({
        strategyName: strat.name,
        host: ent.name,
        hostType: "entity",
        citation: strat.citation,
      });
    }
  }
  for (const s of plan.individual?.strategies || []) {
    if (!s.applied) continue;
    const strat = getStrategy(s.strategyId);
    if (!strat) continue;
    lines.push({
      strategyName: strat.name,
      host: plan.individual.ownerNames || "Personal",
      hostType: "individual",
      citation: strat.citation,
    });
  }

  if (lines.length === 0) {
    return (
      <div className="bg-stone-50 border border-dashed border-stone-300 rounded-sm p-6 text-center">
        <div className="sans text-sm text-stone-600">
          No strategies applied yet. Toggle strategies on entities or the personal return to see
          their combined impact here.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone-300 rounded-sm">
      <div className="px-5 py-3 border-b border-stone-200 grid grid-cols-12 gap-3 sans text-[10px] uppercase tracking-widest font-semibold text-stone-500">
        <div className="col-span-5">Strategy</div>
        <div className="col-span-3">Applied To</div>
        <div className="col-span-2">Authority</div>
        <div className="col-span-2 text-right">Type</div>
      </div>
      {lines.map((l, i) => (
        <div
          key={i}
          className="px-5 py-2.5 grid grid-cols-12 gap-3 border-b border-stone-100 sans text-sm"
        >
          <div className="col-span-5 font-semibold text-stone-900">{l.strategyName}</div>
          <div className="col-span-3 text-stone-600">{l.host}</div>
          <div className="col-span-2 text-stone-500 mono text-xs">{l.citation || "—"}</div>
          <div className="col-span-2 text-right">
            <span
              className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm ${
                l.hostType === "entity"
                  ? "bg-sky-100 text-sky-800"
                  : "bg-stone-100 text-stone-700"
              }`}
            >
              {l.hostType === "entity" ? "Entity" : "Personal"}
            </span>
          </div>
        </div>
      ))}
      <div className="px-5 py-3 bg-stone-900 text-stone-100 grid grid-cols-12 gap-3 sans text-sm">
        <div className="col-span-7 font-semibold">
          Total combined tax savings (federal + state)
        </div>
        <div className="col-span-5 text-right serif text-2xl font-bold text-sky-400 mono num-tabular">
          ${fmt(result.savings)}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Lifecycle Bar
// ════════════════════════════════════════════════════════════════════════
function LifecycleBar({ stage, onChange }) {
  return (
    <div className="bg-white border border-stone-300 rounded-sm p-3 flex items-stretch gap-0">
      {LIFECYCLE_STAGES.map((s, i) => (
        <button
          key={s.id}
          onClick={() => onChange(s.id)}
          className={`flex-1 px-2 py-2 sans text-xs font-semibold transition-colors ${
            stage === s.id
              ? "bg-stone-900 text-sky-400"
              : LIFECYCLE_STAGES.findIndex((x) => x.id === stage) > i
                ? "bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                : "bg-stone-50 text-stone-500 hover:bg-stone-100"
          } ${i > 0 ? "ml-1" : ""}`}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Implementation Timeline — sorted list of strategy deadlines
// ════════════════════════════════════════════════════════════════════════
// Normalize legacy 3-status data to current 2-status Yes/Maybe
function normalizeStratStatus(raw) {
  return raw === "yes" || raw === "committed" || raw === "implemented"
    ? "yes"
    : "maybe";
}

function gatherTimelineItems(plan) {
  const items = [];
  for (const ent of plan.entities || []) {
    for (const s of ent.strategies || []) {
      if (!s.applied) continue;
      const strat = getStrategy(s.strategyId);
      if (!strat) continue;
      const deadline = deadlineDateFor(strat, plan.taxYear, plan.state);
      items.push({
        strategy: strat,
        host: ent.name,
        hostType: "entity",
        notes: s.notes,
        deadline,
        kind: strat.timing?.kind,
        timingNote: strat.timing?.note,
        status: normalizeStratStatus(s.status),
        stratRef: s,
        implementationCost: parseFloat(s.implementationCost) || 0,
        recurringCost: parseFloat(s.recurringCost) || 0,
      });
    }
  }
  for (const s of plan.individual?.strategies || []) {
    if (!s.applied) continue;
    const strat = getStrategy(s.strategyId);
    if (!strat) continue;
    const deadline = deadlineDateFor(strat, plan.taxYear, plan.state);
    items.push({
      strategy: strat,
      host: plan.individual.ownerNames || "Personal",
      hostType: "individual",
      notes: s.notes,
      deadline,
      kind: strat.timing?.kind,
      timingNote: strat.timing?.note,
      status: normalizeStratStatus(s.status),
      stratRef: s,
      implementationCost: parseFloat(s.implementationCost) || 0,
      recurringCost: parseFloat(s.recurringCost) || 0,
    });
  }

  return items.sort((a, b) => {
    if (a.deadline && b.deadline) return a.deadline - b.deadline;
    if (a.deadline) return -1;
    if (b.deadline) return 1;
    return 0;
  });
}

function daysBetween(a, b) {
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

function ImplementationTimeline({ plan }) {
  const items = useMemo(() => gatherTimelineItems(plan), [plan]);
  const today = new Date();

  if (items.length === 0) {
    return (
      <div className="bg-stone-50 border border-dashed border-stone-300 rounded-sm p-6 text-center">
        <div className="sans text-sm text-stone-600">
          Apply strategies to entities or the personal return to build out the implementation
          timeline.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone-300 rounded-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-stone-200 grid grid-cols-12 gap-3 sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 bg-stone-50">
        <div className="col-span-4">Action Item</div>
        <div className="col-span-3">Applied To</div>
        <div className="col-span-3">Deadline</div>
        <div className="col-span-2 text-right">Status</div>
      </div>
      {items.map((item, i) => {
        let status = "Flexible";
        let statusColor = "bg-stone-100 text-stone-600";
        let dueText = item.timingNote || "—";
        if (item.deadline) {
          const days = daysBetween(today, item.deadline);
          dueText = item.deadline.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          if (days < 0) {
            status = "PAST DUE";
            statusColor = "bg-rose-100 text-rose-800";
          } else if (days <= 30) {
            status = `${days} days`;
            statusColor = "bg-rose-50 text-rose-700";
          } else if (days <= 90) {
            status = `${days} days`;
            statusColor = "bg-amber-50 text-amber-800";
          } else {
            status = `${days} days`;
            statusColor = "bg-emerald-50 text-emerald-800";
          }
        } else if (item.kind === "pre_transaction") {
          status = "Pre-Tx";
          statusColor = "bg-sky-100 text-sky-800";
        } else if (item.kind === "ongoing") {
          status = "Ongoing";
          statusColor = "bg-stone-100 text-stone-600";
        }

        return (
          <div
            key={i}
            className="px-5 py-3 grid grid-cols-12 gap-3 border-b border-stone-100 sans text-sm hover:bg-sky-50/30"
          >
            <div className="col-span-4">
              <div className="font-semibold text-stone-900">{item.strategy.name}</div>
              {item.strategy.citation && (
                <div className="sans text-[11px] text-stone-500 mono">
                  {item.strategy.citation}
                </div>
              )}
            </div>
            <div className="col-span-3 text-stone-700">
              {item.host}
              <span
                className={`ml-2 sans text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm ${
                  item.hostType === "entity"
                    ? "bg-sky-100 text-sky-800"
                    : "bg-stone-100 text-stone-700"
                }`}
              >
                {item.hostType === "entity" ? "Entity" : "Personal"}
              </span>
            </div>
            <div className="col-span-3">
              <div className="text-stone-900 font-semibold">{dueText}</div>
              {item.timingNote && item.deadline && (
                <div className="text-[11px] text-stone-500 leading-tight mt-0.5">
                  {item.timingNote}
                </div>
              )}
              {item.notes && (
                <div className="text-[11px] text-sky-700 italic leading-tight mt-0.5">
                  Note: {item.notes}
                </div>
              )}
            </div>
            <div className="col-span-2 text-right">
              <span
                className={`sans text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm ${statusColor}`}
              >
                {status}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// DeliverableSection — wraps each major section of the client deliverable.
// Two independent behaviors:
//   • Print: data-print-skip controls whether the section appears in the
//     printed/PDF output (set by the per-section checklist).
//   • On-screen: a collapse toggle hides the body for screen viewing only.
//     Print always renders the body regardless of collapse state (CSS in
//     index.css handles the screen/print split via data-collapsed).
// ════════════════════════════════════════════════════════════════════════
function DeliverableSection({
  id,
  label,
  collapsed,
  onToggle,
  printSkip,
  children,
}) {
  return (
    <div
      data-print-skip={printSkip}
      data-collapsed={collapsed ? "true" : undefined}
      className="deliverable-section relative"
    >
      {/* Collapsed-state header — screen-only thin bar that opens on click */}
      <button
        type="button"
        onClick={onToggle}
        className="deliverable-section-collapsed no-print w-full text-left px-4 py-2 mb-4 bg-stone-100 border border-stone-300 rounded-sm hover:bg-stone-200 flex items-center justify-between"
        title={`Expand ${label}`}
      >
        <span className="sans text-sm font-semibold text-stone-700">
          {label}
        </span>
        <ChevronDown size={16} className="text-stone-500" />
      </button>

      {/* Expanded-state collapse chevron — small floating button top-right */}
      <button
        type="button"
        onClick={onToggle}
        className="deliverable-section-chevron no-print absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center bg-white border border-stone-300 rounded-full shadow-sm hover:bg-stone-50 z-10"
        title={`Collapse ${label}`}
      >
        <ChevronUp size={14} className="text-stone-500" />
      </button>

      {/* Body — always in DOM. CSS hides on screen when collapsed; print
          always shows. */}
      <div className="deliverable-section-body">{children}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Client Deliverable — print-friendly summary modal
// ════════════════════════════════════════════════════════════════════════
function ClientDeliverable({ plan, result, combinedMarginalRate = 0, onClose }) {
  const items = useMemo(() => gatherTimelineItems(plan), [plan]);
  const today = new Date();

  // Compounding-value controls — planner sets these before showing/printing
  const [investRate, setInvestRate] = useState(0.07);
  const [investYears, setInvestYears] = useState(10);

  // Per-section print toggles. Unchecked sections get data-print-skip="true"
  // which the print CSS hides via display:none. The cover (letterhead + title
  // + hero + tier) is always printed.
  const PRINT_SECTIONS = [
    { id: "scenarios", label: "Scenario Comparison" },
    { id: "callouts", label: "Items to Discuss" },
    { id: "taxBill", label: "Where Your Tax Dollars Go" },
    { id: "brackets", label: "Where You Sit in the Federal Brackets" },
    { id: "strategies", label: "Strategies & Implementation Timeline" },
    { id: "income", label: "Income Calculation Detail" },
    { id: "roi", label: "Strategy ROI — Cost vs. Savings" },
    { id: "multiYear", label: "3-Year Plan Outlook" },
    { id: "estimatedTax", label: "Estimated Tax & Quarterly Payment Schedule" },
    { id: "actionItems", label: "Action Items & Deadlines" },
    { id: "compounding", label: "Compounding Value of These Savings" },
  ];
  const [printChecks, setPrintChecks] = useState(() =>
    PRINT_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), {}),
  );
  const setPrintCheck = (id, val) =>
    setPrintChecks((p) => ({ ...p, [id]: val }));
  const allChecked = PRINT_SECTIONS.every((s) => printChecks[s.id]);
  const noneChecked = PRINT_SECTIONS.every((s) => !printChecks[s.id]);
  const skip = (id) => (printChecks[id] ? undefined : "true");
  const [showPrintPicker, setShowPrintPicker] = useState(false);

  // On-screen collapse state per section — independent from print-skip.
  // Collapsing on screen still prints the section if its print checkbox is
  // checked; CSS hides the body for screen only.
  const [collapsedSections, setCollapsedSections] = useState({});
  const isCollapsed = (id) => !!collapsedSections[id];
  const toggleCollapse = (id) =>
    setCollapsedSections((p) => ({ ...p, [id]: !p[id] }));
  const collapseAll = () =>
    setCollapsedSections(
      PRINT_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), {}),
    );
  const expandAll = () => setCollapsedSections({});
  const anyExpanded = PRINT_SECTIONS.some((s) => !collapsedSections[s.id]);

  // Plan complexity — highest tier among all applied strategies
  const planTier = useMemo(() => {
    let highest = 0;
    const collect = (refs) => {
      for (const ref of refs || []) {
        if (!ref?.applied) continue;
        const strat = getStrategy(ref.strategyId);
        if (!strat) continue;
        if ((strat.complexity || 0) > highest) highest = strat.complexity || 0;
      }
    };
    for (const e of plan.entities || []) collect(e.strategies);
    collect(plan.individual?.strategies);
    return highest;
  }, [plan]);
  const tierMeta = planTier ? TIER_META[planTier] : null;

  // Compute per-strategy savings for the table — prefer the marginal contribution
  // from the compute pipeline so per-row numbers sum to the plan total.
  const itemsWithSavings = useMemo(() => {
    return items.map((item) => {
      if (item.isDerived) return { ...item, savings: 0 };
      const stratRef = item.stratRef;
      if (!stratRef) return { ...item, savings: 0 };
      // Marginal contribution wins when available
      const marginal = result.marginalById?.[stratRef.strategyId];
      if (marginal != null) return { ...item, savings: marginal };
      const reduction = strategyReduction(stratRef);
      const credit = strategyCredit(stratRef);
      const manual = strategyManualSavings(stratRef);
      const savings =
        manual > 0
          ? manual
          : credit > 0
            ? credit
            : reduction * combinedMarginalRate;
      return { ...item, savings };
    });
  }, [items, result.marginalById, combinedMarginalRate]);

  const baselineTax = result.baseline.totalTax;
  const plannedTax = result.planned.totalTax; // Scenario C = full menu
  const yesOnlyTax = result.yesOnlyTotalTax ?? plannedTax; // Scenario B
  const yesOnlySavings = result.yesOnlySavings ?? 0;
  const maybeSavings = result.maybeSavings ?? 0;
  const hasMaybes = maybeSavings > 0;
  const yesPct = baselineTax > 0 ? (yesOnlySavings / baselineTax) * 100 : 0;
  const allPct = baselineTax > 0 ? (result.savings / baselineTax) * 100 : 0;
  const maxScenarioTax = Math.max(baselineTax, yesOnlyTax, plannedTax, 1);

  // Count Yes / Maybe strategies for context
  const yesCount = items.filter(
    (it) => !it.isDerived && it.status === "yes",
  ).length;
  const maybeCount = items.filter(
    (it) => !it.isDerived && it.status === "maybe",
  ).length;

  const handlePrint = () => window.print();

  // Format deadline display
  const fmtDeadline = (item) => {
    if (item.deadline) {
      return item.deadline.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
    if (item.kind === "pre_transaction") return "Before triggering transaction";
    if (item.kind === "ongoing") return "Ongoing discipline";
    return "Flexible";
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-stone-900/80 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white shadow-2xl w-full max-w-[900px] my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top control bar — hidden on print */}
        <div className="no-print border-b border-stone-200 bg-stone-50">
          <div className="px-6 py-3 flex items-center justify-between">
            <div className="sans text-xs uppercase tracking-widest font-semibold text-stone-500">
              Client-Facing Deliverable Preview
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={anyExpanded ? collapseAll : expandAll}
                className="flex items-center gap-1.5 bg-white border border-stone-300 hover:border-stone-400 text-stone-700 px-3 py-1.5 rounded-sm sans text-xs font-semibold"
                title={
                  anyExpanded
                    ? "Collapse every section in the preview (does not affect print)"
                    : "Expand every section in the preview"
                }
              >
                {anyExpanded ? (
                  <>
                    <ChevronUp size={14} />
                    Collapse all
                  </>
                ) : (
                  <>
                    <ChevronDown size={14} />
                    Expand all
                  </>
                )}
              </button>
              <button
                onClick={() => setShowPrintPicker(!showPrintPicker)}
                className="flex items-center gap-1.5 bg-white border border-stone-300 hover:border-stone-400 text-stone-700 px-3 py-1.5 rounded-sm sans text-xs font-semibold"
                title="Choose which sections to include in the print"
              >
                <CheckSquare size={14} />
                Sections to print
                <span className="sans text-[10px] font-semibold text-stone-500 mono num-tabular">
                  {PRINT_SECTIONS.filter((s) => printChecks[s.id]).length}/
                  {PRINT_SECTIONS.length}
                </span>
                <ChevronDown
                  size={12}
                  className={`transition-transform ${showPrintPicker ? "rotate-180" : ""}`}
                />
              </button>
              <button
                onClick={handlePrint}
                disabled={noneChecked}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm sans text-xs font-semibold ${
                  noneChecked
                    ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                    : "bg-stone-900 text-sky-400 hover:bg-stone-800"
                }`}
              >
                <Printer size={14} />
                Print / Save PDF
              </button>
              <button
                onClick={onClose}
                className="text-stone-500 hover:text-stone-900 p-1.5"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {showPrintPicker && (
            <div className="px-6 pb-4 border-t border-stone-200 bg-white">
              <div className="flex items-center justify-between pt-3 mb-2">
                <div className="sans text-[10px] uppercase tracking-widest font-bold text-stone-500">
                  Pick what to include — the cover (letterhead, client name,
                  Max Tax Saved) always prints
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setPrintChecks(
                        PRINT_SECTIONS.reduce(
                          (acc, s) => ({ ...acc, [s.id]: true }),
                          {},
                        ),
                      )
                    }
                    disabled={allChecked}
                    className={`sans text-[11px] font-semibold ${allChecked ? "text-stone-300 cursor-not-allowed" : "text-sky-700 hover:text-sky-900"}`}
                  >
                    Select all
                  </button>
                  <span className="text-stone-300">·</span>
                  <button
                    onClick={() =>
                      setPrintChecks(
                        PRINT_SECTIONS.reduce(
                          (acc, s) => ({ ...acc, [s.id]: false }),
                          {},
                        ),
                      )
                    }
                    disabled={noneChecked}
                    className={`sans text-[11px] font-semibold ${noneChecked ? "text-stone-300 cursor-not-allowed" : "text-stone-700 hover:text-stone-900"}`}
                  >
                    Clear all
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                {PRINT_SECTIONS.map((s) => (
                  <label
                    key={s.id}
                    className="flex items-center gap-2 cursor-pointer hover:bg-stone-50 px-1 py-0.5 rounded-sm"
                  >
                    <input
                      type="checkbox"
                      checked={!!printChecks[s.id]}
                      onChange={(e) => setPrintCheck(s.id, e.target.checked)}
                      className="w-4 h-4 accent-sky-600"
                    />
                    <span
                      className={`sans text-sm ${printChecks[s.id] ? "text-stone-800" : "text-stone-400 line-through"}`}
                    >
                      {s.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Document — marked as print target so CSS isolates this section
            from the rest of the page on print */}
        <div className="px-12 py-10" data-print-target="deliverable">
          {/* Letterhead */}
          <div className="flex items-center justify-between border-b border-stone-300 pb-4 mb-8">
            <div className="flex items-center gap-3">
              <Hexagon size={32} className="text-sky-400" strokeWidth={2.5} />
              <div>
                <div className="serif text-2xl font-bold text-stone-900 leading-none">
                  Linked Accounting LLP
                </div>
                <div className="sans text-xs uppercase tracking-widest text-stone-500 mt-1">
                  Comprehensive Tax Planning
                </div>
              </div>
            </div>
            <div className="text-right sans text-xs text-stone-500">
              Prepared{" "}
              {today.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
              <br />
              Tax Year {plan.taxYear}
            </div>
          </div>

          {/* Title */}
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <div className="sans text-xs uppercase tracking-[0.25em] text-stone-500 mb-2 font-semibold">
                Tax Plan for
              </div>
              <div className="serif text-4xl font-bold text-stone-900">
                {plan.individual?.ownerNames || plan.clientName}
              </div>
            </div>
            {tierMeta && (
              <div
                className={`text-right border-l-4 pl-4 py-1 ${
                  tierMeta.color === "emerald"
                    ? "border-emerald-500"
                    : tierMeta.color === "sky"
                      ? "border-sky-500"
                      : "border-amber-500"
                }`}
              >
                <div className="sans text-[10px] uppercase tracking-[0.25em] text-stone-500 font-semibold mb-1">
                  Plan Tier
                </div>
                <div
                  className={`serif text-2xl font-bold ${
                    tierMeta.color === "emerald"
                      ? "text-emerald-700"
                      : tierMeta.color === "sky"
                        ? "text-sky-700"
                        : "text-amber-700"
                  }`}
                >
                  {tierMeta.label}
                </div>
                <div className="sans text-[11px] text-stone-500 max-w-[260px] mt-1 leading-snug">
                  {tierMeta.description}
                </div>
              </div>
            )}
          </div>

          {/* Hero savings — always shows the WINNING scenario's savings so
              the number here matches the "From Scenario X" callout below and
              matches the sticky KPI bar in the workspace. */}
          {(() => {
            const bestSc = (result.scenarios || []).reduce(
              (best, sc) =>
                !best || sc.fullSavings > best.fullSavings ? sc : best,
              null,
            );
            const heroSavings = bestSc?.fullSavings ?? result.savings;
            const heroBaseline = result.baseline?.totalTax || 0;
            const heroPct =
              heroBaseline > 0 ? (heroSavings / heroBaseline) * 100 : 0;
            return (
              <div className="bg-gradient-to-br from-stone-900 via-stone-900 to-sky-900 text-white p-8 rounded-sm mb-10">
                <div className="sans text-xs uppercase tracking-[0.25em] text-sky-300 font-semibold mb-3">
                  Maximum Estimated Tax Savings
                </div>
                <div className="serif text-7xl font-bold text-sky-400 mono num-tabular leading-none mb-3">
                  ${fmt(heroSavings)}
                </div>
                <div className="sans text-sm text-stone-300 mb-4">
                  {bestSc ? (
                    <>
                      From{" "}
                      <strong className="text-sky-300">{bestSc.name}</strong>{" "}
                      (Yes + Maybe) — the highest-savings path in the scenarios
                      below. {heroPct.toFixed(1)}% off the baseline tax bill.
                    </>
                  ) : (
                    <>
                      Available across the planning scenarios shown below. Each
                      scenario maps a separate path with committed-only and
                      full-menu outcomes.
                    </>
                  )}
                </div>
            <div className="grid grid-cols-3 gap-4 mt-6 pt-5 border-t border-stone-700">
              <div>
                <div className="sans text-[10px] uppercase tracking-widest text-stone-400 font-semibold mb-1">
                  Federal Saved
                </div>
                <div className="serif text-2xl font-bold text-sky-400 num-tabular">
                  ${fmt(result.fedSavings)}
                </div>
              </div>
              <div>
                <div className="sans text-[10px] uppercase tracking-widest text-stone-400 font-semibold mb-1">
                  State Saved
                </div>
                <div className="serif text-2xl font-bold text-sky-400 num-tabular">
                  ${fmt(result.stateSavings)}
                </div>
              </div>
              <div>
                <div className="sans text-[10px] uppercase tracking-widest text-stone-400 font-semibold mb-1">
                  Strategies Applied
                </div>
                <div className="serif text-2xl font-bold text-sky-400 num-tabular">
                  {items.length}
                </div>
              </div>
            </div>
          </div>
            );
          })()}

          {/* Multi-scenario comparison — baseline + N scenarios (Yes-only + Yes+Maybe) */}
          <DeliverableSection
            id="scenarios"
            label="Scenario Comparison"
            collapsed={isCollapsed("scenarios")}
            onToggle={() => toggleCollapse("scenarios")}
            printSkip={skip("scenarios")}
          >
          <div className="mb-10">
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="serif text-2xl font-bold text-stone-900">
                  Scenario Comparison
                </div>
                <div className="sans text-xs text-stone-500 mt-1">
                  Multiple planning paths shown side by side — committed-only
                  and full-menu outcomes for each.
                </div>
              </div>
              <div className="sans text-[11px] text-stone-500">
                <span className="inline-flex items-center gap-1.5 mr-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-600" />
                  Y = Yes (committed)
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  M = Maybe (considering)
                </span>
              </div>
            </div>

            {/* Baseline strip */}
            <div className="border-2 border-rose-300 rounded-sm bg-rose-50/30 p-4 mb-3 flex items-center justify-between">
              <div>
                <div className="sans text-[10px] uppercase tracking-[0.2em] font-bold text-rose-800 mb-1">
                  Baseline · No Action Taken
                </div>
                <div className="sans text-xs text-stone-600">
                  Tax owed if no planning is implemented
                </div>
              </div>
              <div className="mono num-tabular text-3xl font-bold text-rose-700">
                ${fmt(baselineTax)}
              </div>
            </div>

            {/* Scenario cards — one row, two stacked totals per scenario */}
            <div
              className="grid gap-3 mb-3"
              style={{
                gridTemplateColumns: `repeat(${Math.max(result.scenarios?.length || 1, 1)}, minmax(0, 1fr))`,
              }}
            >
              {(result.scenarios || []).map((sc, idx) => {
                const yesPct =
                  baselineTax > 0 ? (sc.yesOnlySavings / baselineTax) * 100 : 0;
                const fullPct =
                  baselineTax > 0 ? (sc.fullSavings / baselineTax) * 100 : 0;
                return (
                  <div
                    key={sc.id}
                    className="border-2 border-stone-300 rounded-sm bg-white"
                  >
                    {/* Header */}
                    <div className="px-4 py-2.5 border-b border-stone-200 bg-stone-50/60">
                      <div className="sans text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">
                        S{idx + 1} · {sc.name}
                      </div>
                      <div className="sans text-[10px] text-stone-500 mt-0.5 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                          {sc.yesCount} Y
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          {sc.maybeCount} M
                        </span>
                      </div>
                    </div>
                    {/* Yes-only block */}
                    <div className="px-4 py-3 border-b border-stone-200 bg-sky-50/30">
                      <div className="sans text-[9px] uppercase tracking-widest font-bold text-sky-700 mb-2">
                        Yes only
                      </div>
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <span className="sans text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                          Tax saved
                        </span>
                        <span className="mono num-tabular text-2xl font-bold text-emerald-700 leading-none">
                          ${fmt(sc.yesOnlySavings)}{" "}
                          <span className="text-xs font-semibold text-emerald-700/70">
                            ({yesPct.toFixed(1)}%)
                          </span>
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="sans text-[10px] uppercase tracking-wider font-semibold text-rose-700">
                          Tax owed
                        </span>
                        <span className="mono num-tabular text-sm font-semibold text-rose-700">
                          ${fmt(sc.yesOnlyTax)}
                        </span>
                      </div>
                    </div>
                    {/* Yes + Maybe block */}
                    <div className="px-4 py-3 bg-emerald-50/40">
                      <div className="sans text-[9px] uppercase tracking-widest font-bold text-emerald-800 mb-2">
                        Yes + Maybe
                      </div>
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <span className="sans text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                          Tax saved
                        </span>
                        <span className="mono num-tabular text-2xl font-bold text-emerald-700 leading-none">
                          ${fmt(sc.fullSavings)}{" "}
                          <span className="text-xs font-semibold text-emerald-700/70">
                            ({fullPct.toFixed(1)}%)
                          </span>
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="sans text-[10px] uppercase tracking-wider font-semibold text-rose-700">
                          Tax owed
                        </span>
                        <span className="mono num-tabular text-sm font-semibold text-rose-700">
                          ${fmt(sc.fullTax)}
                        </span>
                      </div>
                      {sc.maybeSavings > 0 && (
                        <div className="sans text-[10px] text-stone-500 mt-1 text-right">
                          M adds ${fmt(sc.maybeSavings)} on top of Yes
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="sans text-[11px] text-stone-600 leading-relaxed bg-stone-50 border border-stone-200 rounded-sm px-4 py-2">
              Each scenario is an independent planning path. <strong>Yes only</strong>{" "}
              shows the outcome if only the committed strategies are implemented.{" "}
              <strong>Yes + Maybe</strong> shows the outcome if every strategy
              in that scenario is implemented.
            </div>
          </div>
          </DeliverableSection>

          {/* Client-facing callouts — items the client should know about */}
          <DeliverableSection
            id="callouts"
            label="Items to Discuss"
            collapsed={isCollapsed("callouts")}
            onToggle={() => toggleCollapse("callouts")}
            printSkip={skip("callouts")}
          >
            <PlanCallouts
              alerts={generateAlerts(plan, result)}
              audience="client"
              title="Items to Discuss"
            />
          </DeliverableSection>

          {/* Strategies + timeline + savings */}
          <DeliverableSection
            id="strategies"
            label="Strategies & Implementation Timeline"
            collapsed={isCollapsed("strategies")}
            onToggle={() => toggleCollapse("strategies")}
            printSkip={skip("strategies")}
          >
          <div className="mb-10">
            <div className="flex items-baseline justify-between mb-4">
              <div className="serif text-2xl font-bold text-stone-900">
                Strategies & Implementation Timeline
              </div>
              <div className="sans text-xs text-stone-500">
                {itemsWithSavings.length}{" "}
                {itemsWithSavings.length === 1 ? "item" : "items"}
              </div>
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-stone-900 text-sky-400">
                  <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5 w-[28%]">
                    Strategy
                  </th>
                  <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5 w-[14%]">
                    Applied To
                  </th>
                  <th className="sans text-[10px] font-semibold uppercase tracking-widest text-center px-3 py-2.5 w-[20%]">
                    Per Scenario
                  </th>
                  <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5 w-[22%]">
                    Deadline / Action
                  </th>
                  <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5 w-[16%]">
                    Max Saved
                  </th>
                </tr>
              </thead>
              <tbody>
                {itemsWithSavings.map((item, i) => (
                  <tr
                    key={i}
                    className={`border-b border-stone-200 ${
                      i % 2 === 0 ? "bg-white" : "bg-stone-50/50"
                    }`}
                  >
                    <td className="px-3 py-3 align-top">
                      <div className="sans text-sm font-semibold text-stone-900 leading-snug">
                        {item.strategy.name}
                      </div>
                      {item.strategy.citation && (
                        <div className="sans text-[11px] text-stone-500 mono mt-0.5">
                          {item.strategy.citation}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="sans text-sm text-stone-700">{item.host}</div>
                      <div
                        className={`inline-block mt-1 sans text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm ${
                          item.hostType === "entity"
                            ? "bg-sky-100 text-sky-800"
                            : "bg-stone-100 text-stone-700"
                        }`}
                      >
                        {item.hostType === "entity" ? "Entity" : "Personal"}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      {item.isDerived ? (
                        <div className="sans text-[10px] text-stone-400 text-center">—</div>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          {(result.scenarios || []).map((sc, idx) => {
                            const st = getStatusForScenario(
                              item.stratRef,
                              sc.id,
                            );
                            const bg =
                              st === "yes"
                                ? "bg-emerald-600 text-white"
                                : st === "maybe"
                                  ? "bg-amber-500 text-white"
                                  : "bg-stone-200 text-stone-500";
                            const lbl =
                              st === "yes" ? "Y" : st === "maybe" ? "M" : "—";
                            return (
                              <div
                                key={sc.id}
                                className={`w-7 text-center rounded-sm py-0.5 ${bg}`}
                                title={`${sc.name}: ${st}`}
                              >
                                <div className="sans text-[7px] uppercase tracking-wider font-bold leading-none opacity-80">
                                  S{idx + 1}
                                </div>
                                <div className="sans text-[11px] font-bold leading-tight">
                                  {lbl}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="sans text-sm font-semibold text-stone-800 leading-snug">
                        {fmtDeadline(item)}
                      </div>
                      {item.timingNote && (
                        <div className="sans text-[11px] text-stone-500 mt-0.5 leading-snug">
                          {item.timingNote}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top text-right">
                      {item.savings > 0 ? (
                        <div className="mono num-tabular text-base font-bold text-emerald-700">
                          ${fmt(item.savings)}
                        </div>
                      ) : (
                        <div className="sans text-xs text-stone-400 italic">
                          {item.isDerived ? "Procedural" : "—"}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {/* Per-scenario savings totals — one Yes-only row + one Full row per scenario */}
                {(result.scenarios || []).map((sc, idx) => (
                  <React.Fragment key={sc.id}>
                    <tr className="bg-sky-50 border-t-2 border-sky-300">
                      <td
                        colSpan={4}
                        className="px-3 py-2 sans text-xs font-bold text-sky-900"
                      >
                        S{idx + 1} · {sc.name} — Yes only
                      </td>
                      <td className="px-3 py-2 text-right mono num-tabular text-base font-bold text-sky-700">
                        ${fmt(sc.yesOnlySavings)}
                      </td>
                    </tr>
                    <tr className="bg-emerald-50 border-b border-emerald-200">
                      <td
                        colSpan={4}
                        className="px-3 py-2 sans text-xs font-bold text-emerald-900"
                      >
                        S{idx + 1} · {sc.name} — Yes + Maybe
                      </td>
                      <td className="px-3 py-2 text-right mono num-tabular text-base font-bold text-emerald-700">
                        ${fmt(sc.fullSavings)}
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
                {/* Final headline row */}
                <tr className="bg-stone-900 text-stone-100">
                  <td colSpan={4} className="px-3 py-3 sans text-sm font-bold">
                    Max Estimated Tax Savings (best scenario, full menu)
                  </td>
                  <td className="px-3 py-3 text-right serif text-2xl font-bold text-sky-400 mono num-tabular">
                    ${fmt(result.savings)}
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="sans text-[11px] text-stone-500 mt-2 italic leading-relaxed">
              Estimated savings are calculated using your combined marginal rate
              ({fmtPct(combinedMarginalRate, 1)}) where the strategy reduces taxable income.
              Direct credits show dollar-for-dollar. Final figures depend on year-end facts and
              implementation.
            </div>
          </div>
          </DeliverableSection>

          {/* Where the tax dollars come from — visual breakdown */}
          <DeliverableSection
            id="taxBill"
            label="Where Your Tax Dollars Go"
            collapsed={isCollapsed("taxBill")}
            onToggle={() => toggleCollapse("taxBill")}
            printSkip={skip("taxBill")}
          >
            <TaxBillBreakdown result={result} />
          </DeliverableSection>

          {/* Federal bracket position — client-friendly teaching tool */}
          <DeliverableSection
            id="brackets"
            label="Where You Sit in the Federal Brackets"
            collapsed={isCollapsed("brackets")}
            onToggle={() => toggleCollapse("brackets")}
            printSkip={skip("brackets")}
          >
            <BracketPosition result={result} plan={plan} />
          </DeliverableSection>

          {/* Income transparency — per-entity income build with book/cash adjustments */}
          <DeliverableSection
            id="income"
            label="Income Calculation Detail"
            collapsed={isCollapsed("income")}
            onToggle={() => toggleCollapse("income")}
            printSkip={skip("income")}
          >
            <IncomeTransparencyPage plan={plan} result={result} />
          </DeliverableSection>

          {/* Strategy ROI — only renders when at least one strategy has costs entered */}
          <DeliverableSection
            id="roi"
            label="Strategy ROI — Cost vs. Savings"
            collapsed={isCollapsed("roi")}
            onToggle={() => toggleCollapse("roi")}
            printSkip={skip("roi")}
          >
            <StrategyROI items={itemsWithSavings} />
          </DeliverableSection>

          {/* 3-Year Plan Outlook — lifetime view of the savings */}
          <DeliverableSection
            id="multiYear"
            label="3-Year Plan Outlook"
            collapsed={isCollapsed("multiYear")}
            onToggle={() => toggleCollapse("multiYear")}
            printSkip={skip("multiYear")}
          >
            <MultiYearProjection multiYear={result.multiYear} plan={plan} />
          </DeliverableSection>

          {/* Estimated Tax & Quarterly Payment Schedule */}
          <DeliverableSection
            id="estimatedTax"
            label="Estimated Tax & Quarterly Payment Schedule"
            collapsed={isCollapsed("estimatedTax")}
            onToggle={() => toggleCollapse("estimatedTax")}
            printSkip={skip("estimatedTax")}
          >
            <EstimatedTaxSchedule
              estimatedTax={result.estimatedTax}
              taxYear={plan.taxYear}
              plannedTotalTax={result.planned.totalTax}
            />
          </DeliverableSection>

          {/* Year-End Action Punch List */}
          <DeliverableSection
            id="actionItems"
            label="Action Items & Deadlines"
            collapsed={isCollapsed("actionItems")}
            onToggle={() => toggleCollapse("actionItems")}
            printSkip={skip("actionItems")}
          >
            <YearEndChecklist
              items={itemsWithSavings}
              estimatedTax={result.estimatedTax}
              taxYear={plan.taxYear}
              fmtDeadline={fmtDeadline}
            />
          </DeliverableSection>

          {/* Compounding value of the savings */}
          <DeliverableSection
            id="compounding"
            label="Compounding Value of These Savings"
            collapsed={isCollapsed("compounding")}
            onToggle={() => toggleCollapse("compounding")}
            printSkip={skip("compounding")}
          >
            <CompoundingValueSection
              annualSavings={result.savings}
              rate={investRate}
              years={investYears}
              onRateChange={setInvestRate}
              onYearsChange={setInvestYears}
            />
          </DeliverableSection>

          {/* Footer */}
          <div className="border-t border-stone-300 pt-4 sans text-[11px] text-stone-500 leading-relaxed">
            This plan represents projected tax outcomes based on information provided and current
            tax law. Actual results depend on faithful implementation, final-year facts, and any
            legislative or regulatory changes. Linked Accounting LLP will coordinate execution
            timing and deliverables for each strategy listed above.
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Strategy Detail Modal — preview the client-facing strategy brief, then
// download as a Word doc and/or compose an email to the client.
// ════════════════════════════════════════════════════════════════════════
// Error boundary that prevents a broken brief from crashing the whole app
class BriefErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("Brief render error:", error, errorInfo);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="p-6">
          <div className="bg-rose-50 border border-rose-300 rounded-sm p-4">
            <div className="serif text-lg font-bold text-rose-900 mb-2">
              Couldn't render this brief
            </div>
            <div className="sans text-sm text-rose-800 mb-3">
              Something in the brief data tripped the renderer. The rest of the
              app keeps working — close this modal to continue. Error details:
            </div>
            <pre className="mono text-xs text-rose-900 bg-white border border-rose-200 p-3 rounded-sm overflow-auto">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function StrategyDetailModal({ strategy, plan, source, onClose }) {
  const [step, setStep] = useState("preview"); // "preview" | "confirmEmail"
  const t = strategy?.detailTemplate;
  const brief = strategy?.clientBrief;

  // Fire brief_opened event once per mount for engagement analytics
  useEffect(() => {
    if (!strategy?.id) return;
    logStrategyEvent({
      strategyId: strategy.id,
      eventType: "brief_opened",
      source: source || "unknown",
      planId: plan?.id || null,
    });
  }, [strategy?.id, source, plan?.id]);

  // Forecast computed inline so the preview shows the same numbers the doc does.
  // IMPORTANT: keep this hook ABOVE the early-return guard below — when the
  // strategy prop changes to one without t/brief, an early return after a
  // hook causes React error #310 (rendered more hooks than during the
  // previous render).
  const forecastRows = useMemo(() => {
    if (!t || !t.forecast) return [];
    const f = t.forecast;
    const rows = [];
    let balance = 0;
    let cumulativeSavings = 0;
    for (let y = 1; y <= f.years; y++) {
      const investmentReturn = y === 1 ? 0 : balance * f.annualReturn;
      const programs = investmentReturn * f.programPct;
      const retained = investmentReturn * f.retainPct;
      const excise = investmentReturn * f.excisePct;
      balance = balance + f.annualContribution + retained - excise;
      cumulativeSavings += f.annualContribution * f.marginalRate;
      rows.push({
        year: y,
        contribution: f.annualContribution,
        investmentReturn,
        programs,
        retained,
        excise,
        balance,
        cumulativeSavings,
        isMilestone: !!f.milestones?.[y],
      });
    }
    return rows;
  }, [t?.forecast]);

  // Modal opens if either rich structured brief OR Word-doc template exists.
  // Early return MUST be after every hook above so hook order stays stable
  // across re-renders (avoids React error #310).
  if (!t && !brief) return null;

  const ctx = {
    clientName: plan.individual?.ownerNames || plan.clientName || "",
    clientEmail: plan.clientEmail || "",
    firmName: "Linked Accounting LLP",
    taxYear: plan.taxYear,
    preparedDate: new Date().toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }),
  };

  const handleDownload = () => {
    downloadStrategyDoc(strategy, ctx);
  };

  const handleEmailClick = () => {
    if (!plan.clientEmail) {
      alert(
        "Add a client email to this plan (in the header) before sending. The doc will still download for you to attach manually.",
      );
      // Still download
      downloadStrategyDoc(strategy, ctx);
      return;
    }
    setStep("confirmEmail");
  };

  const handleEmailConfirm = () => {
    const filename = downloadStrategyDoc(strategy, ctx);
    const mailto = buildStrategyMailto(strategy, {
      ...ctx,
      attachmentFilename: filename,
    });
    window.open(mailto, "_blank");
    setStep("preview");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-stone-900/80 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white shadow-2xl w-full max-w-[820px] my-8 rounded-sm flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-300 flex items-center justify-between bg-gradient-to-r from-stone-900 to-sky-900">
          <div>
            <div className="serif text-xl font-bold text-white">
              Strategy Brief: {strategy.name}
            </div>
            <div className="sans text-xs text-sky-200 mt-0.5">
              Client-facing summary — preview, download, or email to{" "}
              <strong>{ctx.clientName || "client"}</strong>
            </div>
          </div>
          <button onClick={onClose} className="text-white p-1">
            <X size={20} />
          </button>
        </div>

        {/* Body — marked as print target so the CSS isolates this section
            from the rest of the page when the user prints */}
        <div className="overflow-auto flex-1 px-6 py-5" data-print-target="brief">
          {step === "confirmEmail" ? (
            <div className="py-6 text-center">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-sky-100 rounded-full mb-4">
                <Mail size={22} className="text-sky-700" />
              </div>
              <div className="serif text-2xl font-bold text-stone-900 mb-2">
                Email this brief to your client?
              </div>
              <div className="sans text-sm text-stone-600 max-w-md mx-auto leading-relaxed mb-5">
                We'll download the <strong>{strategy.name}</strong> brief as a Word
                document and open a new email pre-filled to{" "}
                <strong className="text-stone-900">{plan.clientEmail}</strong> with a
                friendly subject and intro. Attach the downloaded file in the email
                and click send.
              </div>
              <div className="sans text-[11px] text-stone-500 italic max-w-md mx-auto leading-relaxed mb-6">
                Browsers can't attach files to emails directly — that's why you
                attach manually. (We could automate this end-to-end with a backend
                email service if it becomes a frequent workflow.)
              </div>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setStep("preview")}
                  className="px-3 py-2 sans text-sm text-stone-600 hover:text-stone-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEmailConfirm}
                  className="flex items-center gap-1.5 bg-stone-900 text-sky-400 px-4 py-2 rounded-sm sans text-sm font-bold hover:bg-stone-800"
                >
                  <Mail size={14} />
                  Yes, download &amp; open email
                </button>
              </div>
            </div>
          ) : brief ? (
            <BriefErrorBoundary>
              <ClientBriefView strategy={strategy} brief={brief} />
            </BriefErrorBoundary>
          ) : (
            <article className="sans text-sm leading-relaxed">
              <h3 className="serif text-3xl font-bold text-stone-900 mb-1">
                {strategy.name}
              </h3>
              <p className="sans text-stone-600 italic mb-4">
                {strategy.shortDescription}
              </p>

              {t.auditConfidence && (
                <div className="inline-block bg-sky-50 border border-sky-300 px-3 py-2 rounded-sm mb-5">
                  <div className="sans text-[10px] uppercase tracking-widest text-stone-600 font-semibold">
                    IRS Audit Confidence
                  </div>
                  <div className="mono num-tabular text-lg font-bold text-sky-900">
                    {t.auditConfidence.percent} —{" "}
                    <span className="sans">{t.auditConfidence.label}</span>
                  </div>
                </div>
              )}

              {t.idealClient && (
                <DocSection title="Ideal Tax Client">
                  <p className="text-stone-800">{t.idealClient}</p>
                </DocSection>
              )}

              {t.overview && (
                <DocSection title="Strategy Overview">
                  {(Array.isArray(t.overview) ? t.overview : [t.overview]).map(
                    (p, i) => (
                      <p key={i} className="text-stone-800 mb-3 last:mb-0">
                        {p}
                      </p>
                    ),
                  )}
                </DocSection>
              )}

              {Array.isArray(t.flow) && t.flow.length > 0 && (
                <DocSection title="Flow of Funds">
                  <div className="space-y-2">
                    {t.flow.map((f) => (
                      <div
                        key={f.step}
                        className="flex gap-3 bg-stone-50 border-l-2 border-sky-400 px-3 py-2 rounded-r-sm"
                      >
                        <div className="shrink-0 w-6 h-6 rounded-full bg-sky-500 text-white sans text-xs font-bold flex items-center justify-center">
                          {f.step}
                        </div>
                        <div>
                          <div className="font-semibold text-sky-900">
                            {f.title}
                          </div>
                          <div className="text-stone-800 text-sm">{f.body}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </DocSection>
              )}

              {forecastRows.length > 0 && (
                <DocSection title={`Financial Forecast — ${t.forecast.years}-Year Horizon`}>
                  {t.forecast.intro && (
                    <p className="text-stone-700 italic mb-3 text-xs">
                      {t.forecast.intro}
                    </p>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-stone-900 text-sky-400">
                          <th className="px-2 py-1.5 text-left sans text-[10px] uppercase tracking-wider">
                            Year
                          </th>
                          <th className="px-2 py-1.5 text-right sans text-[10px] uppercase tracking-wider">
                            Contribution
                          </th>
                          <th className="px-2 py-1.5 text-right sans text-[10px] uppercase tracking-wider">
                            Return
                          </th>
                          <th className="px-2 py-1.5 text-right sans text-[10px] uppercase tracking-wider">
                            Programs
                          </th>
                          <th className="px-2 py-1.5 text-right sans text-[10px] uppercase tracking-wider">
                            Balance
                          </th>
                          <th className="px-2 py-1.5 text-right sans text-[10px] uppercase tracking-wider">
                            Cum. Tax Saved
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecastRows.map((r) => (
                          <tr
                            key={r.year}
                            className={`border-b border-stone-200 ${
                              r.isMilestone ? "bg-amber-50" : ""
                            }`}
                          >
                            <td className="px-2 py-1.5 font-semibold">
                              Year {r.year}
                              {r.isMilestone && " ★"}
                            </td>
                            <td className="px-2 py-1.5 text-right mono num-tabular">
                              ${fmt(r.contribution)}
                            </td>
                            <td className="px-2 py-1.5 text-right mono num-tabular">
                              ${fmt(r.investmentReturn)}
                            </td>
                            <td className="px-2 py-1.5 text-right mono num-tabular">
                              ${fmt(r.programs)}
                            </td>
                            <td className="px-2 py-1.5 text-right mono num-tabular font-semibold text-sky-700">
                              ${fmt(r.balance)}
                            </td>
                            <td className="px-2 py-1.5 text-right mono num-tabular font-semibold text-emerald-700">
                              ${fmt(r.cumulativeSavings)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {Object.entries(t.forecast.milestones || {}).map(
                    ([yr, note]) => (
                      <div
                        key={yr}
                        className="bg-amber-50 border-l-2 border-amber-400 px-3 py-2 rounded-r-sm mt-2 text-xs"
                      >
                        <strong className="text-amber-900">
                          ★ Year {yr} milestone:
                        </strong>{" "}
                        <span className="text-stone-800">{note}</span>
                      </div>
                    ),
                  )}
                  {t.forecast.note && (
                    <p className="text-[11px] text-stone-500 italic mt-2">
                      {t.forecast.note}
                    </p>
                  )}
                </DocSection>
              )}

              {Array.isArray(t.faqs) && t.faqs.length > 0 && (
                <DocSection title="Frequently Asked Questions">
                  <div className="space-y-3">
                    {t.faqs.map((f, i) => (
                      <div key={i}>
                        <div className="font-semibold text-sky-900 mb-0.5">
                          {f.q}
                        </div>
                        <div className="text-stone-800 text-sm">{f.a}</div>
                      </div>
                    ))}
                  </div>
                </DocSection>
              )}

              {Array.isArray(t.cautions) && t.cautions.length > 0 && (
                <DocSection title="⚠ Critical Considerations">
                  <div className="space-y-2">
                    {t.cautions.map((c, i) => (
                      <div
                        key={i}
                        className="bg-rose-50 border-l-2 border-rose-500 px-3 py-2 rounded-r-sm text-sm"
                      >
                        <strong className="text-rose-900">{c.title}:</strong>{" "}
                        <span className="text-stone-800">{c.body}</span>
                      </div>
                    ))}
                  </div>
                </DocSection>
              )}

              {Array.isArray(t.nextSteps) && t.nextSteps.length > 0 && (
                <DocSection title="Recommended Next Steps">
                  <ol className="space-y-1.5 list-decimal list-inside text-stone-800">
                    {t.nextSteps.map((s, i) => (
                      <li key={i} className="pl-1">
                        {s}
                      </li>
                    ))}
                  </ol>
                </DocSection>
              )}

              {t.disclaimer && (
                <div className="sans text-[11px] text-stone-500 mt-6 pt-4 border-t border-stone-200 italic leading-relaxed">
                  {t.disclaimer}
                </div>
              )}
            </article>
          )}
        </div>

        {/* Footer */}
        {step === "preview" && (
          <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex items-center justify-between">
            <div className="sans text-xs text-stone-500">
              {plan.clientEmail ? (
                <>
                  Will send to{" "}
                  <strong className="text-stone-900">{plan.clientEmail}</strong>
                </>
              ) : (
                <span className="text-amber-700">
                  No client email set — add one in the plan header to email directly
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-3 py-2 sans text-sm text-stone-600 hover:text-stone-900"
              >
                Close
              </button>
              {t ? (
                <>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-1.5 px-3 py-2 sans text-sm font-semibold text-stone-700 hover:text-stone-900 border border-stone-300 rounded-sm hover:border-stone-500"
                  >
                    <Download size={14} />
                    Download Word Doc
                  </button>
                  <button
                    onClick={handleEmailClick}
                    className="flex items-center gap-1.5 bg-stone-900 text-sky-400 px-4 py-2 rounded-sm sans text-sm font-bold hover:bg-stone-800"
                  >
                    <Mail size={14} />
                    Email to Client
                  </button>
                </>
              ) : (
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-1.5 bg-stone-900 text-sky-400 px-4 py-2 rounded-sm sans text-sm font-bold hover:bg-stone-800"
                  title="Print or save the brief as PDF"
                >
                  <Printer size={14} />
                  Print / Save PDF
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DocSection({ title, children }) {
  return (
    <section className="mt-5">
      <h4 className="sans text-[11px] uppercase tracking-widest font-bold text-sky-700 border-b border-sky-200 pb-1 mb-2">
        {title}
      </h4>
      {children}
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Client Brief View — renders a structured rich brief like the firm's
// printed PDFs (STR / REPS) with tables, callouts, and comparison panels.
// ════════════════════════════════════════════════════════════════════════
function ClientBriefView({ strategy, brief }) {
  const theme = (t) =>
    ({
      navy: "bg-sky-900 text-sky-100",
      green: "bg-emerald-700 text-white",
      red: "bg-rose-700 text-white",
      orange: "bg-amber-700 text-white",
      purple: "bg-purple-700 text-white",
      gray: "bg-stone-700 text-white",
    })[t] || "bg-stone-900 text-sky-300";

  const calloutVariant = (v) =>
    ({
      highlight: {
        wrap: "bg-amber-50 border-l-4 border-amber-500",
        title: "text-amber-900",
      },
      warn: {
        wrap: "bg-rose-50 border-l-4 border-rose-500",
        title: "text-rose-900",
      },
      info: {
        wrap: "bg-sky-50 border-l-4 border-sky-500",
        title: "text-sky-900",
      },
      success: {
        wrap: "bg-emerald-50 border-l-4 border-emerald-500",
        title: "text-emerald-900",
      },
    })[v] || {
      wrap: "bg-stone-50 border-l-4 border-stone-400",
      title: "text-stone-900",
    };

  const sectionBlockTheme = (t) =>
    ({
      green: "bg-emerald-50 border border-emerald-200",
      red: "bg-rose-50 border border-rose-200",
      navy: "bg-sky-50 border border-sky-200",
      purple: "bg-purple-50 border border-purple-200",
      orange: "bg-amber-50 border border-amber-300",
    })[t] || "bg-stone-50 border border-stone-200";

  return (
    <article className="sans text-sm leading-relaxed text-stone-800">
      {/* Letterhead */}
      <div className="flex items-center justify-between border-b border-stone-300 pb-3 mb-5">
        <div className="flex items-center gap-2">
          <Hexagon size={22} className="text-sky-400" strokeWidth={2.5} />
          <div>
            <div className="serif text-lg font-bold text-stone-900 leading-none">
              Linked Accounting LLP
            </div>
            <div className="sans text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">
              Strategy Brief
            </div>
          </div>
        </div>
        <div className="sans text-[10px] uppercase tracking-widest text-stone-500">
          {strategy.citation || ""}
        </div>
      </div>

      <h3 className="serif text-2xl font-bold text-stone-900 leading-tight mb-1">
        {brief.title}
      </h3>
      {brief.subtitle && (
        <div className="sans text-sm text-sky-800 font-semibold mb-4">
          {brief.subtitle}
        </div>
      )}
      {brief.intro && (
        <p className="text-stone-800 leading-relaxed mb-5">{brief.intro}</p>
      )}

      {/* Sections */}
      {(brief.sections || []).map((sec, i) => {
        if (sec.type === "heading") {
          return (
            <h4
              key={i}
              className="sans text-[11px] uppercase tracking-widest font-bold text-sky-700 border-b border-sky-200 pb-1 mt-6 mb-3"
            >
              {sec.text}
            </h4>
          );
        }
        if (sec.type === "paragraph") {
          return (
            <p key={i} className="text-stone-800 leading-relaxed mb-3">
              {sec.text}
            </p>
          );
        }
        if (sec.type === "bullets") {
          return (
            <ul key={i} className="list-disc list-inside space-y-1 mb-3 ml-2">
              {sec.items.map((it, j) => (
                <li key={j} className="text-stone-800">
                  {it}
                </li>
              ))}
            </ul>
          );
        }
        if (sec.type === "table") {
          return (
            <div
              key={i}
              className="mb-4 border border-stone-300 rounded-sm overflow-hidden"
            >
              <table className="w-full text-xs">
                <thead>
                  <tr className={theme(sec.theme || "navy")}>
                    {sec.headers.map((h, j) => (
                      <th
                        key={j}
                        className="px-3 py-2 text-left sans text-[10px] uppercase tracking-wider font-bold"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sec.rows.map((row, j) => (
                    <tr
                      key={j}
                      className={`border-t border-stone-200 ${
                        j % 2 === 0 ? "bg-white" : "bg-stone-50"
                      }`}
                    >
                      {row.map((cell, k) => (
                        <td
                          key={k}
                          className="px-3 py-2 align-top text-stone-800 leading-snug"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (sec.type === "callout") {
          const cv = calloutVariant(sec.variant);
          return (
            <div key={i} className={`${cv.wrap} px-4 py-3 rounded-r-sm mb-4`}>
              {sec.title && (
                <div className={`sans text-sm font-bold mb-1 ${cv.title}`}>
                  {sec.title}
                </div>
              )}
              <div className="text-stone-800 leading-relaxed text-sm">
                {sec.body}
              </div>
            </div>
          );
        }
        if (sec.type === "twoColCompare") {
          return (
            <div key={i} className="grid grid-cols-2 gap-3 mb-4">
              {[sec.left, sec.right].map((col, j) => (
                <div
                  key={j}
                  className={`${sectionBlockTheme(col.theme)} rounded-sm overflow-hidden`}
                >
                  <div
                    className={`${theme(col.theme)} px-3 py-2 sans text-[11px] uppercase tracking-wider font-bold`}
                  >
                    {col.title}
                  </div>
                  <div className="px-3 py-2.5 text-xs text-stone-800 space-y-1.5">
                    {col.body && (
                      <p className="leading-snug mb-2">{col.body}</p>
                    )}
                    {Array.isArray(col.items) && col.items.length > 0 && (
                      <ul className="space-y-1">
                        {col.items.map((it, k) => (
                          <li key={k} className="flex gap-1.5">
                            <span className="shrink-0">•</span>
                            <span className="leading-snug">{it}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {col.footer && (
                      <p className="mt-2 pt-2 border-t border-stone-300 italic leading-snug">
                        {col.footer}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        }
        if (sec.type === "threeColCompare") {
          return (
            <div key={i} className="grid grid-cols-3 gap-2 mb-4">
              {sec.columns.map((col, j) => (
                <div
                  key={j}
                  className={`${sectionBlockTheme(col.theme)} rounded-sm overflow-hidden`}
                >
                  <div
                    className={`${theme(col.theme)} px-2.5 py-2 sans text-[10px] uppercase tracking-wider font-bold text-center`}
                  >
                    {col.title}
                  </div>
                  <div className="px-2.5 py-2 text-[11px] text-stone-800">
                    {Array.isArray(col.items) && (
                      <ul className="space-y-1">
                        {col.items.map((it, k) => (
                          <li key={k} className="flex gap-1">
                            <span className="shrink-0">•</span>
                            <span className="leading-snug">{it}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        }
        return null;
      })}

      <div className="sans text-[10px] text-stone-500 mt-6 pt-3 border-t border-stone-200 italic leading-relaxed text-center">
        This brief is for educational purposes only and does not constitute tax
        advice. Individual circumstances vary. Consult a qualified independent
        tax advisor before implementing any strategy. Linked Accounting |
        linkedaccounting.com
      </div>
    </article>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Tax Bill Breakdown — single stacked horizontal bar showing where the
// tax dollars come from. Legend below. Client-friendly visual.
// ════════════════════════════════════════════════════════════════════════
function TaxBillBreakdown({ result, compact = false }) {
  if (!result?.planned) return null;
  const fed = result.planned.federal || {};
  const state = result.planned.stateTax || {};

  const rawSegments = [
    {
      label: "Federal ordinary income",
      amount: fed.ordinaryTax || 0,
      color: "bg-rose-600",
      legendBg: "bg-rose-600",
    },
    {
      label: "Federal capital gains",
      amount: fed.capitalGainsTax || 0,
      color: "bg-rose-400",
      legendBg: "bg-rose-400",
    },
    {
      label: "AMT (§55)",
      amount: fed.amtTax || 0,
      color: "bg-amber-600",
      legendBg: "bg-amber-600",
    },
    {
      label: "NIIT (3.8%)",
      amount: fed.niitTax || 0,
      color: "bg-amber-500",
      legendBg: "bg-amber-500",
    },
    {
      label: "SE tax",
      amount: fed.seTax || 0,
      color: "bg-orange-500",
      legendBg: "bg-orange-500",
    },
    {
      label: "Additional Medicare",
      amount: fed.additionalMedicare || 0,
      color: "bg-orange-600",
      legendBg: "bg-orange-600",
    },
    {
      label: "State tax",
      amount: state.totalTax || 0,
      color: "bg-sky-700",
      legendBg: "bg-sky-700",
    },
  ];
  const segments = rawSegments.filter((s) => s.amount > 0);
  const total = segments.reduce((acc, s) => acc + s.amount, 0);
  if (total <= 0) return null;

  return (
    <div className={compact ? "mb-6" : "mb-10"}>
      <div className="mb-4">
        <div
          className={`serif font-bold text-stone-900 ${compact ? "text-lg" : "text-2xl"}`}
        >
          Where Your Tax Dollars Go
        </div>
        <div className="sans text-xs text-stone-500 mt-1">
          Every dollar of tax broken down by source.
        </div>
      </div>

      {/* Stacked bar */}
      <div className="h-10 flex rounded-sm overflow-hidden bg-stone-100 mb-3 border border-stone-200">
        {segments.map((s, i) => (
          <div
            key={i}
            className={`${s.color} flex items-center justify-center sans text-[10px] font-bold text-white`}
            style={{ width: `${(s.amount / total) * 100}%` }}
            title={`${s.label}: $${fmt(s.amount)}`}
          >
            {(s.amount / total) >= 0.05 &&
              `${((s.amount / total) * 100).toFixed(0)}%`}
          </div>
        ))}
      </div>

      {/* Legend grid */}
      <div className={`grid gap-x-6 gap-y-1.5 ${compact ? "grid-cols-2" : "grid-cols-2"}`}>
        {segments.map((s, i) => (
          <div
            key={i}
            className="flex items-center justify-between text-sm border-b border-stone-100 pb-1"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className={`w-3 h-3 rounded-sm ${s.legendBg} shrink-0`} />
              <div className="sans text-stone-700 truncate">{s.label}</div>
            </div>
            <div className="mono num-tabular text-stone-900 shrink-0 ml-2">
              <span className="font-bold">${fmt(s.amount)}</span>
              <span className="text-stone-400 ml-1 text-xs">
                ({((s.amount / total) * 100).toFixed(1)}%)
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-2 border-t-2 border-stone-300 flex justify-between sans text-sm font-bold">
        <div className="text-stone-900">Total federal + state</div>
        <div className="mono num-tabular text-stone-900">${fmt(total)}</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Bracket Position — horizontal bracket bar with a marker showing the
// client's marginal rate. Teaches the "next-dollar tax" concept in 2 sec.
// ════════════════════════════════════════════════════════════════════════
function BracketPosition({ result, plan, compact = false }) {
  if (!result?.planned?.federal) return null;
  const fed = result.planned.federal;
  const ordinaryTaxable = fed.ordinaryTaxable || 0;
  const filingStatus = fed.filingStatus || plan.individual?.filingStatus || "single";
  const fedData = getFederalData(plan.taxYear);
  const brackets = fedData.brackets[filingStatus] || [];

  if (brackets.length === 0 || ordinaryTaxable <= 0) return null;

  // Find the bracket the client is currently in
  let currentIdx = 0;
  for (let i = 0; i < brackets.length; i++) {
    if (ordinaryTaxable >= brackets[i].from) currentIdx = i;
  }
  const currentBracket = brackets[currentIdx];
  const nextBracket = brackets[currentIdx + 1];

  // Cap the visualization right edge
  const topBracketStart = brackets[brackets.length - 1].from;
  const cap = Math.max(ordinaryTaxable * 1.25, topBracketStart * 1.15);

  // Build segments
  const segments = brackets.map((b, i) => {
    const next = brackets[i + 1];
    const top = next ? Math.min(next.from, cap) : cap;
    return {
      from: b.from,
      to: top,
      rate: b.rate,
      width: ((top - b.from) / cap) * 100,
      isCurrent: i === currentIdx,
    };
  });

  const markerPct = Math.min((ordinaryTaxable / cap) * 100, 100);

  // Color gradient: green → amber → orange → rose
  const colorFor = (rate) => {
    if (rate <= 0.1) return "bg-emerald-300";
    if (rate <= 0.12) return "bg-emerald-400";
    if (rate <= 0.22) return "bg-amber-300";
    if (rate <= 0.24) return "bg-amber-400";
    if (rate <= 0.32) return "bg-orange-400";
    if (rate <= 0.35) return "bg-orange-500";
    return "bg-rose-600";
  };

  return (
    <div className={compact ? "mb-6" : "mb-10"}>
      <div className="mb-4">
        <div
          className={`serif font-bold text-stone-900 ${compact ? "text-lg" : "text-2xl"}`}
        >
          Where You Sit in the Federal Brackets
        </div>
        <div className="sans text-xs text-stone-500 mt-1">
          Each band is a federal tax bracket. The line shows where your
          ordinary taxable income lands.
        </div>
      </div>

      {/* Bracket bar */}
      <div className="relative h-12 flex rounded-sm overflow-hidden border border-stone-300 mb-1">
        {segments.map((s, i) => (
          <div
            key={i}
            className={`${colorFor(s.rate)} flex items-center justify-center sans text-[11px] font-bold text-stone-900 relative ${s.isCurrent ? "ring-2 ring-inset ring-stone-900" : ""}`}
            style={{ width: `${s.width}%` }}
          >
            {(s.rate * 100).toFixed(0)}%
          </div>
        ))}
        {/* Marker line */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-stone-900 shadow-lg"
          style={{ left: `${markerPct}%`, transform: "translateX(-50%)" }}
        />
        {/* Marker label */}
        <div
          className="absolute -top-1 sans text-[10px] font-bold text-stone-900 bg-sky-300 px-1.5 py-0.5 rounded-sm whitespace-nowrap"
          style={{
            left: `${markerPct}%`,
            transform: "translateX(-50%) translateY(-100%)",
          }}
        >
          You
        </div>
      </div>

      {/* Scale labels — show bracket cutoffs below */}
      <div className="relative h-4 mb-3">
        {segments.map((s, i) => {
          const labelPct = ((s.from + 0) / cap) * 100;
          if (i === 0) return null;
          return (
            <div
              key={i}
              className="absolute sans text-[9px] text-stone-500 mono num-tabular"
              style={{
                left: `${Math.min(labelPct, 95)}%`,
                transform: "translateX(-50%)",
              }}
            >
              ${fmt(s.from)}
            </div>
          );
        })}
      </div>

      {/* Plain-English summary */}
      <div className="bg-sky-50 border border-sky-200 rounded-sm px-4 py-3 sans text-sm text-stone-800 leading-relaxed">
        <strong>Your next dollar of ordinary income is taxed at{" "}
          <span className="text-rose-700">
            {(currentBracket.rate * 100).toFixed(0)}%
          </span>.
        </strong>{" "}
        {nextBracket ? (
          <>
            You're in the <strong>{(currentBracket.rate * 100).toFixed(0)}%
            bracket</strong>, which runs from ${fmt(currentBracket.from)} to{" "}
            ${fmt(nextBracket.from)}. Each dollar above ${fmt(nextBracket.from)}
            {" "}would be taxed at {(nextBracket.rate * 100).toFixed(0)}%.
          </>
        ) : (
          <>You're at the top federal bracket — every additional dollar is taxed at {(currentBracket.rate * 100).toFixed(0)}%.</>
        )}
      </div>

      <div className="sans text-[11px] text-stone-500 mt-2 italic leading-relaxed">
        This shows ordinary income only. Long-term capital gains and qualified
        dividends are taxed separately at 0%, 15%, or 20%.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Multi-Year Projection — 3 simple year cards + cumulative total.
// Editable income-growth slider for the planner; strategies surface their
// "Years of Savings" assumption inline so the client sees what drives Y2/Y3.
// ════════════════════════════════════════════════════════════════════════
function MultiYearProjection({ multiYear, plan, onUpdatePlan, compact = false }) {
  if (!multiYear || !multiYear.years || multiYear.years.length === 0)
    return null;
  const { years, cumulative, incomeGrowth } = multiYear;

  const maxTax = Math.max(...years.map((y) => y.baselineTax), 1);

  const handleGrowthChange = (raw) => {
    if (!onUpdatePlan) return;
    const num = parseFloat(raw);
    if (isNaN(num)) return;
    const pct = Math.max(-50, Math.min(num, 100));
    onUpdatePlan({ ...plan, incomeGrowth: pct / 100 });
  };

  return (
    <div
      className={
        compact
          ? "mb-6 bg-white border border-stone-300 rounded-sm p-5"
          : "mb-10"
      }
    >
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div
            className={`serif font-bold text-stone-900 ${compact ? "text-lg" : "text-2xl"}`}
          >
            {compact ? "Multi-Year Projection" : "3-Year Plan Outlook"}
          </div>
          <div className="sans text-xs text-stone-500 mt-1">
            Lifetime value of this plan, projecting income growth and
            strategies that taper after Year 1.
          </div>
        </div>
        {onUpdatePlan && (
          <div className="flex items-center gap-2 sans text-xs text-stone-600">
            <span className="font-semibold">Income growth:</span>
            <input
              type="number"
              step="0.5"
              value={(incomeGrowth * 100).toFixed(1)}
              onChange={(e) => handleGrowthChange(e.target.value)}
              className="w-16 px-2 py-1 border border-stone-300 rounded-sm mono num-tabular text-sm text-right focus:outline-none focus:border-sky-500"
            />
            <span className="text-stone-500">% / yr</span>
          </div>
        )}
        {!onUpdatePlan && (
          <div className="sans text-xs text-stone-500">
            Income growth: {(incomeGrowth * 100).toFixed(1)}%/yr
          </div>
        )}
      </div>

      {/* Year cards — one row, three columns */}
      <div
        className="grid gap-3 mb-3"
        style={{
          gridTemplateColumns: `repeat(${years.length}, minmax(0, 1fr))`,
        }}
      >
        {years.map((y) => {
          const savePct =
            y.baselineTax > 0 ? (y.savings / y.baselineTax) * 100 : 0;
          return (
            <div
              key={y.yearNumber}
              className="border-2 border-stone-300 rounded-sm bg-white overflow-hidden"
            >
              <div className="bg-stone-100 px-3 py-2 border-b border-stone-200 flex items-baseline justify-between">
                <div className="sans text-[10px] uppercase tracking-[0.2em] font-bold text-stone-700">
                  Year {y.yearNumber}
                </div>
                <div className="sans text-[10px] text-stone-500 mono num-tabular">
                  TY {y.taxYear}
                </div>
              </div>
              <div className="px-3 py-3 space-y-2">
                {/* Baseline */}
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="sans text-[10px] uppercase tracking-wider font-semibold text-stone-500">
                      Baseline
                    </div>
                    <div className="mono num-tabular text-sm font-bold text-rose-700">
                      ${fmt(y.baselineTax)}
                    </div>
                  </div>
                  <div className="h-1.5 bg-rose-100 rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-rose-400"
                      style={{
                        width: `${(y.baselineTax / maxTax) * 100}%`,
                      }}
                    />
                  </div>
                </div>
                {/* Planned */}
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="sans text-[10px] uppercase tracking-wider font-semibold text-stone-500">
                      With plan
                    </div>
                    <div className="mono num-tabular text-sm font-bold text-emerald-700">
                      ${fmt(y.plannedTax)}
                    </div>
                  </div>
                  <div className="h-1.5 bg-emerald-100 rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-emerald-500"
                      style={{
                        width: `${(y.plannedTax / maxTax) * 100}%`,
                      }}
                    />
                  </div>
                </div>
                {/* Saved */}
                <div className="pt-2 border-t border-stone-200 flex items-baseline justify-between">
                  <div className="sans text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                    Saved
                  </div>
                  <div className="text-right">
                    <div className="mono num-tabular text-lg font-bold text-emerald-700 leading-none">
                      ${fmt(y.savings)}
                    </div>
                    <div className="sans text-[10px] text-stone-500 mt-0.5">
                      {savePct.toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Cumulative strip */}
      <div className="bg-gradient-to-r from-stone-900 to-sky-900 text-white rounded-sm px-5 py-3 flex items-baseline justify-between">
        <div>
          <div className="sans text-[10px] uppercase tracking-[0.2em] font-bold text-sky-300 mb-1">
            {years.length}-Year Cumulative Savings
          </div>
          <div className="sans text-[11px] text-stone-300">
            Total dollars kept across the projection window
          </div>
        </div>
        <div className="text-right">
          <div className="mono num-tabular serif text-3xl font-bold text-sky-400 leading-none">
            ${fmt(cumulative.savings)}
          </div>
          <div className="sans text-[11px] text-stone-300 mt-1">
            vs. ${fmt(cumulative.baselineTax)} baseline
          </div>
        </div>
      </div>

      <div className="sans text-[11px] text-stone-500 mt-2 italic leading-relaxed">
        Each strategy carries a "Years of Savings" setting (1 = Y1 only,
        3 = annual). One-time strategies like cost segregation drop out after
        Year 1; recurring strategies like PTE election or retirement
        contributions repeat. Income is projected forward at {(incomeGrowth * 100).toFixed(1)}% per year.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Income Transparency Page — per-entity build of accrual → book→tax adj →
// cash/accrual adjustments → tax taxable income. Cash↔accrual shown in the
// 3-column format the client expects: 12/31 PY · Current · 12/31 Forecast.
// Lets the client see exactly which adjustments drove the tax number.
// ════════════════════════════════════════════════════════════════════════
function IncomeTransparencyPage({ plan, result }) {
  const entities = plan.entities || [];
  if (entities.length === 0) return null;

  const n = (v) => parseFloat(v) || 0;
  const taxYear = plan.taxYear || new Date().getFullYear();
  const priorYear = taxYear - 1;
  const today = new Date();
  const currentMonth = today.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  // Cash↔accrual line definitions — must mirror the entity card section
  const caLines = [
    { key: "AR", label: "Accounts Receivable", priorKey: "priorAR", currKey: "currentAR", yeKey: "yeAR", direction: "minus" },
    { key: "AP", label: "Accounts Payable", priorKey: "priorAP", currKey: "currentAP", yeKey: "yeAP", direction: "plus" },
    { key: "AccruedLiab", label: "Accrued Liabilities", priorKey: "priorAccruedLiab", currKey: "currentAccruedLiab", yeKey: "yeAccruedLiab", direction: "plus" },
    { key: "Prepaid", label: "Prepaid Expenses", priorKey: "priorPrepaid", currKey: "currentPrepaid", yeKey: "yePrepaid", direction: "minus" },
    { key: "ContractAsset", label: "Contract Assets", priorKey: "priorContractAsset", currKey: "currentContractAsset", yeKey: "yeContractAsset", direction: "minus" },
    { key: "ContractLiab", label: "Contract Liabilities (Deferred Revenue)", priorKey: "priorContractLiab", currKey: "currentContractLiab", yeKey: "yeContractLiab", direction: "plus" },
  ];

  // Book-to-tax fixed line definitions
  const btLines = [
    { key: "bookDepreciation", label: "Book Depreciation" },
    { key: "bookAmortization", label: "Book Amortization" },
    { key: "principalPayments", label: "Principal Payments (Expensed)" },
    { key: "mortgagePayments", label: "Mortgage Payments through P&L" },
    { key: "nonDeductibleTax", label: "Non-Deductible Tax Payments" },
  ];

  return (
    <div className="mb-10 income-transparency-page">
      <div className="mb-4">
        <div className="serif text-2xl font-bold text-stone-900">
          Income Calculation Detail
        </div>
        <div className="sans text-xs text-stone-500 mt-1">
          How we got from your books to the tax return. Each entity is built
          accrual income → book-to-tax addbacks → cash/accrual adjustment →
          taxable income.
        </div>
      </div>

      {entities.map((entity, idx) => {
        const er = result.planned?.entities?.[idx];
        if (!er) return null;
        const revenue = n(entity.revenue);
        const expenses = n(entity.expenses);
        const depreciation = n(entity.depreciation);
        const otherDeductions = n(entity.otherDeductions);
        const accrual = revenue - expenses - depreciation - otherDeductions;

        // Book-to-tax addbacks
        const bt = entity.bookToTax || {};
        const btFixedTotal = btLines.reduce((s, l) => s + n(bt[l.key]), 0);
        const btOtherLegacy = n(bt.other);
        const btOtherItems = Array.isArray(bt.otherItems) ? bt.otherItems : [];
        const btOtherTotal = btOtherItems.reduce(
          (s, i) => s + n(i.amount),
          0,
        );
        const totalAddback = btFixedTotal + btOtherLegacy + btOtherTotal;
        const adjustedAccrual = accrual + totalAddback;
        const hasBookToTax = totalAddback !== 0;

        // Cash↔accrual adjustment
        const ca = entity.cashAccrual || {};
        const taxMethod = entity.taxMethod || "accrual";
        const willApply =
          taxMethod === "cash" || ca.appliedAdjustmentOverride !== "";
        const caLineAdj = (line) => {
          const prior = n(ca[line.priorKey]);
          const ye = n(ca[line.yeKey]);
          const curr = n(ca[line.currKey]);
          const ending = ye !== 0 ? ye : curr;
          return line.direction === "minus" ? prior - ending : ending - prior;
        };
        const caUsingForecast = (line) => n(ca[line.yeKey]) !== 0;
        const caCustomLines = Array.isArray(ca.customLines) ? ca.customLines : [];
        const caCustomAdj = (cl) => {
          const prior = n(cl.prior);
          const ye = n(cl.ye);
          const curr = n(cl.current);
          const ending = ye !== 0 ? ye : curr;
          return cl.direction === "liability" ? ending - prior : prior - ending;
        };
        const caCustomUsingForecast = (cl) => n(cl.ye) !== 0;
        const caTotal =
          caLines.reduce((s, l) => s + caLineAdj(l), 0) +
          caCustomLines.reduce((s, cl) => s + caCustomAdj(cl), 0);
        const finalTaxIncome = adjustedAccrual + (willApply ? caTotal : 0);
        const hasCAEntries =
          caLines.some(
            (l) =>
              n(ca[l.priorKey]) !== 0 ||
              n(ca[l.currKey]) !== 0 ||
              n(ca[l.yeKey]) !== 0,
          ) || caCustomLines.length > 0;

        return (
          <div
            key={entity.id}
            className="mb-6 border border-stone-300 rounded-sm overflow-hidden bg-white income-entity-block"
          >
            {/* Entity header */}
            <div className="px-5 py-3 bg-stone-900 text-stone-100">
              <div className="serif text-lg font-bold">
                {entity.name}
              </div>
              <div className="sans text-[11px] uppercase tracking-widest text-stone-400">
                {ENTITY_TYPE_OPTIONS?.find((t) => t.id === entity.type)?.label ||
                  entity.type}
                {entity.ownershipPct != null && (
                  <span className="ml-2">
                    · Owner {n(entity.ownershipPct).toFixed(0)}%
                  </span>
                )}
                <span className="ml-2">
                  · Tax method: {taxMethod}
                </span>
              </div>
            </div>

            {/* Accrual income build */}
            <div className="px-5 py-4 border-b border-stone-200">
              <div className="sans text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-3">
                Accrual Income Build
              </div>
              <div className="space-y-1.5 sans text-sm">
                <div className="flex justify-between">
                  <span className="text-stone-700">Forecast Revenue</span>
                  <span className="mono num-tabular text-stone-900">
                    ${fmt(revenue)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-700">
                    Less: Operating Expenses
                  </span>
                  <span className="mono num-tabular text-rose-700">
                    (${fmt(expenses)})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-700">Less: Depreciation</span>
                  <span className="mono num-tabular text-rose-700">
                    (${fmt(depreciation)})
                  </span>
                </div>
                {otherDeductions > 0 && (
                  <div className="flex justify-between">
                    <span className="text-stone-700">
                      Less: Other Deductions
                    </span>
                    <span className="mono num-tabular text-rose-700">
                      (${fmt(otherDeductions)})
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-1.5 mt-1 border-t border-stone-300 font-semibold">
                  <span className="text-stone-900">Accrual Income</span>
                  <span className="mono num-tabular text-stone-900">
                    ${fmt(accrual)}
                  </span>
                </div>
              </div>
            </div>

            {/* Book-to-tax addbacks */}
            {hasBookToTax && (
              <div className="px-5 py-4 border-b border-stone-200 bg-sky-50/30">
                <div className="sans text-[10px] uppercase tracking-widest font-bold text-sky-700 mb-3">
                  Book → Tax Addbacks
                </div>
                <div className="space-y-1.5 sans text-sm">
                  {btLines.map(
                    (l) =>
                      n(bt[l.key]) !== 0 && (
                        <div key={l.key} className="flex justify-between">
                          <span className="text-stone-700">{l.label}</span>
                          <span className="mono num-tabular text-emerald-700">
                            +${fmt(n(bt[l.key]))}
                          </span>
                        </div>
                      ),
                  )}
                  {btOtherLegacy !== 0 && (
                    <div className="flex justify-between">
                      <span className="text-stone-700">
                        Other {bt.otherNote ? `— ${bt.otherNote}` : ""}
                      </span>
                      <span className="mono num-tabular text-emerald-700">
                        +${fmt(btOtherLegacy)}
                      </span>
                    </div>
                  )}
                  {btOtherItems.map(
                    (it) =>
                      n(it.amount) !== 0 && (
                        <div key={it.id} className="flex justify-between">
                          <span className="text-stone-700">
                            {it.description || "(no description)"}
                          </span>
                          <span className="mono num-tabular text-emerald-700">
                            +${fmt(n(it.amount))}
                          </span>
                        </div>
                      ),
                  )}
                  <div className="flex justify-between pt-1.5 mt-1 border-t border-stone-300 font-semibold">
                    <span className="text-stone-900">
                      Adjusted Accrual Income
                    </span>
                    <span className="mono num-tabular text-stone-900">
                      ${fmt(adjustedAccrual)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Cash↔accrual adjustment table (3-column format) */}
            {hasCAEntries && (
              <div className="px-5 py-4 border-b border-stone-200 bg-emerald-50/20 income-cash-accrual-block">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="sans text-[10px] uppercase tracking-widest font-bold text-emerald-800">
                    Cash ↔ Accrual Adjustment
                  </div>
                  <div className="sans text-[10px] text-stone-500">
                    {willApply
                      ? `Applied (tax method: ${taxMethod})`
                      : "Not applied (accrual entity, no override)"}
                  </div>
                </div>
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-stone-100">
                      <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-left px-3 py-2">
                        Account
                      </th>
                      <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-right px-3 py-2">
                        12/31 {priorYear}
                      </th>
                      <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-right px-3 py-2">
                        {currentMonth}
                      </th>
                      <th className="sans text-[10px] uppercase tracking-widest font-semibold text-sky-700 text-right px-3 py-2 bg-sky-50/60">
                        12/31 {taxYear}
                        <div className="sans text-[8px] font-normal text-sky-600 normal-case tracking-normal mt-0.5">
                          Forecast
                        </div>
                      </th>
                      <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-right px-3 py-2">
                        Adjustment
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {caLines.map((l) => {
                      const prior = n(ca[l.priorKey]);
                      const curr = n(ca[l.currKey]);
                      const ye = n(ca[l.yeKey]);
                      const adj = caLineAdj(l);
                      const usingFc = caUsingForecast(l);
                      // Skip rows that have no values at all
                      if (prior === 0 && curr === 0 && ye === 0) return null;
                      return (
                        <tr key={l.key} className="border-t border-stone-100">
                          <td className="px-3 py-1.5 sans text-stone-700">
                            {l.label}
                          </td>
                          <td className="px-3 py-1.5 text-right mono num-tabular text-stone-800">
                            ${fmt(prior)}
                          </td>
                          <td className="px-3 py-1.5 text-right mono num-tabular text-stone-800">
                            ${fmt(curr)}
                          </td>
                          <td className="px-3 py-1.5 text-right mono num-tabular text-stone-900 bg-sky-50/30 font-semibold">
                            ${fmt(ye)}
                          </td>
                          <td
                            className={`px-3 py-1.5 text-right mono num-tabular font-semibold ${
                              adj > 0
                                ? "text-emerald-700"
                                : adj < 0
                                  ? "text-rose-700"
                                  : "text-stone-500"
                            }`}
                          >
                            {adj === 0
                              ? "—"
                              : `${adj < 0 ? "−" : "+"}$${fmt(Math.abs(adj))}`}
                            {usingFc && adj !== 0 && (
                              <span className="ml-1 sans text-[8px] uppercase tracking-wider font-bold text-sky-600">
                                YE
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {caCustomLines.map((cl) => {
                      const prior = n(cl.prior);
                      const curr = n(cl.current);
                      const ye = n(cl.ye);
                      const adj = caCustomAdj(cl);
                      const usingFc = caCustomUsingForecast(cl);
                      if (prior === 0 && curr === 0 && ye === 0) return null;
                      return (
                        <tr key={cl.id} className="border-t border-stone-100">
                          <td className="px-3 py-1.5 sans text-stone-700">
                            {cl.label || "(unnamed)"}{" "}
                            <span className="text-[10px] text-stone-400 uppercase tracking-wider">
                              {cl.direction === "liability" ? "liab" : "asset"}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-right mono num-tabular text-stone-800">
                            ${fmt(prior)}
                          </td>
                          <td className="px-3 py-1.5 text-right mono num-tabular text-stone-800">
                            ${fmt(curr)}
                          </td>
                          <td className="px-3 py-1.5 text-right mono num-tabular text-stone-900 bg-sky-50/30 font-semibold">
                            ${fmt(ye)}
                          </td>
                          <td
                            className={`px-3 py-1.5 text-right mono num-tabular font-semibold ${
                              adj > 0
                                ? "text-emerald-700"
                                : adj < 0
                                  ? "text-rose-700"
                                  : "text-stone-500"
                            }`}
                          >
                            {adj === 0
                              ? "—"
                              : `${adj < 0 ? "−" : "+"}$${fmt(Math.abs(adj))}`}
                            {usingFc && adj !== 0 && (
                              <span className="ml-1 sans text-[8px] uppercase tracking-wider font-bold text-sky-600">
                                YE
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-stone-50 border-t-2 border-stone-300">
                      <td
                        colSpan={4}
                        className="px-3 py-2 sans text-sm font-semibold text-stone-900 text-right"
                      >
                        Total cash-basis adjustment
                      </td>
                      <td
                        className={`px-3 py-2 text-right mono num-tabular font-bold ${
                          caTotal > 0
                            ? "text-emerald-700"
                            : caTotal < 0
                              ? "text-rose-700"
                              : "text-stone-500"
                        }`}
                      >
                        {caTotal === 0
                          ? "—"
                          : `${caTotal < 0 ? "−" : "+"}$${fmt(Math.abs(caTotal))}`}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Final taxable income */}
            <div className="px-5 py-3 bg-stone-900 text-stone-100 flex items-baseline justify-between">
              <div>
                <div className="sans text-[10px] uppercase tracking-widest font-semibold text-sky-300">
                  Tax Taxable Income
                </div>
                <div className="sans text-[10px] text-stone-400 mt-0.5">
                  Flows to the owner's K-1 at {n(entity.ownershipPct).toFixed(0)}%
                </div>
              </div>
              <div className="mono num-tabular serif text-2xl font-bold text-sky-400">
                ${fmt(finalTaxIncome)}
              </div>
            </div>
          </div>
        );
      })}

      <div className="sans text-[11px] text-stone-500 italic leading-relaxed">
        Each entity's tax taxable income flows to your 1040 as K-1 income at
        your ownership percentage. Pass-through credits and capital gains flow
        separately as shown in the K-1 detail on the personal return.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Strategy ROI — implementation cost vs annual savings, payback, 5-yr net
// ════════════════════════════════════════════════════════════════════════
function StrategyROI({ items }) {
  const withCosts = (items || []).filter(
    (it) =>
      !it.isDerived &&
      ((it.implementationCost || 0) > 0 || (it.recurringCost || 0) > 0),
  );
  if (withCosts.length === 0) return null;

  // Aggregate totals
  const totals = withCosts.reduce(
    (acc, it) => {
      const annualNet = (it.savings || 0) - (it.recurringCost || 0);
      acc.impl += it.implementationCost || 0;
      acc.recurring += it.recurringCost || 0;
      acc.savings += it.savings || 0;
      acc.fiveYrNet += annualNet * 5 - (it.implementationCost || 0);
      return acc;
    },
    { impl: 0, recurring: 0, savings: 0, fiveYrNet: 0 },
  );

  return (
    <div className="mb-10">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="serif text-2xl font-bold text-stone-900">
            Strategy ROI — Cost vs. Savings
          </div>
          <div className="sans text-xs text-stone-500 mt-1">
            What each strategy costs to implement vs. what it returns. Payback
            is implementation cost ÷ first-year net savings.
          </div>
        </div>
      </div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-stone-900 text-sky-400">
            <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">
              Strategy
            </th>
            <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5">
              Implementation Cost
            </th>
            <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5">
              Recurring Cost
            </th>
            <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5">
              Annual Savings
            </th>
            <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5">
              Payback
            </th>
            <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5">
              5-Year Net
            </th>
          </tr>
        </thead>
        <tbody>
          {withCosts.map((it, i) => {
            const annualNet = (it.savings || 0) - (it.recurringCost || 0);
            const payback =
              annualNet > 0 && (it.implementationCost || 0) > 0
                ? it.implementationCost / annualNet
                : it.implementationCost > 0
                  ? Infinity
                  : 0;
            const fiveYr = annualNet * 5 - (it.implementationCost || 0);
            return (
              <tr
                key={i}
                className={`border-b border-stone-200 ${i % 2 === 0 ? "bg-white" : "bg-stone-50/50"}`}
              >
                <td className="px-3 py-3 sans text-sm font-semibold text-stone-900">
                  {it.strategy.name}
                </td>
                <td className="px-3 py-3 text-right mono num-tabular text-sm text-rose-700">
                  {it.implementationCost > 0 ? `$${fmt(it.implementationCost)}` : "—"}
                </td>
                <td className="px-3 py-3 text-right mono num-tabular text-sm text-rose-600">
                  {it.recurringCost > 0
                    ? `$${fmt(it.recurringCost)}/yr`
                    : "—"}
                </td>
                <td className="px-3 py-3 text-right mono num-tabular text-sm font-bold text-emerald-700">
                  ${fmt(it.savings || 0)}
                </td>
                <td className="px-3 py-3 text-right mono num-tabular text-sm text-stone-800">
                  {payback === 0
                    ? "Instant"
                    : payback === Infinity
                      ? "N/A"
                      : payback < 1
                        ? "< 1 yr"
                        : `${payback.toFixed(1)} yrs`}
                </td>
                <td
                  className={`px-3 py-3 text-right mono num-tabular text-sm font-bold ${fiveYr >= 0 ? "text-emerald-700" : "text-rose-700"}`}
                >
                  {fiveYr < 0 ? "−" : ""}${fmt(Math.abs(fiveYr))}
                </td>
              </tr>
            );
          })}
          <tr className="bg-stone-900 text-stone-100">
            <td className="px-3 py-3 sans text-sm font-bold">Plan total</td>
            <td className="px-3 py-3 text-right mono num-tabular text-sm font-bold text-rose-300">
              ${fmt(totals.impl)}
            </td>
            <td className="px-3 py-3 text-right mono num-tabular text-sm text-rose-300">
              ${fmt(totals.recurring)}/yr
            </td>
            <td className="px-3 py-3 text-right mono num-tabular text-sm font-bold text-emerald-400">
              ${fmt(totals.savings)}
            </td>
            <td className="px-3 py-3 text-right"></td>
            <td
              className={`px-3 py-3 text-right serif text-lg font-bold mono num-tabular ${totals.fiveYrNet >= 0 ? "text-sky-400" : "text-rose-300"}`}
            >
              {totals.fiveYrNet < 0 ? "−" : ""}${fmt(Math.abs(totals.fiveYrNet))}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="sans text-[11px] text-stone-500 mt-2 italic leading-relaxed">
        5-Year Net = (Annual Savings − Recurring Cost) × 5 years − Implementation
        Cost. Strategies with payback under 12 months are typically no-brainers;
        anything beyond 3 years warrants a multi-year planning conversation.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Estimated Tax & Quarterly Payment Schedule (Form 1040-ES)
// ════════════════════════════════════════════════════════════════════════
function EstimatedTaxSchedule({ estimatedTax, taxYear, plannedTotalTax }) {
  if (!estimatedTax) return null;
  const {
    safeHarborAmount,
    safeHarborBasis,
    priorYearSafeHarbor,
    currentYearSafeHarbor,
    hasPriorYear,
    withholding,
    paymentsMade,
    netRequired,
    perQuarter,
    quarterly,
    balanceDueAtFiling,
    penaltyExposure,
  } = estimatedTax;

  const fmtDate = (d) =>
    d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  return (
    <div className="mb-10">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="serif text-2xl font-bold text-stone-900">
            Estimated Tax & Quarterly Payment Schedule
          </div>
          <div className="sans text-xs text-stone-500 mt-1">
            Pay these amounts by these dates to satisfy §6654 safe harbor and
            avoid underpayment penalty.
          </div>
        </div>
      </div>

      {!hasPriorYear && (
        <div className="mb-4 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-sm sans text-xs text-amber-900">
          <strong>Note:</strong> Prior-year tax not yet entered. The schedule
          below uses the 90% of current-year rule as a fallback. For a tighter
          safe-harbor target, enter Prior-Year Total Tax and AGI on the 1040.
        </div>
      )}

      {/* Safe harbor summary */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="border border-stone-300 rounded-sm p-3 bg-white">
          <div className="sans text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">
            Safe Harbor Target
          </div>
          <div className="mono num-tabular text-xl font-bold text-stone-900">
            ${fmt(safeHarborAmount)}
          </div>
          <div className="sans text-[10px] text-stone-500 mt-1 leading-snug">
            {safeHarborBasis}
          </div>
        </div>
        <div className="border border-stone-300 rounded-sm p-3 bg-white">
          <div className="sans text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">
            Withholding
          </div>
          <div className="mono num-tabular text-xl font-bold text-stone-900">
            − ${fmt(withholding)}
          </div>
          <div className="sans text-[10px] text-stone-500 mt-1 leading-snug">
            W-2 + 1099 withheld
          </div>
        </div>
        <div className="border border-stone-300 rounded-sm p-3 bg-white">
          <div className="sans text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">
            Already Paid
          </div>
          <div className="mono num-tabular text-xl font-bold text-stone-900">
            − ${fmt(paymentsMade)}
          </div>
          <div className="sans text-[10px] text-stone-500 mt-1 leading-snug">
            Estimated payments YTD
          </div>
        </div>
        <div className="border-2 border-emerald-500 rounded-sm p-3 bg-emerald-50">
          <div className="sans text-[10px] uppercase tracking-widest font-bold text-emerald-800 mb-1">
            Net Still Required
          </div>
          <div className="mono num-tabular text-xl font-bold text-emerald-700">
            ${fmt(netRequired)}
          </div>
          <div className="sans text-[10px] text-emerald-700 mt-1 leading-snug font-semibold">
            ${fmt(perQuarter)} × 4 quarters
          </div>
        </div>
      </div>

      {/* Quarterly payment table */}
      <table className="w-full border-collapse mb-3">
        <thead>
          <tr className="bg-stone-900 text-sky-400">
            <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5 w-[20%]">
              Quarter
            </th>
            <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5 w-[40%]">
              Due Date
            </th>
            <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5 w-[20%]">
              Payment
            </th>
            <th className="sans text-[10px] font-semibold uppercase tracking-widest text-center px-3 py-2.5 w-[20%]">
              Method
            </th>
          </tr>
        </thead>
        <tbody>
          {quarterly.map((q, i) => (
            <tr
              key={q.quarter}
              className={`border-b border-stone-200 ${i % 2 === 0 ? "bg-white" : "bg-stone-50/50"}`}
            >
              <td className="px-3 py-2.5 sans text-sm font-semibold text-stone-900">
                {q.label}
              </td>
              <td className="px-3 py-2.5 sans text-sm text-stone-700">
                {fmtDate(q.due)}
              </td>
              <td className="px-3 py-2.5 text-right mono num-tabular text-base font-bold text-emerald-700">
                ${fmt(q.amount)}
              </td>
              <td className="px-3 py-2.5 text-center sans text-[11px] text-stone-500">
                Form 1040-ES · IRS Direct Pay
              </td>
            </tr>
          ))}
          {balanceDueAtFiling > 0 && (
            <tr className="bg-amber-50 border-b border-amber-200">
              <td className="px-3 py-2.5 sans text-sm font-semibold text-amber-900">
                Final
              </td>
              <td className="px-3 py-2.5 sans text-sm text-amber-900">
                April 15, {(taxYear || new Date().getFullYear()) + 1} (with 1040)
              </td>
              <td className="px-3 py-2.5 text-right mono num-tabular text-base font-bold text-amber-700">
                ${fmt(balanceDueAtFiling)}
              </td>
              <td className="px-3 py-2.5 text-center sans text-[11px] text-amber-700">
                Balance due with return
              </td>
            </tr>
          )}
          <tr className="bg-stone-900 text-stone-100">
            <td colSpan={2} className="px-3 py-3 sans text-sm font-bold">
              Total Federal Tax Owed (projected)
            </td>
            <td className="px-3 py-3 text-right serif text-xl font-bold text-sky-400 mono num-tabular">
              ${fmt(plannedTotalTax)}
            </td>
            <td className="px-3 py-3"></td>
          </tr>
        </tbody>
      </table>

      <div className="sans text-[11px] text-stone-500 italic leading-relaxed">
        Safe harbor is the LESSER of {hasPriorYear ? "110% (or 100%) of prior-year tax" : "(not available — enter prior year)"}
        {hasPriorYear && " and "}90% of current-year projected tax. Paying these
        amounts on time prevents the §6654 underpayment penalty even if final
        liability runs higher.
        {penaltyExposure &&
          " ⚠ Current inputs suggest a shortfall against safe harbor — adjust withholding or quarterly amounts."}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Year-End Action Punch List — client-friendly checklist sorted by deadline
// ════════════════════════════════════════════════════════════════════════
function YearEndChecklist({ items, estimatedTax, taxYear, fmtDeadline }) {
  // Combine strategy deadlines + quarterly estimated tax payments
  const today = new Date();
  const tasks = [];

  for (const item of items || []) {
    if (item.isDerived) continue;
    tasks.push({
      kind: "strategy",
      label: item.strategy.name,
      detail: item.timingNote || `Applied to ${item.host}`,
      deadlineDate: item.deadline,
      deadlineText: fmtDeadline ? fmtDeadline(item) : "Flexible",
    });
  }
  if (estimatedTax) {
    for (const q of estimatedTax.quarterly || []) {
      if (q.amount <= 0) continue;
      tasks.push({
        kind: "esttax",
        label: `Estimated Tax — ${q.label}`,
        detail: `Pay $${fmt(q.amount)} via Form 1040-ES or IRS Direct Pay`,
        deadlineDate: q.due,
        deadlineText: q.due.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
        amount: q.amount,
      });
    }
    if (estimatedTax.balanceDueAtFiling > 0) {
      tasks.push({
        kind: "filing",
        label: "File 1040 + Pay Balance Due",
        detail: `Final balance of $${fmt(estimatedTax.balanceDueAtFiling)} with the return`,
        deadlineDate: new Date((taxYear || today.getFullYear()) + 1, 3, 15),
        deadlineText: `April 15, ${(taxYear || today.getFullYear()) + 1}`,
      });
    }
  }

  // Sort by deadline (items without a date sink to the bottom)
  tasks.sort((a, b) => {
    if (a.deadlineDate && b.deadlineDate)
      return a.deadlineDate - b.deadlineDate;
    if (a.deadlineDate) return -1;
    if (b.deadlineDate) return 1;
    return 0;
  });

  if (tasks.length === 0) return null;

  const kindIcon = {
    strategy: "○",
    esttax: "$",
    filing: "✓",
  };
  const kindColor = {
    strategy: "border-sky-300 bg-sky-50/40",
    esttax: "border-emerald-300 bg-emerald-50/40",
    filing: "border-amber-300 bg-amber-50/40",
  };

  return (
    <div className="mb-10">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="serif text-2xl font-bold text-stone-900">
            Action Items & Deadlines
          </div>
          <div className="sans text-xs text-stone-500 mt-1">
            Your at-a-glance checklist. Sorted by deadline.
          </div>
        </div>
        <div className="sans text-xs text-stone-500">
          {tasks.length} item{tasks.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="border border-stone-300 rounded-sm bg-white divide-y divide-stone-100">
        {tasks.map((t, i) => {
          const overdue = t.deadlineDate && t.deadlineDate < today;
          return (
            <div
              key={i}
              className={`flex items-start gap-3 px-4 py-3 border-l-4 ${kindColor[t.kind]}`}
            >
              <div className="w-5 h-5 mt-0.5 border-2 border-stone-400 rounded-sm shrink-0 flex items-center justify-center sans text-[10px] font-bold text-stone-500">
                {kindIcon[t.kind]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="sans text-sm font-bold text-stone-900 leading-snug">
                  {t.label}
                </div>
                <div className="sans text-xs text-stone-600 mt-0.5 leading-snug">
                  {t.detail}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div
                  className={`sans text-xs font-bold ${overdue ? "text-rose-700" : "text-stone-800"}`}
                >
                  {t.deadlineText}
                </div>
                {overdue && (
                  <div className="sans text-[10px] uppercase tracking-wider font-bold text-rose-700">
                    Past due
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="sans text-[11px] text-stone-500 mt-2 italic">
        Check items off as they're completed. Linked Accounting LLP can execute
        any of the above on your behalf with appropriate engagement.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Compounding Value — what the savings become if invested
// ════════════════════════════════════════════════════════════════════════
function CompoundingValueSection({
  annualSavings,
  rate,
  years,
  onRateChange,
  onYearsChange,
}) {
  if (!annualSavings || annualSavings <= 0) return null;

  // Lump sum: this year's savings, invested today, growing for N years
  const lumpSumFV = annualSavings * Math.pow(1 + rate, years);

  // Annuity: same savings every year for N years, each year invested at end of year
  const annuityFV =
    rate === 0
      ? annualSavings * years
      : annualSavings * ((Math.pow(1 + rate, years) - 1) / rate);
  const totalContributed = annualSavings * years;
  const compoundedGain = annuityFV - totalContributed;

  // Per-year cumulative for the bar chart
  const yearly = [];
  for (let y = 1; y <= years; y++) {
    const annuityVal =
      rate === 0
        ? annualSavings * y
        : annualSavings * ((Math.pow(1 + rate, y) - 1) / rate);
    const lumpVal = annualSavings * Math.pow(1 + rate, y);
    yearly.push({ year: y, annuity: annuityVal, lump: lumpVal });
  }
  const chartMax = Math.max(...yearly.map((d) => d.annuity), 1);
  const chartHeight = 180;

  const formatCompact = (n) => {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${Math.round(n / 1000)}K`;
    return `$${Math.round(n)}`;
  };

  return (
    <div className="mb-10">
      <div className="serif text-2xl font-bold text-stone-900 mb-2">
        The Compounding Value of These Savings
      </div>
      <div className="sans text-sm text-stone-600 mb-5 leading-relaxed">
        Tax dollars saved aren't just dollars saved today — invested over time, they
        become meaningfully larger. Here's what your savings grow into at different
        return assumptions.
      </div>

      {/* Controls — visible on screen, hidden on print so the chosen values bake in */}
      <div className="flex items-end gap-3 mb-5 no-print">
        <div className="flex-1 max-w-[220px]">
          <Field label="Assumed Annual Return">
            <Select
              value={rate}
              onChange={(v) => onRateChange(parseFloat(v))}
              options={[
                { id: 0.04, label: "4% — Conservative bonds" },
                { id: 0.05, label: "5% — Cash + bonds" },
                { id: 0.06, label: "6% — Balanced" },
                { id: 0.07, label: "7% — S&P historical avg" },
                { id: 0.08, label: "8% — Equity-tilted" },
                { id: 0.10, label: "10% — Aggressive equity" },
                { id: 0.12, label: "12% — Real estate / PE" },
              ]}
            />
          </Field>
        </div>
        <div className="flex-1 max-w-[160px]">
          <Field label="Time Horizon">
            <Select
              value={years}
              onChange={(v) => onYearsChange(parseInt(v, 10))}
              options={[
                { id: 5, label: "5 years" },
                { id: 10, label: "10 years" },
                { id: 15, label: "15 years" },
                { id: 20, label: "20 years" },
                { id: 25, label: "25 years" },
                { id: 30, label: "30 years" },
              ]}
            />
          </Field>
        </div>
        <div className="sans text-[11px] text-stone-500 pb-2">
          (controls hidden on print)
        </div>
      </div>

      {/* Two scenarios side by side */}
      <div className="grid grid-cols-2 gap-4 mb-5">
        {/* Lump sum */}
        <div className="bg-stone-50 border border-stone-200 rounded-sm p-5">
          <div className="sans text-[11px] uppercase tracking-widest font-semibold text-stone-500 mb-1">
            If You Invest This Year's Savings
          </div>
          <div className="sans text-xs text-stone-600 mb-3 leading-snug">
            ${fmt(annualSavings)} invested today, growing at {fmtPct(rate, 0)} for{" "}
            {years} years
          </div>
          <div className="serif text-4xl font-bold text-emerald-700 mono num-tabular leading-none">
            ${fmt(lumpSumFV)}
          </div>
          <div className="sans text-xs text-stone-600 mt-3">
            Compound gain:{" "}
            <span className="font-semibold text-emerald-700 mono num-tabular">
              +${fmt(lumpSumFV - annualSavings)}
            </span>
          </div>
        </div>

        {/* Annuity — the headline */}
        <div className="bg-gradient-to-br from-stone-900 via-stone-900 to-sky-900 text-white p-5 rounded-sm">
          <div className="sans text-[11px] uppercase tracking-widest font-semibold text-sky-300 mb-1">
            If We Save You This Every Year
          </div>
          <div className="sans text-xs text-sky-200 mb-3 leading-snug">
            ${fmt(annualSavings)}/yr × {years} yrs at {fmtPct(rate, 0)} (recurring planning)
          </div>
          <div className="serif text-4xl font-bold text-sky-400 mono num-tabular leading-none">
            ${fmt(annuityFV)}
          </div>
          <div className="sans text-xs text-stone-300 mt-3 leading-snug">
            <span className="mono num-tabular">${fmt(totalContributed)}</span> contributed
            <span className="mx-1.5">·</span>
            <span className="font-semibold text-emerald-400 mono num-tabular">
              +${fmt(compoundedGain)}
            </span>{" "}
            from compounding
          </div>
        </div>
      </div>

      {/* Year-by-year bar chart (recurring scenario) */}
      <div className="bg-stone-50 border border-stone-200 rounded-sm p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div className="sans text-[11px] uppercase tracking-widest font-semibold text-stone-500">
            Cumulative Value Over Time
          </div>
          <div className="sans text-[10px] text-stone-500">
            Annual savings reinvested at {fmtPct(rate, 0)}
          </div>
        </div>
        <div
          className="flex items-end gap-1.5 mb-1"
          style={{ height: `${chartHeight + 28}px` }}
        >
          {yearly.map(({ year, annuity }) => (
            <div
              key={year}
              className="flex-1 flex flex-col items-center justify-end"
            >
              <div className="sans text-[9px] mono num-tabular text-stone-600 mb-0.5">
                {formatCompact(annuity)}
              </div>
              <div
                className="w-full bg-gradient-to-t from-sky-300 to-sky-500 border-t-2 border-sky-600 rounded-t-sm"
                style={{
                  height: `${Math.max(2, (annuity / chartMax) * chartHeight)}px`,
                }}
              />
            </div>
          ))}
        </div>
        <div className="flex gap-1.5">
          {yearly.map(({ year }) => (
            <div
              key={year}
              className="flex-1 sans text-[10px] text-stone-500 text-center mono num-tabular"
            >
              {year}
            </div>
          ))}
        </div>
        <div className="sans text-[10px] text-stone-500 text-center mt-1 uppercase tracking-wider font-semibold">
          Years
        </div>
      </div>

      <div className="sans text-[11px] text-stone-500 mt-3 italic leading-relaxed">
        Compounding figures assume annual savings reinvested at year-end and held
        through the time horizon. Actual outcomes vary with market returns, fees,
        and tax drag on investment income. Past performance does not guarantee
        future results.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Strategy Picker Modal — multi-select
// ════════════════════════════════════════════════════════════════════════
function StrategyPicker({
  host,
  alreadyAppliedIds = new Set(),
  combinedMarginalRate = 0,
  customStrategies = [],
  onSaveCustomStrategy,
  onDeleteCustomStrategy,
  onPickMany,
  onClose,
}) {
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterGoal, setFilterGoal] = useState("all");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [editingCustom, setEditingCustom] = useState(null); // null | "new" | strategyObject

  // Merged list = built-ins + custom. customStrategies is passed from parent
  // so the picker reflects deletes/edits without unmounting.
  const allStrategies = useMemo(
    () => [...STRATEGIES, ...customStrategies],
    [customStrategies],
  );

  const customIdSet = useMemo(
    () => new Set(customStrategies.map((s) => s.id)),
    [customStrategies],
  );

  const filtered = useMemo(() => {
    return allStrategies.filter((s) => {
      if (filterCategory !== "all" && !(s.categories || []).includes(filterCategory))
        return false;
      if (filterGoal !== "all" && !(s.goals || []).includes(filterGoal)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !(s.name || "").toLowerCase().includes(q) &&
          !(s.shortDescription || "").toLowerCase().includes(q) &&
          !(s.citation || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [allStrategies, search, filterCategory, filterGoal]);

  const toggleSelection = (id) => {
    if (alreadyAppliedIds.has(id)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const s of filtered) {
        if (!alreadyAppliedIds.has(s.id)) next.add(s.id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleAddSelected = () => {
    if (selectedIds.size === 0) return;
    onPickMany(Array.from(selectedIds));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-sm shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-stone-300 flex items-center justify-between bg-gradient-to-r from-stone-900 to-sky-900">
          <div>
            <div className="serif text-xl font-bold text-white">Select Strategies</div>
            <div className="sans text-xs text-sky-200 mt-0.5">
              Check any number — they all add together. Applying to:{" "}
              {host.kind === "entity" ? "Business Entity" : "Personal Return (1040)"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditingCustom("new")}
              className="flex items-center gap-1.5 bg-sky-400 text-stone-900 px-3 py-1.5 rounded-sm sans text-xs font-bold hover:bg-sky-300"
              title="Create a custom strategy that all firm users will see"
            >
              <Plus size={14} />
              New Custom Strategy
            </button>
            <button onClick={onClose} className="text-white p-1">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-stone-200 flex items-center gap-3 bg-stone-50">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search strategies, citations, descriptions..."
              className="w-full pl-9 pr-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
            />
          </div>
          <Select
            value={filterCategory}
            onChange={setFilterCategory}
            options={[
              { id: "all", label: "All Categories" },
              ...CATEGORIES.map((c) => ({ id: c.id, label: c.label })),
            ]}
            className="!w-52"
          />
          <Select
            value={filterGoal}
            onChange={setFilterGoal}
            options={[
              { id: "all", label: "All Goals" },
              ...GOALS.map((g) => ({ id: g.id, label: g.label })),
            ]}
            className="!w-52"
          />
          <button
            onClick={selectAllVisible}
            className="sans text-xs text-sky-700 hover:text-sky-900 font-semibold whitespace-nowrap"
          >
            Select all visible
          </button>
        </div>

        {/* Results grouped by goal */}
        <div className="overflow-auto flex-1 px-6 py-4">
          {filtered.length === 0 ? (
            <div className="text-center py-10 sans text-sm text-stone-500">
              No strategies match your filters.
            </div>
          ) : (
            GOALS.map((goal) => {
              const inGoal = filtered.filter((s) => s.goals.includes(goal.id));
              if (inGoal.length === 0) return null;
              return (
                <div key={goal.id} className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        goal.color === "emerald"
                          ? "bg-emerald-500"
                          : goal.color === "sky"
                            ? "bg-sky-500"
                            : "bg-amber-500"
                      }`}
                    />
                    <div className="serif text-lg font-bold text-stone-900">{goal.label}</div>
                    <div className="sans text-xs text-stone-500">{goal.description}</div>
                    <div className="sans text-xs text-stone-400 ml-auto">
                      {inGoal.length} strategies
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {inGoal.map((s) => {
                      const isApplied = alreadyAppliedIds.has(s.id);
                      const isSelected = selectedIds.has(s.id);
                      const isCustom = customIdSet.has(s.id);
                      return (
                        <div
                          key={s.id}
                          className={`relative group border rounded-sm transition-colors ${
                            isApplied
                              ? "border-stone-200 bg-stone-100 opacity-60"
                              : isSelected
                                ? "border-emerald-500 bg-emerald-50"
                                : isCustom
                                  ? "border-sky-300 bg-sky-50/30 hover:border-sky-500"
                                  : "border-stone-200 hover:border-sky-400 hover:bg-sky-50/50"
                          }`}
                        >
                          <button
                            onClick={() => toggleSelection(s.id)}
                            disabled={isApplied}
                            className="text-left p-3 w-full flex items-start gap-2"
                          >
                            <div
                              className={`mt-0.5 w-5 h-5 rounded-sm flex items-center justify-center shrink-0 ${
                                isApplied
                                  ? "bg-stone-300 text-stone-500"
                                  : isSelected
                                    ? "bg-emerald-500 text-white"
                                    : "bg-white border border-stone-300"
                              }`}
                            >
                              {(isSelected || isApplied) && (
                                <Check size={12} strokeWidth={3} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="sans text-sm font-semibold text-stone-900 flex items-center gap-1.5 flex-wrap">
                                {(s.clientBrief || s.detailTemplate) && (
                                  <Star
                                    size={13}
                                    className="fill-amber-400 text-amber-500 shrink-0"
                                  />
                                )}
                                {s.name}
                                {(s.clientBrief || s.detailTemplate) && (
                                  <span className="sans text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 flex items-center gap-0.5">
                                    Brief
                                  </span>
                                )}
                                {isCustom && (
                                  <span className="sans text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm bg-sky-500 text-white">
                                    Custom
                                  </span>
                                )}
                                {isApplied && (
                                  <span className="sans text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm bg-stone-300 text-stone-700">
                                    Already Applied
                                  </span>
                                )}
                              </div>
                              <div className="sans text-[11px] text-stone-500 mt-0.5">
                                {s.citation || "—"} ·{" "}
                                {(s.categories || [])
                                  .map(
                                    (c) =>
                                      CATEGORIES.find((x) => x.id === c)?.label ||
                                      c,
                                  )
                                  .join(", ")}
                              </div>
                              <div className="sans text-xs text-stone-700 mt-1.5 line-clamp-2">
                                {s.shortDescription}
                              </div>
                            </div>
                          </button>
                          {isCustom && (
                            <div className="absolute top-1 right-1 hidden group-hover:flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingCustom(s);
                                }}
                                className="p-1 bg-white border border-stone-300 rounded-sm hover:border-sky-500 text-stone-600 hover:text-sky-700"
                                title="Edit this custom strategy"
                              >
                                <FileText size={12} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (
                                    confirm(
                                      `Delete custom strategy "${s.name}"? This affects all firm users.`,
                                    )
                                  ) {
                                    onDeleteCustomStrategy(s.id);
                                  }
                                }}
                                className="p-1 bg-white border border-stone-300 rounded-sm hover:border-rose-500 text-stone-600 hover:text-rose-700"
                                title="Delete this custom strategy"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Custom Strategy Editor (mounts on top of picker) */}
        {editingCustom && (
          <CustomStrategyEditor
            initial={editingCustom === "new" ? null : editingCustom}
            onSave={async (strat) => {
              await onSaveCustomStrategy(strat);
              setEditingCustom(null);
            }}
            onCancel={() => setEditingCustom(null)}
          />
        )}

        {/* Footer with Add Selected */}
        <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex items-center justify-between">
          <div className="sans text-sm text-stone-700">
            <strong className="text-stone-900">{selectedIds.size}</strong> selected
            {selectedIds.size > 0 && (
              <button
                onClick={clearSelection}
                className="ml-3 text-stone-500 hover:text-stone-900 sans text-xs underline"
              >
                Clear
              </button>
            )}
            {combinedMarginalRate > 0 && (
              <span className="ml-3 text-stone-500 text-xs">
                · Combined marginal rate {fmtPct(combinedMarginalRate, 1)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 sans text-sm text-stone-600 hover:text-stone-900"
            >
              Cancel
            </button>
            <button
              onClick={handleAddSelected}
              disabled={selectedIds.size === 0}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-sm sans text-sm font-semibold ${
                selectedIds.size === 0
                  ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                  : "bg-stone-900 text-sky-400 hover:bg-stone-800"
              }`}
            >
              <Plus size={14} />
              Add {selectedIds.size} {selectedIds.size === 1 ? "Strategy" : "Strategies"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Strategy Bank — browse the full library of strategies, see differences
// at a glance (tier, category, goal, brief availability), click for details
// ════════════════════════════════════════════════════════════════════════
function StrategyBank({ customStrategies = [], onOpenBrief }) {
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterGoal, setFilterGoal] = useState("all");
  const [filterTier, setFilterTier] = useState("all");
  const [briefsOnly, setBriefsOnly] = useState(false);
  const [groupBy, setGroupBy] = useState("goal"); // "goal" | "tier" | "category" | "none"

  const allStrategies = useMemo(
    () => [...STRATEGIES, ...customStrategies],
    [customStrategies],
  );

  const briefCount = useMemo(
    () =>
      allStrategies.filter((s) => !!s.clientBrief || !!s.detailTemplate).length,
    [allStrategies],
  );

  const customIdSet = useMemo(
    () => new Set(customStrategies.map((s) => s.id)),
    [customStrategies],
  );

  const filtered = useMemo(() => {
    return allStrategies.filter((s) => {
      if (filterCategory !== "all" && !(s.categories || []).includes(filterCategory))
        return false;
      if (filterGoal !== "all" && !(s.goals || []).includes(filterGoal)) return false;
      if (filterTier !== "all" && String(s.complexity || 2) !== filterTier)
        return false;
      if (briefsOnly && !s.clientBrief && !s.detailTemplate) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !(s.name || "").toLowerCase().includes(q) &&
          !(s.shortDescription || "").toLowerCase().includes(q) &&
          !(s.citation || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [allStrategies, search, filterCategory, filterGoal, filterTier, briefsOnly]);

  // Build groups based on groupBy axis
  const groups = useMemo(() => {
    if (groupBy === "none") return [{ label: null, items: filtered }];
    if (groupBy === "tier") {
      return [
        { label: "Tier 1 — Foundational", color: "emerald", items: filtered.filter((s) => (s.complexity || 2) === 1) },
        { label: "Tier 2 — Intermediate", color: "sky", items: filtered.filter((s) => (s.complexity || 2) === 2) },
        { label: "Tier 3 — Advanced", color: "amber", items: filtered.filter((s) => (s.complexity || 2) === 3) },
      ].filter((g) => g.items.length > 0);
    }
    if (groupBy === "category") {
      return CATEGORIES.map((c) => ({
        label: c.label,
        items: filtered.filter((s) => (s.categories || []).includes(c.id)),
      })).filter((g) => g.items.length > 0);
    }
    // default: by goal (eliminate → reduce → defer)
    return GOALS.map((g) => ({
      label: g.label,
      color: g.color,
      description: g.description,
      items: filtered.filter((s) => (s.goals || []).includes(g.id)),
    })).filter((g) => g.items.length > 0);
  }, [filtered, groupBy]);

  return (
    <main className="max-w-[1400px] mx-auto px-6 py-6">
      <div className="bg-white border border-stone-300 rounded-sm shadow-sm flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-300 bg-gradient-to-r from-stone-900 to-sky-900 rounded-t-sm">
          <div className="serif text-2xl font-bold text-white flex items-center gap-2">
            <Library size={22} className="text-sky-300" />
            Strategy Bank
          </div>
          <div className="sans text-sm text-sky-200 mt-1">
            Every strategy the firm runs — browse, compare, and click for the
            full client brief
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-stone-200 flex items-center gap-2 bg-stone-50 flex-wrap">
          <div className="flex-1 min-w-[220px] relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, citation, description..."
              className="w-full pl-9 pr-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
            />
          </div>
          <Select
            value={filterCategory}
            onChange={setFilterCategory}
            options={[
              { id: "all", label: "All Categories" },
              ...CATEGORIES.map((c) => ({ id: c.id, label: c.label })),
            ]}
            className="!w-48"
          />
          <Select
            value={filterGoal}
            onChange={setFilterGoal}
            options={[
              { id: "all", label: "All Goals" },
              ...GOALS.map((g) => ({ id: g.id, label: g.label })),
            ]}
            className="!w-44"
          />
          <Select
            value={filterTier}
            onChange={setFilterTier}
            options={[
              { id: "all", label: "All Tiers" },
              { id: "1", label: "Tier 1 — Foundational" },
              { id: "2", label: "Tier 2 — Intermediate" },
              { id: "3", label: "Tier 3 — Advanced" },
            ]}
            className="!w-48"
          />
          <button
            onClick={() => setBriefsOnly(!briefsOnly)}
            className={`flex items-center gap-1.5 px-3 py-1.5 sans text-sm font-semibold rounded-sm border transition-colors ${
              briefsOnly
                ? "bg-amber-100 border-amber-400 text-amber-900"
                : "bg-white border-stone-300 text-stone-600 hover:border-stone-400"
            }`}
            title="Show only strategies that have a detailed client brief available"
          >
            <Star
              size={14}
              className={briefsOnly ? "fill-amber-500 text-amber-600" : ""}
            />
            Briefs only
            <span
              className={`sans text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm num-tabular ${
                briefsOnly
                  ? "bg-amber-500 text-white"
                  : "bg-stone-100 text-stone-700"
              }`}
            >
              {briefCount}
            </span>
          </button>
          <div className="flex items-center gap-1 border border-stone-300 rounded-sm bg-white">
            <span className="sans text-[10px] uppercase tracking-wider text-stone-500 font-semibold pl-2">
              Group by
            </span>
            {[
              { id: "goal", label: "Goal" },
              { id: "tier", label: "Tier" },
              { id: "category", label: "Category" },
              { id: "none", label: "None" },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setGroupBy(opt.id)}
                className={`sans text-xs px-2 py-1.5 ${
                  groupBy === opt.id
                    ? "bg-stone-900 text-sky-400 font-semibold"
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <div className="px-6 py-2 border-b border-stone-200 sans text-xs text-stone-500 bg-stone-50/60">
          Showing <strong className="text-stone-900">{filtered.length}</strong> of{" "}
          {allStrategies.length} strategies
          {customStrategies.length > 0 && (
            <span>
              {" "}
              · {customStrategies.length} custom
            </span>
          )}
        </div>

        {/* Grouped grid */}
        <div className="overflow-auto flex-1 px-6 py-4">
          {filtered.length === 0 ? (
            <div className="text-center py-12 sans text-sm text-stone-500">
              No strategies match your filters.
            </div>
          ) : (
            groups.map((group, gi) => (
              <div key={gi} className="mb-6 last:mb-0">
                {group.label && (
                  <div className="flex items-center gap-2 mb-3 pb-1 border-b border-stone-200">
                    {group.color && (
                      <div
                        className={`w-2 h-2 rounded-full ${
                          group.color === "emerald"
                            ? "bg-emerald-500"
                            : group.color === "sky"
                              ? "bg-sky-500"
                              : "bg-amber-500"
                        }`}
                      />
                    )}
                    <div className="serif text-lg font-bold text-stone-900">
                      {group.label}
                    </div>
                    {group.description && (
                      <div className="sans text-xs text-stone-500">
                        {group.description}
                      </div>
                    )}
                    <div className="sans text-xs text-stone-400 ml-auto">
                      {group.items.length}{" "}
                      {group.items.length === 1 ? "strategy" : "strategies"}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-3">
                  {group.items.map((s) => (
                    <StrategyBankCard
                      key={s.id}
                      strategy={s}
                      isCustom={customIdSet.has(s.id)}
                      onOpenBrief={onOpenBrief}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 sans text-xs text-stone-500 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Tier 1
            Foundational
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-sky-500" /> Tier 2
            Intermediate
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-amber-500" /> Tier 3
            Advanced
          </span>
          <span className="flex items-center gap-1.5 ml-3 pl-3 border-l border-stone-300">
            <Star size={12} className="fill-amber-400 text-amber-500" />
            Has detailed client brief ({briefCount})
          </span>
          <span className="ml-auto italic">
            Click any card to open the full client brief
          </span>
        </div>
      </div>
    </main>
  );
}

function StrategyBankCard({ strategy, isCustom, onOpenBrief }) {
  const tier = strategy.complexity || 2;
  const tierColor =
    tier === 1
      ? { border: "border-l-emerald-500", badge: "bg-emerald-100 text-emerald-800" }
      : tier === 2
        ? { border: "border-l-sky-500", badge: "bg-sky-100 text-sky-800" }
        : { border: "border-l-amber-500", badge: "bg-amber-100 text-amber-900" };
  const tierLabel = tier === 1 ? "Foundational" : tier === 2 ? "Intermediate" : "Advanced";

  const goalColors = {
    eliminate: "bg-emerald-50 text-emerald-700 border-emerald-200",
    reduce_rate: "bg-sky-50 text-sky-700 border-sky-200",
    defer: "bg-amber-50 text-amber-700 border-amber-200",
  };

  const hasBrief = !!strategy.clientBrief || !!strategy.detailTemplate;

  return (
    <button
      onClick={() => onOpenBrief(strategy)}
      className={`relative text-left bg-white border border-stone-200 border-l-4 ${tierColor.border} rounded-sm p-3 hover:shadow-md hover:border-sky-400 transition-all flex flex-col gap-2 group ${
        hasBrief ? "ring-1 ring-amber-200" : ""
      }`}
    >
      {/* Gold corner ribbon for strategies with briefs */}
      {hasBrief && (
        <div className="absolute top-0 right-0 flex items-center gap-1 bg-gradient-to-l from-amber-400 to-amber-300 text-amber-900 px-2 py-0.5 rounded-bl-sm sans text-[9px] uppercase tracking-wider font-bold shadow-sm">
          <Star size={10} className="fill-amber-700 text-amber-700" />
          Brief
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="sans text-sm font-bold text-stone-900 leading-tight group-hover:text-sky-700 flex items-center gap-1.5">
            {hasBrief && (
              <Star
                size={14}
                className="fill-amber-400 text-amber-500 shrink-0"
              />
            )}
            <span className="truncate">{strategy.name}</span>
          </div>
          {strategy.citation && (
            <div className="sans text-[10px] text-stone-500 mono mt-0.5">
              {strategy.citation}
            </div>
          )}
        </div>
        <div
          className={`flex flex-col items-end gap-1 shrink-0 ${
            hasBrief ? "mt-5" : ""
          }`}
        >
          <span
            className={`sans text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${tierColor.badge}`}
          >
            T{tier} · {tierLabel}
          </span>
          {isCustom && (
            <span className="sans text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm bg-sky-500 text-white">
              Custom
            </span>
          )}
        </div>
      </div>

      <div className="sans text-xs text-stone-700 leading-snug line-clamp-3">
        {strategy.shortDescription}
      </div>

      <div className="flex items-center gap-1 flex-wrap mt-auto">
        {(strategy.goals || []).map((g) => {
          const goal = GOALS.find((x) => x.id === g);
          if (!goal) return null;
          return (
            <span
              key={g}
              className={`sans text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm border ${goalColors[g] || "bg-stone-50 text-stone-700 border-stone-200"}`}
            >
              {goal.label.split(" ")[0]}
            </span>
          );
        })}
        <span className="text-stone-300 mx-0.5">·</span>
        {(strategy.categories || []).map((c) => {
          const cat = CATEGORIES.find((x) => x.id === c);
          if (!cat) return null;
          return (
            <span
              key={c}
              className="sans text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm bg-stone-100 text-stone-700"
            >
              {cat.label.split(" ")[0]}
            </span>
          );
        })}
      </div>
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Analytics Dashboard — firm-wide rollup of plans, savings, top strategies,
// pipeline funnel, tier mix, and activity over time
// ════════════════════════════════════════════════════════════════════════
function AnalyticsDashboard({ onOpenPlan }) {
  const [plans, setPlans] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [planData, eventData] = await Promise.all([
        loadAllPlansForAnalytics(),
        loadStrategyEvents(90),
      ]);
      if (!mounted) return;
      setPlans(planData);
      setEvents(eventData);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const metrics = useMemo(() => {
    if (!plans) return null;
    return computeFirmMetrics(plans);
  }, [plans]);

  const engagement = useMemo(() => computeEngagementMetrics(events), [events]);

  if (loading) {
    return (
      <main className="max-w-[1400px] mx-auto px-6 py-12 text-center sans text-stone-300">
        Loading analytics…
      </main>
    );
  }
  if (!plans || plans.length === 0) {
    return (
      <main className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="bg-white border border-stone-300 rounded-sm p-12 text-center">
          <BarChart3 size={32} className="text-stone-300 mx-auto mb-3" />
          <div className="serif text-2xl font-bold text-stone-900 mb-2">
            No firm activity yet
          </div>
          <div className="sans text-sm text-stone-600 max-w-md mx-auto">
            Analytics populate as plans are created across the firm. Once you
            and your partners add a few clients, this page will summarize
            everything.
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-[1400px] mx-auto px-6 py-6">
      {/* Title */}
      <div className="mb-5 flex items-end justify-between">
        <div>
          <div className="serif text-3xl font-bold text-stone-100">
            Firm Analytics
          </div>
          <div className="sans text-sm text-stone-400 mt-1">
            Rollup across every plan visible to your account · updated live
          </div>
        </div>
        <div className="sans text-xs text-stone-500">
          {plans.length} plan{plans.length === 1 ? "" : "s"} across{" "}
          {metrics.uniqueStrategies} unique strategies applied
        </div>
      </div>

      {/* Hero tiles */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <HeroMetricCard
          label="Total Plans Created"
          value={metrics.totals.planCount}
          icon={<FileText size={16} className="text-sky-400" />}
        />
        <HeroMetricCard
          label="Total Tax Saved"
          value={`$${fmt(metrics.totals.totalSaved)}`}
          icon={<TrendingDown size={16} className="text-emerald-400" />}
          highlight
        />
        <HeroMetricCard
          label="Plans Delivered"
          value={metrics.totals.delivered}
          icon={<Check size={16} className="text-emerald-400" />}
        />
        <HeroMetricCard
          label="Plans In Progress"
          value={metrics.totals.inProgress}
          icon={<Clock size={16} className="text-amber-400" />}
        />
      </div>

      <div className="grid grid-cols-12 gap-3 mb-6">
        {/* Pipeline funnel */}
        <div className="col-span-5 bg-white border border-stone-300 rounded-sm">
          <div className="px-5 py-3 border-b border-stone-200">
            <div className="serif text-lg font-bold text-stone-900">
              Pipeline Funnel
            </div>
            <div className="sans text-xs text-stone-500">
              Plans by lifecycle stage
            </div>
          </div>
          <div className="px-5 py-4">
            {LIFECYCLE_STAGES.map((s) => {
              const count = metrics.stageCount[s.id] || 0;
              const pct =
                metrics.totals.planCount > 0
                  ? (count / metrics.totals.planCount) * 100
                  : 0;
              const color = pipelineStageColor(s.id);
              return (
                <div key={s.id} className="mb-2 last:mb-0">
                  <div className="flex items-baseline justify-between mb-0.5 sans text-xs">
                    <span className="font-semibold text-stone-700">
                      {s.label}
                    </span>
                    <span className="mono num-tabular text-stone-500">
                      {count}{" "}
                      <span className="text-stone-400">
                        ({pct.toFixed(0)}%)
                      </span>
                    </span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-sm overflow-hidden">
                    <div
                      className={`h-full ${color}`}
                      style={{ width: `${Math.max(2, pct)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Plan tier mix */}
        <div className="col-span-3 bg-white border border-stone-300 rounded-sm">
          <div className="px-5 py-3 border-b border-stone-200">
            <div className="serif text-lg font-bold text-stone-900">
              Plan Tier Mix
            </div>
            <div className="sans text-xs text-stone-500">
              Highest complexity per plan
            </div>
          </div>
          <div className="px-5 py-4 space-y-3">
            {[
              { tier: 1, label: "Foundational", color: "bg-emerald-500", text: "text-emerald-700" },
              { tier: 2, label: "Intermediate", color: "bg-sky-500", text: "text-sky-700" },
              { tier: 3, label: "Advanced", color: "bg-amber-500", text: "text-amber-700" },
            ].map(({ tier, label, color, text }) => {
              const count = metrics.tierCount[tier] || 0;
              const total = metrics.totals.planCount;
              const pct = total > 0 ? (count / total) * 100 : 0;
              return (
                <div key={tier}>
                  <div className="flex items-baseline justify-between mb-1 sans text-xs">
                    <span className="font-semibold text-stone-700">
                      Tier {tier} · {label}
                    </span>
                    <span className={`mono num-tabular font-bold ${text}`}>
                      {count}
                    </span>
                  </div>
                  <div className="h-3 bg-stone-100 rounded-sm overflow-hidden">
                    <div
                      className={`h-full ${color}`}
                      style={{ width: `${Math.max(2, pct)}%` }}
                    />
                  </div>
                </div>
              );
            })}
            {metrics.tierCount.none > 0 && (
              <div className="sans text-[11px] text-stone-500 italic pt-2 border-t border-stone-200">
                {metrics.tierCount.none} plan
                {metrics.tierCount.none === 1 ? "" : "s"} with no strategies
                applied yet
              </div>
            )}
          </div>
        </div>

        {/* Quick savings stats */}
        <div className="col-span-4 bg-gradient-to-br from-stone-900 via-stone-900 to-sky-900 text-white rounded-sm p-5">
          <div className="sans text-[11px] uppercase tracking-widest text-sky-300 font-semibold mb-2">
            Savings Summary
          </div>
          <div className="serif text-4xl font-bold text-sky-400 mono num-tabular leading-none mb-2">
            ${fmt(metrics.totals.totalSaved)}
          </div>
          <div className="sans text-xs text-stone-400 mb-4">
            Aggregate estimated tax savings across all firm plans
          </div>
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-stone-700">
            <div>
              <div className="sans text-[10px] uppercase tracking-widest text-stone-400 font-semibold mb-1">
                Avg Per Plan
              </div>
              <div className="serif text-xl font-bold text-sky-400 mono num-tabular">
                ${fmt(metrics.totals.avgSaved)}
              </div>
            </div>
            <div>
              <div className="sans text-[10px] uppercase tracking-widest text-stone-400 font-semibold mb-1">
                Delivered Plans Saved
              </div>
              <div className="serif text-xl font-bold text-emerald-400 mono num-tabular">
                ${fmt(metrics.totals.deliveredSaved)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Strategies */}
      <div className="bg-white border border-stone-300 rounded-sm mb-6">
        <div className="px-5 py-3 border-b border-stone-200 flex items-baseline justify-between">
          <div>
            <div className="serif text-lg font-bold text-stone-900">
              Top Strategies — Most Picked
            </div>
            <div className="sans text-xs text-stone-500">
              Count of plans each strategy has been added to · all-time
            </div>
          </div>
          <div className="sans text-xs text-stone-500">
            {metrics.uniqueStrategies} unique strategies applied across firm
          </div>
        </div>
        {metrics.topStrategies.length === 0 ? (
          <div className="py-10 text-center sans text-sm text-stone-500">
            No strategies applied to any plan yet.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-left px-5 py-2 w-10">
                  Rank
                </th>
                <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-left px-3 py-2">
                  Strategy
                </th>
                <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-left px-3 py-2">
                  Tier
                </th>
                <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-right px-3 py-2">
                  Times Picked
                </th>
                <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-right px-3 py-2">
                  % of Plans
                </th>
                <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-right px-5 py-2">
                  Attributed Savings*
                </th>
              </tr>
            </thead>
            <tbody>
              {metrics.topStrategies.map((s, i) => {
                const tierTextColor =
                  s.complexity === 3
                    ? "text-amber-800"
                    : s.complexity === 2
                      ? "text-sky-800"
                      : "text-emerald-800";
                const tierBgColor =
                  s.complexity === 3
                    ? "bg-amber-100"
                    : s.complexity === 2
                      ? "bg-sky-100"
                      : "bg-emerald-100";
                const planPct =
                  (s.planCount / metrics.totals.planCount) * 100;
                return (
                  <tr
                    key={s.id}
                    className={`border-b border-stone-100 ${i % 2 === 0 ? "bg-white" : "bg-stone-50/50"}`}
                  >
                    <td className="px-5 py-2.5 sans text-sm font-bold text-stone-500 num-tabular">
                      #{i + 1}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="sans text-sm font-semibold text-stone-900">
                        {s.name}
                      </div>
                      {s.citation && (
                        <div className="sans text-[10px] text-stone-500 mono mt-0.5">
                          {s.citation}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`sans text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${tierBgColor} ${tierTextColor}`}
                      >
                        T{s.complexity || 2}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right sans text-sm font-semibold text-stone-900 mono num-tabular">
                      {s.planCount}
                    </td>
                    <td className="px-3 py-2.5 text-right sans text-sm text-stone-700 mono num-tabular">
                      {planPct.toFixed(0)}%
                    </td>
                    <td className="px-5 py-2.5 text-right sans text-sm font-bold text-emerald-700 mono num-tabular">
                      ${fmt(s.attributedSavings)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="px-5 py-2 border-t border-stone-200 sans text-[10px] text-stone-500 italic">
          * Attributed savings allocates each plan's total estimated savings
          evenly across its applied strategies — a rough proxy, not a
          per-strategy estimate.
        </div>
      </div>

      {/* Activity over time */}
      <div className="bg-white border border-stone-300 rounded-sm mb-6">
        <div className="px-5 py-3 border-b border-stone-200 flex items-baseline justify-between">
          <div>
            <div className="serif text-lg font-bold text-stone-900">
              Plan Creation Activity
            </div>
            <div className="sans text-xs text-stone-500">
              Plans created per month, last 12 months
            </div>
          </div>
        </div>
        <div className="px-5 py-4">
          <ActivityBarChart monthly={metrics.monthlyActivity} />
        </div>
      </div>

      {/* Strategy Engagement — click tracking from briefs and add-actions */}
      <div className="bg-white border border-stone-300 rounded-sm mb-6">
        <div className="px-5 py-3 border-b border-stone-200 flex items-baseline justify-between">
          <div>
            <div className="serif text-lg font-bold text-stone-900">
              Strategy Engagement — Last 90 Days
            </div>
            <div className="sans text-xs text-stone-500">
              Brief opens and strategy adds across all firm users
            </div>
          </div>
          <div className="sans text-xs text-stone-500">
            {engagement.totalEvents} events · {engagement.activeUserCount}{" "}
            active user{engagement.activeUserCount === 1 ? "" : "s"}
          </div>
        </div>
        {engagement.totalEvents === 0 ? (
          <div className="px-5 py-8 text-center sans text-sm text-stone-500">
            No engagement events yet. Open strategy briefs and add strategies
            to plans — the activity will appear here.
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4 px-5 py-4">
            {/* Most-clicked briefs */}
            <div className="col-span-7">
              <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-2">
                Most-Clicked Briefs
              </div>
              {engagement.topByClicks.length === 0 ? (
                <div className="sans text-xs text-stone-500 italic py-3">
                  No briefs opened yet.
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-stone-200">
                      <th className="sans text-[10px] uppercase tracking-wider font-semibold text-stone-500 text-left py-1.5">
                        Strategy
                      </th>
                      <th className="sans text-[10px] uppercase tracking-wider font-semibold text-stone-500 text-right py-1.5">
                        Brief Opens
                      </th>
                      <th className="sans text-[10px] uppercase tracking-wider font-semibold text-stone-500 text-right py-1.5">
                        Picks (90d)
                      </th>
                      <th className="sans text-[10px] uppercase tracking-wider font-semibold text-stone-500 text-right py-1.5 pr-2">
                        Click→Pick
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {engagement.topByClicks.map((row, i) => {
                      const ratio =
                        row.opens > 0
                          ? Math.round((row.adds / row.opens) * 100)
                          : null;
                      const ratioColor =
                        ratio == null
                          ? "text-stone-400"
                          : ratio >= 50
                            ? "text-emerald-700"
                            : ratio >= 20
                              ? "text-amber-700"
                              : "text-stone-500";
                      return (
                        <tr
                          key={row.strategyId}
                          className={`border-b border-stone-100 ${i % 2 === 0 ? "bg-white" : "bg-stone-50/40"}`}
                        >
                          <td className="py-1.5 sans text-sm">
                            <div className="font-semibold text-stone-900">
                              {row.name}
                            </div>
                          </td>
                          <td className="py-1.5 text-right sans text-sm font-bold text-sky-700 mono num-tabular">
                            {row.opens}
                          </td>
                          <td className="py-1.5 text-right sans text-sm text-stone-700 mono num-tabular">
                            {row.adds}
                          </td>
                          <td
                            className={`py-1.5 text-right sans text-sm font-semibold mono num-tabular pr-2 ${ratioColor}`}
                          >
                            {ratio == null ? "—" : `${ratio}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              <div className="sans text-[10px] text-stone-500 italic mt-2 leading-relaxed">
                Click→Pick is the fraction of brief opens that led to a
                strategy being picked in the same window. Low ratios suggest
                strategies that get researched but rarely fit.
              </div>
            </div>

            {/* Most active users */}
            <div className="col-span-5">
              <div className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-2">
                Most Active Users
              </div>
              {engagement.topUsers.length === 0 ? (
                <div className="sans text-xs text-stone-500 italic py-3">
                  No user activity yet.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {engagement.topUsers.map((u, i) => {
                    const maxCount = engagement.topUsers[0]?.count || 1;
                    const pct = (u.count / maxCount) * 100;
                    return (
                      <div key={u.email || i}>
                        <div className="flex items-baseline justify-between mb-0.5 sans text-xs">
                          <span className="font-semibold text-stone-700 truncate">
                            {u.email || "(unknown)"}
                          </span>
                          <span className="mono num-tabular text-stone-500 shrink-0 ml-2">
                            {u.count} event{u.count === 1 ? "" : "s"}
                          </span>
                        </div>
                        <div className="h-2 bg-stone-100 rounded-sm overflow-hidden">
                          <div
                            className="h-full bg-sky-500"
                            style={{ width: `${Math.max(2, pct)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Most-recent / largest savings plans */}
      <div className="bg-white border border-stone-300 rounded-sm">
        <div className="px-5 py-3 border-b border-stone-200">
          <div className="serif text-lg font-bold text-stone-900">
            Largest Tax Savings — Top 10 Plans
          </div>
          <div className="sans text-xs text-stone-500">
            Click any row to open the plan
          </div>
        </div>
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-left px-5 py-2">
                Client
              </th>
              <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-left px-3 py-2">
                Tax Year
              </th>
              <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-left px-3 py-2">
                Stage
              </th>
              <th className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 text-right px-5 py-2">
                Estimated Savings
              </th>
            </tr>
          </thead>
          <tbody>
            {metrics.topPlansBySavings.map((p, i) => (
              <tr
                key={p.id}
                onClick={() => onOpenPlan(p.id)}
                className="border-b border-stone-100 hover:bg-sky-50/40 cursor-pointer"
              >
                <td className="px-5 py-2.5 sans text-sm font-semibold text-stone-900">
                  {p.client_name || "Untitled"}
                </td>
                <td className="px-3 py-2.5 sans text-sm text-stone-700 mono num-tabular">
                  {p.tax_year || "—"}
                </td>
                <td className="px-3 py-2.5">
                  <StageBadge stage={p.stage} />
                </td>
                <td className="px-5 py-2.5 text-right sans text-sm font-bold text-emerald-700 mono num-tabular">
                  ${fmt(p.savings || 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}

function HeroMetricCard({ label, value, icon, highlight }) {
  return (
    <div
      className={`p-4 rounded-sm border ${
        highlight
          ? "bg-gradient-to-br from-stone-900 to-sky-900 border-stone-700 text-white"
          : "bg-white border-stone-300"
      }`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <div
          className={`sans text-[10px] uppercase tracking-widest font-semibold ${
            highlight ? "text-sky-300" : "text-stone-500"
          }`}
        >
          {label}
        </div>
      </div>
      <div
        className={`serif text-3xl font-bold mt-1 num-tabular mono ${
          highlight ? "text-sky-400" : "text-stone-900"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function pipelineStageColor(stageId) {
  return (
    {
      prospect: "bg-stone-400",
      conversation: "bg-stone-500",
      data_received: "bg-amber-400",
      plan_built: "bg-amber-500",
      presented: "bg-sky-500",
      accepted: "bg-sky-600",
      instituted: "bg-emerald-500",
      delivered: "bg-emerald-700",
    }[stageId] || "bg-stone-400"
  );
}

function ActivityBarChart({ monthly }) {
  // monthly: array of { label, count }, last 12 months in order
  const max = Math.max(...monthly.map((m) => m.count), 1);
  const chartHeight = 140;
  return (
    <div>
      <div
        className="flex items-end gap-1.5"
        style={{ height: `${chartHeight + 24}px` }}
      >
        {monthly.map((m) => (
          <div
            key={m.label}
            className="flex-1 flex flex-col items-center justify-end"
          >
            <div className="sans text-[9px] mono num-tabular text-stone-500 mb-0.5">
              {m.count > 0 ? m.count : ""}
            </div>
            <div
              className="w-full bg-gradient-to-t from-sky-300 to-sky-500 border-t-2 border-sky-600 rounded-t-sm"
              style={{
                height: `${Math.max(2, (m.count / max) * chartHeight)}px`,
              }}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-1.5 mt-1">
        {monthly.map((m) => (
          <div
            key={m.label}
            className="flex-1 sans text-[10px] text-stone-500 text-center"
          >
            {m.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// Aggregate plan rows into firm-wide metrics for the dashboard.
function computeFirmMetrics(plans) {
  const totals = {
    planCount: plans.length,
    totalSaved: 0,
    avgSaved: 0,
    delivered: 0,
    inProgress: 0,
    prospects: 0,
    deliveredSaved: 0,
  };

  const stageCount = {};
  const strategyAgg = {}; // id -> { id, name, citation, complexity, planCount, attributedSavings }
  const tierCount = { 1: 0, 2: 0, 3: 0, none: 0 };
  const monthlyByKey = {};

  for (const p of plans) {
    const saved = parseFloat(p.savings) || 0;
    totals.totalSaved += saved;

    const stage = p.stage || "prospect";
    stageCount[stage] = (stageCount[stage] || 0) + 1;
    if (stage === "delivered") {
      totals.delivered++;
      totals.deliveredSaved += saved;
    } else if (
      stage === "instituted" ||
      stage === "accepted" ||
      stage === "presented" ||
      stage === "plan_built"
    ) {
      totals.inProgress++;
    } else if (stage === "prospect" || stage === "conversation") {
      totals.prospects++;
    }

    // Activity bucket by created_at YYYY-MM
    const key = (p.created_at || "").slice(0, 7); // "2026-05"
    if (key) monthlyByKey[key] = (monthlyByKey[key] || 0) + 1;

    // Walk applied strategies in state_data
    const sd = p.state_data || {};
    const applied = [];
    for (const e of sd.entities || []) {
      for (const s of e.strategies || []) {
        if (s?.applied) applied.push(s);
      }
    }
    for (const s of sd.individual?.strategies || []) {
      if (s?.applied) applied.push(s);
    }

    let maxTier = 0;
    const numStrategies = applied.length;
    for (const ref of applied) {
      const strat = getStrategy(ref.strategyId);
      if (!strat) continue;
      const id = strat.id;
      if (!strategyAgg[id]) {
        strategyAgg[id] = {
          id,
          name: strat.name,
          citation: strat.citation,
          complexity: strat.complexity || 2,
          planCount: 0,
          attributedSavings: 0,
        };
      }
      strategyAgg[id].planCount++;
      strategyAgg[id].attributedSavings +=
        numStrategies > 0 ? saved / numStrategies : 0;
      if ((strat.complexity || 0) > maxTier) maxTier = strat.complexity || 0;
    }

    if (maxTier === 0) tierCount.none++;
    else tierCount[maxTier] = (tierCount[maxTier] || 0) + 1;
  }

  totals.avgSaved =
    totals.planCount > 0 ? totals.totalSaved / totals.planCount : 0;

  const topStrategies = Object.values(strategyAgg)
    .sort((a, b) => b.planCount - a.planCount || b.attributedSavings - a.attributedSavings)
    .slice(0, 10);

  const topPlansBySavings = [...plans]
    .sort((a, b) => (parseFloat(b.savings) || 0) - (parseFloat(a.savings) || 0))
    .slice(0, 10);

  // Last 12 months activity — uses created_at month buckets
  const monthly = [];
  const today = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { month: "short" });
    monthly.push({ label, count: monthlyByKey[key] || 0 });
  }

  return {
    totals,
    stageCount,
    topStrategies,
    tierCount,
    monthlyActivity: monthly,
    topPlansBySavings,
    uniqueStrategies: Object.keys(strategyAgg).length,
  };
}

// Aggregate strategy_events into engagement metrics for the dashboard.
function computeEngagementMetrics(events) {
  const perStrategy = {}; // strategyId -> { strategyId, name, opens, adds, removes }
  const perUser = {}; // email -> count
  for (const e of events || []) {
    const id = e.strategy_id;
    if (!id) continue;
    if (!perStrategy[id]) {
      const strat = getStrategy(id);
      perStrategy[id] = {
        strategyId: id,
        name: strat?.name || id,
        opens: 0,
        adds: 0,
        removes: 0,
      };
    }
    if (e.event_type === "brief_opened") perStrategy[id].opens++;
    else if (e.event_type === "strategy_added") perStrategy[id].adds++;
    else if (e.event_type === "strategy_removed") perStrategy[id].removes++;

    if (e.user_email) {
      perUser[e.user_email] = (perUser[e.user_email] || 0) + 1;
    }
  }
  const topByClicks = Object.values(perStrategy)
    .filter((r) => r.opens > 0 || r.adds > 0)
    .sort((a, b) => b.opens - a.opens || b.adds - a.adds)
    .slice(0, 10);
  const topUsers = Object.entries(perUser)
    .map(([email, count]) => ({ email, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);
  return {
    totalEvents: (events || []).length,
    activeUserCount: Object.keys(perUser).length,
    topByClicks,
    topUsers,
  };
}

// ════════════════════════════════════════════════════════════════════════
// Custom Strategy Editor — create/edit a firm-wide custom strategy
// ════════════════════════════════════════════════════════════════════════
function CustomStrategyEditor({ initial, onSave, onCancel }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [citation, setCitation] = useState(initial?.citation || "");
  const [shortDescription, setShortDescription] = useState(
    initial?.shortDescription || "",
  );
  const [longDescription, setLongDescription] = useState(initial?.longDescription || "");
  const [categories, setCategoriesState] = useState(
    () => new Set(initial?.categories || ["business"]),
  );
  const [goals, setGoals] = useState(
    () => new Set(initial?.goals || ["reduce_rate"]),
  );
  const [entityTypes, setEntityTypes] = useState(
    () => new Set(initial?.entityTypes || ["1040", "1120S", "1065"]),
  );
  const [calcType, setCalcType] = useState(initial?.calcType || "reduceTaxableIncome");
  const [timingKind, setTimingKind] = useState(initial?.timing?.kind || "year_end");
  const [timingNote, setTimingNote] = useState(initial?.timing?.note || "");

  const toggle = (set, setSet, value) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setSet(next);
  };

  const canSubmit =
    name.trim().length > 0 && categories.size > 0 && goals.size > 0 && entityTypes.size > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const id =
      initial?.id ||
      `custom_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
    const strategy = {
      id,
      name: name.trim(),
      citation: citation.trim() || null,
      shortDescription: shortDescription.trim(),
      longDescription: longDescription.trim(),
      categories: Array.from(categories),
      goals: Array.from(goals),
      entityTypes: Array.from(entityTypes),
      calcType,
      inputs: [],
      defaultEstimate: 0,
      timing: { kind: timingKind, note: timingNote.trim() },
      isCustom: true,
    };
    onSave(strategy);
  };

  const Toggle = ({ active, onClick, children, className = "" }) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 sans text-xs font-semibold rounded-sm border transition-colors ${
        active
          ? "bg-stone-900 text-sky-400 border-stone-900"
          : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
      } ${className}`}
    >
      {children}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-900/80 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-sm shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-stone-300 flex items-center justify-between bg-gradient-to-r from-stone-900 to-sky-900">
          <div>
            <div className="serif text-xl font-bold text-white">
              {isEdit ? "Edit Custom Strategy" : "New Custom Strategy"}
            </div>
            <div className="sans text-xs text-sky-200 mt-0.5">
              Available to all firm users in the strategy picker
            </div>
          </div>
          <button type="button" onClick={onCancel} className="text-white p-1">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-auto flex-1 px-6 py-5 space-y-4">
          <Field label="Strategy Name" hint="Required — short, partner-readable">
            <TextInput
              value={name}
              onChange={setName}
              placeholder="e.g. Captive Insurance Premium Stacking"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Citation (optional)" hint="IRC §, Rev. Proc., case law, etc.">
              <TextInput
                value={citation}
                onChange={setCitation}
                placeholder="IRC §831(b)"
              />
            </Field>
            <Field label="Calculation Type">
              <Select
                value={calcType}
                onChange={setCalcType}
                options={[
                  { id: "manual", label: "Manual — planner enters savings" },
                  { id: "reduceTaxableIncome", label: "Reduces taxable income" },
                  { id: "taxCredit", label: "Direct tax credit" },
                ]}
              />
            </Field>
          </div>

          <Field label="Short Description" hint="One sentence — appears in the picker">
            <TextInput
              value={shortDescription}
              onChange={setShortDescription}
              placeholder="What it does, in plain English"
            />
          </Field>

          <Field label="Long Description (optional)">
            <textarea
              value={longDescription}
              onChange={(e) => setLongDescription(e.target.value)}
              placeholder="Mechanics, eligibility, gotchas, documentation requirements"
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500 resize-y"
            />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Categories">
              <div className="flex flex-wrap gap-1">
                {CATEGORIES.map((c) => (
                  <Toggle
                    key={c.id}
                    active={categories.has(c.id)}
                    onClick={() => toggle(categories, setCategoriesState, c.id)}
                  >
                    {c.label}
                  </Toggle>
                ))}
              </div>
            </Field>
            <Field label="Goals">
              <div className="flex flex-wrap gap-1">
                {GOALS.map((g) => (
                  <Toggle
                    key={g.id}
                    active={goals.has(g.id)}
                    onClick={() => toggle(goals, setGoals, g.id)}
                  >
                    {g.label.split(" ")[0]}
                  </Toggle>
                ))}
              </div>
            </Field>
            <Field label="Entity Types">
              <div className="flex flex-wrap gap-1">
                {STRATEGY_ENTITY_TYPES.map((e) => (
                  <Toggle
                    key={e.id}
                    active={entityTypes.has(e.id)}
                    onClick={() => toggle(entityTypes, setEntityTypes, e.id)}
                  >
                    {e.id}
                  </Toggle>
                ))}
              </div>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Timing">
              <Select
                value={timingKind}
                onChange={setTimingKind}
                options={[
                  { id: "year_end", label: "By year-end (Dec 31)" },
                  { id: "specific_date", label: "Specific date" },
                  { id: "original_due", label: "By original due date" },
                  { id: "extended_due", label: "By extended due date" },
                  { id: "prior_to_filing", label: "Before return filed" },
                  { id: "pre_transaction", label: "Before triggering tx" },
                  { id: "ongoing", label: "Ongoing discipline" },
                ]}
              />
            </Field>
            <Field label="Timing Note (optional)" hint="Specifics — e.g. 'Plan adopted by 11/15'">
              <TextInput
                value={timingNote}
                onChange={setTimingNote}
                placeholder="When and how"
              />
            </Field>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 sans text-sm text-stone-600 hover:text-stone-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-sm sans text-sm font-semibold ${
              canSubmit
                ? "bg-stone-900 text-sky-400 hover:bg-stone-800"
                : "bg-stone-200 text-stone-400 cursor-not-allowed"
            }`}
          >
            <Check size={14} />
            {isEdit ? "Save Changes" : "Create Strategy"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Main shell — manages plan registry + active plan
// ════════════════════════════════════════════════════════════════════════
const REGISTRY_KEY = storage.KEYS.PLAN_REGISTRY;
const STATE_KEY = storage.KEYS.PLAN_STATE;

export default function TaxPlanningTool({ session, profile } = {}) {
  const [plans, setPlans] = useState([]);
  const [activePlanId, setActivePlanIdState] = useState(null);
  const [activePlan, setActivePlanState] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showNewPlanModal, setShowNewPlanModal] = useState(false);
  const [feedbackOpenCount, setFeedbackOpenCount] = useState(0);
  const [feedbackTotalCount, setFeedbackTotalCount] = useState(0);
  const [customStrategies, setCustomStrategiesState] = useState([]);
  const [mainView, setMainView] = useState("plans"); // "plans" | "bank" | "analytics"
  const [bankBriefOpen, setBankBriefOpen] = useState(null); // strategy when viewing brief from bank

  const userEmail = session?.user?.email || "";
  const submitterName =
    profile?.display_name || session?.user?.email?.split("@")[0] || "Anonymous";

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      /* ignore */
    }
  };

  // Refresh feedback counters from storage. Called on mount and whenever
  // the FeedbackPanel modal closes (so newly-added items update the badge).
  const refreshFeedbackCount = useCallback(async () => {
    try {
      const raw = await storage.get(storage.KEYS.FEEDBACK, true);
      if (!raw?.value) return;
      const items = JSON.parse(raw.value);
      if (!Array.isArray(items)) return;
      setFeedbackTotalCount(items.length);
      setFeedbackOpenCount(
        items.filter(
          (i) => i?.status === "open" || i?.status === "in_progress",
        ).length,
      );
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (loaded) refreshFeedbackCount();
  }, [loaded, refreshFeedbackCount]);

  const handleCloseFeedback = () => {
    setShowFeedback(false);
    refreshFeedbackCount();
  };

  // Load custom strategies on mount; mirror into module cache so
  // synchronous getStrategy(id) lookups everywhere also see them.
  useEffect(() => {
    if (!loaded) return;
    loadCustomStrategies().then((arr) => {
      setCustomStrategies(arr);
      setCustomStrategiesState(arr);
    });
  }, [loaded]);

  const handleSaveCustomStrategy = async (strat) => {
    await saveCustomStrategy(strat);
    const next = customStrategies.some((s) => s.id === strat.id)
      ? customStrategies.map((s) => (s.id === strat.id ? strat : s))
      : [...customStrategies, strat];
    setCustomStrategies(next);
    setCustomStrategiesState(next);
  };

  const handleDeleteCustomStrategy = async (id) => {
    await deleteCustomStrategy(id);
    const next = customStrategies.filter((s) => s.id !== id);
    setCustomStrategies(next);
    setCustomStrategiesState(next);
  };

  // Load registry on mount
  useEffect(() => {
    (async () => {
      const raw = await storage.get(REGISTRY_KEY, true);
      if (raw?.value) {
        try {
          const parsed = JSON.parse(raw.value);
          if (Array.isArray(parsed)) setPlans(parsed);
        } catch {
          /* ignore */
        }
      }
      setLoaded(true);
    })();
  }, []);

  // Persist registry whenever it changes
  useEffect(() => {
    if (!loaded) return;
    storage.set(REGISTRY_KEY, JSON.stringify(plans), true);
  }, [plans, loaded]);

  // When active plan id changes, load it
  useEffect(() => {
    if (!activePlanId) {
      setActivePlanState(null);
      setStorageActivePlan(null);
      return;
    }
    setStorageActivePlan(activePlanId);
    (async () => {
      const raw = await storage.get(STATE_KEY);
      if (raw?.value) {
        try {
          setActivePlanState(migrateScenarios(JSON.parse(raw.value)));
        } catch {
          setActivePlanState(null);
        }
      } else {
        setActivePlanState(null);
      }
    })();
  }, [activePlanId]);

  // Persist active plan changes (debounced via React batching)
  const updateActivePlan = useCallback(
    (next) => {
      setActivePlanState(next);
      storage.set(STATE_KEY, JSON.stringify(next));

      // Update registry denormalized fields
      try {
        const result = computePlanWithSavings(next);
        setPlans((prev) =>
          prev.map((p) =>
            p.id === next.id
              ? {
                  ...p,
                  clientName: next.clientName,
                  taxYear: next.taxYear,
                  state: next.state,
                  entityCount: (next.entities || []).length,
                  stage: next.lifecycle?.stage || "prospect",
                  savings: result.savings,
                  updatedAt: new Date().toISOString(),
                }
              : p,
          ),
        );
      } catch (e) {
        console.error("computePlanWithSavings error", e);
      }
    },
    [setActivePlanState, setPlans],
  );

  const handleCreatePlan = ({ clientName, clientEmail, taxYear, state }) => {
    const id = uid("plan");
    const p = newPlan({ id, clientName: clientName || "New Client" });
    if (clientEmail) p.clientEmail = clientEmail;
    if (taxYear) p.taxYear = parseInt(taxYear, 10);
    if (state) p.state = state;
    setPlans((prev) => [
      ...prev,
      {
        id,
        clientName: p.clientName,
        taxYear: p.taxYear,
        state: p.state,
        entityCount: 0,
        stage: "prospect",
        savings: 0,
        createdAt: p.createdAt,
      },
    ]);
    setStorageActivePlan(id);
    storage.set(STATE_KEY, JSON.stringify(p));
    setActivePlanState(p);
    setActivePlanIdState(id);
    setShowNewPlanModal(false);
  };

  const handleOpenPlan = (id) => setActivePlanIdState(id);
  const handleBack = () => setActivePlanIdState(null);
  const handleDeletePlan = (id) => {
    setPlans((prev) => prev.filter((p) => p.id !== id));
    storage.deletePlanStateById(id);
    if (activePlanId === id) setActivePlanIdState(null);
  };

  if (!loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center sans text-stone-500">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <AppHeader
        activePlan={activePlan}
        mainView={mainView}
        onSwitchView={setMainView}
        onBack={handleBack}
        onUpdatePlan={updateActivePlan}
        onOpenFeedback={() => setShowFeedback(true)}
        onSignOut={handleSignOut}
        userEmail={userEmail}
        feedbackOpenCount={feedbackOpenCount}
        feedbackTotalCount={feedbackTotalCount}
      />
      {mainView === "bank" ? (
        <StrategyBank
          customStrategies={customStrategies}
          onOpenBrief={(s) => setBankBriefOpen(s)}
        />
      ) : mainView === "analytics" ? (
        <AnalyticsDashboard
          onOpenPlan={(id) => {
            setMainView("plans");
            handleOpenPlan(id);
          }}
        />
      ) : !activePlan ? (
        <PlansHome
          plans={plans}
          onOpenPlan={handleOpenPlan}
          onNewPlan={() => setShowNewPlanModal(true)}
          onDeletePlan={handleDeletePlan}
        />
      ) : (
        <PlanWorkspace
          plan={activePlan}
          onUpdatePlan={updateActivePlan}
          customStrategies={customStrategies}
          onSaveCustomStrategy={handleSaveCustomStrategy}
          onDeleteCustomStrategy={handleDeleteCustomStrategy}
        />
      )}
      {showFeedback && (
        <FeedbackPanel
          submitterName={submitterName}
          onClose={handleCloseFeedback}
        />
      )}
      {showNewPlanModal && (
        <NewPlanModal
          onCreate={handleCreatePlan}
          onClose={() => setShowNewPlanModal(false)}
        />
      )}
      {bankBriefOpen && (
        <StrategyDetailModal
          strategy={bankBriefOpen}
          plan={activePlan || { taxYear: new Date().getFullYear(), individual: {} }}
          source="strategy_bank"
          onClose={() => setBankBriefOpen(null)}
        />
      )}

      {/* Floating feedback button — always visible across all views */}
      <FeedbackFAB
        onClick={() => setShowFeedback(true)}
        openCount={feedbackOpenCount}
        totalCount={feedbackTotalCount}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// FeedbackFAB — floating action button anchored bottom-right of the viewport.
// Always visible across every view and scroll position. Replaces the
// in-header Feedback button which got hidden behind plan controls when a
// plan was open.
// ════════════════════════════════════════════════════════════════════════
function FeedbackFAB({ onClick, openCount = 0, totalCount = 0 }) {
  const title =
    totalCount > 0
      ? `${openCount} open · ${totalCount} total feedback items — click to open`
      : "Send firm feedback — bugs, ideas, planning improvements";
  return (
    <button
      onClick={onClick}
      title={title}
      className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-sky-400 hover:bg-sky-300 text-stone-900 shadow-xl flex items-center justify-center transition-colors group print:hidden"
      aria-label="Open feedback panel"
    >
      <MessageSquare size={22} strokeWidth={2.5} />
      {openCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white sans text-[10px] font-bold flex items-center justify-center num-tabular border-2 border-[#F1F7FC]">
          {openCount > 99 ? "99+" : openCount}
        </span>
      )}
      {openCount === 0 && totalCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-stone-700 text-stone-200 sans text-[10px] font-semibold flex items-center justify-center num-tabular border-2 border-[#F1F7FC]">
          {totalCount > 99 ? "99+" : totalCount}
        </span>
      )}
      {/* Tooltip label that fades in on hover */}
      <span className="absolute right-full mr-3 px-2.5 py-1 rounded-sm bg-stone-900 text-stone-100 sans text-xs font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity shadow-lg">
        Feedback
      </span>
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════════
// New Plan Modal — captures client name (and basic context) up front
// ════════════════════════════════════════════════════════════════════════
function NewPlanModal({ onCreate, onClose }) {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [taxYear, setTaxYear] = useState(TAX_YEAR_OPTIONS[TAX_YEAR_OPTIONS.length - 1]);
  const [state, setState] = useState("UT");

  const canSubmit = clientName.trim().length > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onCreate({
      clientName: clientName.trim(),
      clientEmail: clientEmail.trim(),
      taxYear,
      state,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-sm shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-stone-300 flex items-center justify-between bg-gradient-to-r from-stone-900 to-sky-900">
          <div className="serif text-xl font-bold text-white">New Tax Plan</div>
          <button
            type="button"
            onClick={onClose}
            className="text-white p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <Field label="Client Name" hint="Family or individual filer — e.g. Smith Family">
            <TextInput
              value={clientName}
              onChange={setClientName}
              placeholder="Enter client name"
            />
          </Field>
          <Field label="Client Email" hint="Used to email strategy briefs directly (optional)">
            <TextInput
              value={clientEmail}
              onChange={setClientEmail}
              placeholder="client@example.com"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tax Year">
              <Select
                value={taxYear}
                onChange={(v) => setTaxYear(parseInt(v, 10))}
                options={TAX_YEAR_OPTIONS.map((y) => ({ id: y, label: `TY ${y}` }))}
              />
            </Field>
            <Field label="State">
              <Select
                value={state}
                onChange={setState}
                options={STATE_OPTIONS.map((s) => ({
                  id: s.id,
                  label: `${s.id} — ${s.name}`,
                }))}
              />
            </Field>
          </div>
          <div className="sans text-[11px] text-stone-500 leading-relaxed pt-1">
            You can change all of these later from the plan header. Filing status, entities, and
            income figures get added once you're inside the plan.
          </div>
        </div>
        <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 sans text-sm text-stone-600 hover:text-stone-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-sm sans text-sm font-semibold ${
              canSubmit
                ? "bg-stone-900 text-sky-400 hover:bg-stone-800"
                : "bg-stone-200 text-stone-400 cursor-not-allowed"
            }`}
          >
            <Plus size={14} />
            Create Plan
          </button>
        </div>
      </form>
    </div>
  );
}
